import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import api from "../../api/axios";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import useInstagramIAB from "../../hooks/useInstagramIAB";
import { useLocale } from "../../context/LocaleContext";
import {
  trackSignupStart,
  trackSignupSuccess,
  trackSignupView,
} from "../../utils/gaEventTracking";
import clsx from "clsx";
import GlobalErrorModal from "../../components/special/GlobalMessageModal.jsx";
import {
  LoadingAvatarSkeleton,
} from "../../components/common/LoadingSkeleton";
import { useTranslation, Trans } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
  AUTH_DESIGN_HEIGHT,
  AUTH_DESIGN_WIDTH,
  AUTH_GRADIENT_BACKGROUND,
  AUTH_GRADIENT_STYLE,
} from "../../constants/authVisuals";
import {
  PROFILE_LOCATION_COUNTRIES,
  PROFILE_LOCATION_COUNTRY_CODES,
} from "../../constants/profileLocations";
import useRegistrationCities from "./useRegistrationCities";
import CitySearchFeedback from "./CitySearchFeedback";
import RegistrationAgeWheel from "./RegistrationAgeWheel";
import { isValidRegistrationAge } from "./registrationAge";
import {
  buildRegistrationPreviewCityOptions,
  canSubmitRegistrationIdentityStep,
  getRegistrationRegionFromHostname,
  REGISTRATION_REGION_RU,
  RU_REGISTRATION_COUNTRY_CODE,
} from "./registrationLocations";
import {
  LABEL_CROSSFADE_TRANSITION,
  PRESS_HOVER,
  PRESS_TAP,
  PRESS_TRANSITION,
  SUBTLE_PRESS_TAP,
} from "../../utils/motionInteractions";
import { AUTH_REQUIRED_REASON } from "../../utils/authRedirect";
import { loginNative, setNativeAuthTokens } from "../../native/auth";
import {
  activateNativeRegistrationRoute,
  clearNativeSelectedRegistrationCountry,
  getNativeRoutingSnapshot,
} from "../../native/regionRouting";
import {
  isNativeApp,
  isNativeAndroid,
  isNativeIOS,
  getNativeApiBaseUrl,
  openExternalUrl,
} from "../../native/runtime";
import { isLocalDevelopmentHost } from "../../utils/localDev";
import {
  getMobileAuthKeyboardShiftY,
  getNativeAuthViewportHeight,
  getStableAuthKeyboardHeight,
  getStableMobileAuthStageMetrics,
} from "./authKeyboardLayout";
import { createAuthFieldScroller, getAuthKeyboardScrollTarget } from "./authFieldScrolling";
import { getPostAuthRedirectPath } from "./authFlow";
import { storeRegistrationOnboardingPreparation } from "../../utils/registrationOnboardingGender";
import { transitionRegistrationRoute } from "../../utils/registrationRouteTransition";
import {
  buildClassicRegistrationAvailabilityPayload,
  getPublicAuthRequestConfig,
  normalizeRegistrationAvailabilityErrors,
} from "./registrationAvailability";
import {
  MOBILE_SIGNUP_INTRO_BODY_DELAY_MS,
  MOBILE_SIGNUP_INTRO_CHAR_REVEAL_MS,
  createMobileSignupIntroRun,
  getMobileSignupIntroBody,
} from "./registrationIntroAnimation";
import {
  DEFAULT_EVENT_DISCOVERY_RADIUS_KM,
} from "../../utils/eventDiscoveryRadius";
import { writeEventLocationFilter } from "../../utils/eventLocationFilterStorage";

const USERNAME_MIN_LENGTH = 3;

const AUTH_TAB_STORAGE_KEY = "vg_auth_active_tab";
const ADMIN_REF_VISITOR_ID_STORAGE_KEY = "vg_ref_track_visitor_id";
const REGISTRATION_CODE_STORAGE_KEY = "vg_registration_code";
const REGISTRATION_CODE_HISTORY_STORAGE_KEY = "vg_registration_code_history";
const REGISTRATION_FLOW_ID_STORAGE_KEY = "vg_registration_flow_id";
const WELCOME_ROTATION_MESSAGES = [
  "Welcome!",
  "Vitaj!",
  "Willkommen!",
  "Bienvenue!",
  "Hola!",
  "Ciao!",
  "Witaj!",
  "Ahoj!",
  "Привет!",
];
const REGISTRATION_CODE_LIMIT_DEFAULT = 10;
const REGISTRATION_CODE_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const REGISTRATION_PHOTO_FRAME_SIZE = 270;
const REGISTRATION_PHOTO_MIN_SCALE = 1;
const REGISTRATION_PHOTO_MAX_SCALE = 3;
const REGISTRATION_ONBOARDING_PRELOAD_IMAGES = [
  "/assets/onboarding/registration/welcome.webp",
  "/assets/onboarding/registration/culture.webp",
  "/assets/onboarding/registration/respect.webp",
  "/assets/onboarding/registration/responsibility.webp",
  "/assets/onboarding/registration/preferences.webp",
  "/assets/onboarding/registration/come-alone.webp",
  "/assets/onboarding/registration/first-step.webp",
];

const createRegistrationFlowId = () => {
  try {
    if (typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
  } catch {
    // Fall through to a non-identifying random id for older WebViews.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

const readOrCreateRegistrationFlowId = () => {
  try {
    const existing = window.sessionStorage.getItem(REGISTRATION_FLOW_ID_STORAGE_KEY);
    if (existing) return existing;
    const created = createRegistrationFlowId();
    window.sessionStorage.setItem(REGISTRATION_FLOW_ID_STORAGE_KEY, created);
    return created;
  } catch {
    return createRegistrationFlowId();
  }
};

const clearRegistrationFlowId = () => {
  try {
    window.sessionStorage.removeItem(REGISTRATION_FLOW_ID_STORAGE_KEY);
  } catch {
    // Funnel telemetry must never interfere with registration.
  }
};
const REGISTRATION_CITY_I18N_KEYS = {
  Yekaterinburg: "yekaterinburg",
  Moscow: "moscow",
  "Saint Petersburg": "saint_petersburg",
  Novosibirsk: "novosibirsk",
  Kazan: "kazan",
  "Nizhny Novgorod": "nizhny_novgorod",
  Chelyabinsk: "chelyabinsk",
  Samara: "samara",
  Ufa: "ufa",
  "Rostov-on-Don": "rostov_on_don",
  Perm: "perm",
  Tyumen: "tyumen",
  Omsk: "omsk",
  Krasnoyarsk: "krasnoyarsk",
  Krasnodar: "krasnodar",
  Voronezh: "voronezh",
  Volgograd: "volgograd",
  Saratov: "saratov",
  Tolyatti: "tolyatti",
  Izhevsk: "izhevsk",
  Barnaul: "barnaul",
  Ulyanovsk: "ulyanovsk",
  Irkutsk: "irkutsk",
  Khabarovsk: "khabarovsk",
  Vladivostok: "vladivostok",
  Yaroslavl: "yaroslavl",
  Makhachkala: "makhachkala",
  Tomsk: "tomsk",
  Kaliningrad: "kaliningrad",
};
const MOBILE_SIGNUP_INTRO_TITLE_OFFSET_PX = -44;
export const buildMobileSignupIntroSteps = (t) => [
  {
    title: t("registration_flow.intro.respect.title", { defaultValue: "Be respectful" }),
    bodyBlocks: [
      [
        t("registration_flow.intro.respect.block_1_line_1", { defaultValue: "VaultGrid culture respects" }),
        t("registration_flow.intro.respect.block_1_line_2", { defaultValue: "personal boundaries." }),
      ],
      [
        t("registration_flow.intro.respect.block_2_line_1", { defaultValue: "There is no place for harassment," }),
        t("registration_flow.intro.respect.block_2_line_2", { defaultValue: "pressure, or behavior that makes" }),
      ],
    ],
  },
  {
    title: t("registration_flow.intro.responsible.title", { defaultValue: "Be responsible" }),
    bodyBlocks: [
      [
        t("registration_flow.intro.responsible.block_1_line_1", { defaultValue: "VaultGrid is built for people who" }),
        t("registration_flow.intro.responsible.block_1_line_2", { defaultValue: "take their actions seriously." }),
      ],
      [
        t("registration_flow.intro.responsible.block_2_line_1", { defaultValue: "Keep participant and host ratings high" }),
        t("registration_flow.intro.responsible.block_2_line_2", { defaultValue: "by showing up, cancelling in time," }),
      ],
      [
        t("registration_flow.intro.responsible.block_3_line_1", { defaultValue: "If your plans change," }),
        t("registration_flow.intro.responsible.block_3_line_2", { defaultValue: "cancel your attendance in time." }),
      ],
    ],
  },
  {
    title: t("registration_flow.intro.open_minded.title", { defaultValue: "Be open-minded" }),
    bodyBlocks: [
      [
        t("registration_flow.intro.open_minded.block_1_line_1", { defaultValue: "VaultGrid is designed to make" }),
        t("registration_flow.intro.open_minded.block_1_line_2", { defaultValue: "first events feel natural." }),
      ],
      [
        t("registration_flow.intro.open_minded.block_2_line_1", { defaultValue: "You do not need to know anyone" }),
        t("registration_flow.intro.open_minded.block_2_line_2", { defaultValue: "or bring friends." }),
      ],
      [
        t("registration_flow.intro.open_minded.block_3_line_1", { defaultValue: "You do not need anyone else" }),
        t("registration_flow.intro.open_minded.block_3_line_2", { defaultValue: "to take the first step." }),
      ],
    ],
  },
];
const buildMobileSignupIntroBlocks = (blocks) => {
  let unitIndex = 0;
  return (blocks || []).map((block, blockIndex, allBlocks) => {
    const lines = (block || []).map((line, lineIndex, allLines) => {
      const words = String(line || "")
        .split(/\s+/)
        .filter(Boolean)
        .map((word, wordIndex, allWords) => {
          const chars = Array.from(word).map((char) => {
            const delaySec =
              MOBILE_SIGNUP_INTRO_BODY_DELAY_MS / 1000 +
              (unitIndex * MOBILE_SIGNUP_INTRO_CHAR_REVEAL_MS) / 1000;
            unitIndex += 1;
            return { char, delaySec };
          });
          if (wordIndex < allWords.length - 1) {
            unitIndex += 1;
          }
          return { chars, word };
        });
      if (lineIndex < allLines.length - 1) {
        unitIndex += 1;
      }
      return { line, words };
    });
    if (blockIndex < allBlocks.length - 1) {
      unitIndex += 2;
    }
    return { lines };
  });
};
const LOCAL_MOBILE_SIGNUP_PREVIEW_COUNTRY = "SK";
const AUTH_REQUIRED_NOTICE_MS = 3800;
const REGISTRATION_IDENTITY_FIELDS = new Set([
  "username",
  "email",
  "password",
  "password1",
  "password2",
]);

function AuthLanguageToggle({ className = "", safeAreaOffset = true }) {
  const { i18n } = useTranslation();
  const { setLanguage } = useLocale();

  const current = String(i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
  const isRu = current.startsWith("ru");
  const isSk = current.startsWith("sk");
  const isCs = current.startsWith("cs");
  const isEn = !isRu && !isSk && !isCs;

  if (isRu) return null;

  const apply = async (lang) => {
    try {
      setLanguage(lang, { persist: true });
    } catch {
      try {
        await i18n.changeLanguage(lang);
      } catch {
        // ignore language-switch fallback failures on auth surface
      }
    }
  };

  return (
    <div
      className={clsx(
        "absolute left-1/2 z-20 flex -translate-x-1/2 select-none items-center text-[15px] font-bold leading-none text-white",
        className
      )}
      style={{
        top: safeAreaOffset
          ? "calc(var(--vg-safe-area-top) + 24px)"
          : "24px",
      }}
    >
      <button
        type="button"
        onClick={() => apply("en")}
        className={clsx(
          "px-0.5 py-1 transition-opacity",
          isEn ? "opacity-100" : "opacity-45 hover:opacity-80"
        )}
        aria-pressed={isEn}
        aria-label="English"
      >
        EN
      </button>

      <span className="mx-2 inline-block h-4 w-px bg-white/30" aria-hidden="true" />

      <button
        type="button"
        onClick={() => apply("sk")}
        className={clsx(
          "px-0.5 py-1 transition-opacity",
          isSk ? "opacity-100" : "opacity-45 hover:opacity-80"
        )}
        aria-pressed={isSk}
        aria-label="Slovak"
      >
        SK
      </button>
      <span className="mx-2 inline-block h-4 w-px bg-white/30" aria-hidden="true" />
      <button
        type="button"
        onClick={() => apply("cs")}
        className={clsx(
          "px-0.5 py-1 transition-opacity",
          isCs ? "opacity-100" : "opacity-45 hover:opacity-80"
        )}
        aria-pressed={isCs}
        aria-label="Čeština"
      >
        CS
      </button>
    </div>
  );
}

function normalizeCachedRegistrationCode(value) {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z0-9]{3}$/.test(code) ? code : "";
}

function readStoredRegistrationCode() {
  if (typeof window === "undefined") return "";
  try {
    return normalizeCachedRegistrationCode(window.localStorage.getItem(REGISTRATION_CODE_STORAGE_KEY));
  } catch {
    return "";
  }
}

function readStoredRegistrationCodeHistory() {
  if (typeof window === "undefined") return [];
  try {
    const raw = JSON.parse(window.localStorage.getItem(REGISTRATION_CODE_HISTORY_STORAGE_KEY) || "[]");
    if (!Array.isArray(raw)) return [];
    return Array.from(new Set(raw.map(normalizeCachedRegistrationCode).filter(Boolean))).slice(-10);
  } catch {
    return [];
  }
}

function persistRegistrationCodeState(code, history) {
  if (typeof window === "undefined") return;
  try {
    const normalized = normalizeCachedRegistrationCode(code);
    if (normalized) {
      window.localStorage.setItem(REGISTRATION_CODE_STORAGE_KEY, normalized);
    } else {
      window.localStorage.removeItem(REGISTRATION_CODE_STORAGE_KEY);
    }
    window.localStorage.setItem(
      REGISTRATION_CODE_HISTORY_STORAGE_KEY,
      JSON.stringify(Array.from(new Set((history || []).map(normalizeCachedRegistrationCode).filter(Boolean))).slice(-10))
    );
  } catch {
    // localStorage can be unavailable in private or embedded browsers; keep in-memory state.
  }
}

function createRegistrationCodeFallback(existingCodes = []) {
  const used = new Set((existingCodes || []).map(normalizeCachedRegistrationCode).filter(Boolean));
  for (let attempt = 0; attempt < 100; attempt += 1) {
    let code = "";
    for (let i = 0; i < 3; i += 1) {
      code += REGISTRATION_CODE_ALPHABET[Math.floor(Math.random() * REGISTRATION_CODE_ALPHABET.length)];
    }
    if (!used.has(code)) return code;
  }
  return "VG1";
}

function loadImageElementFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load registration photo."));
    };
    image.src = url;
  });
}

function clampRegistrationPhotoValue(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function createNativeAppleNonce() {
  const cryptoApi = window.crypto;
  if (!cryptoApi?.getRandomValues || !cryptoApi?.subtle) {
    throw new Error("Secure Apple sign-in nonce is unavailable.");
  }
  const bytes = new Uint8Array(32);
  cryptoApi.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(value) {
  const bytes = new TextEncoder().encode(String(value || ""));
  const digest = await window.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function isNativeAppleSignInCancelled(error) {
  const message = String(error?.message || error || "");
  return /cancelled|canceled|authorizationerror.*1001/i.test(message);
}

function isNativeGoogleSignInCancelled(error) {
  if (String(error?.code || "").toUpperCase() === "CANCELLED") return true;
  const message = String(error?.message || error || "");
  return /cancelled|canceled|\bCANCELLED\b|GIDSignIn.*-5/i.test(message);
}

async function createAdjustedRegistrationPhotoFile(file, cropState) {
  if (!file) return null;
  const image = await loadImageElementFromFile(file);
  const canvasSize = 1024;
  const canvas = document.createElement("canvas");
  canvas.width = canvasSize;
  canvas.height = canvasSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;

  const factor = canvasSize / REGISTRATION_PHOTO_FRAME_SIZE;
  const imageWidth = cropState?.imageSize?.w || image.naturalWidth;
  const imageHeight = cropState?.imageSize?.h || image.naturalHeight;
  const baseScale = cropState?.baseScale || (REGISTRATION_PHOTO_FRAME_SIZE / Math.min(imageWidth, imageHeight));
  const zoom = cropState?.zoom || 1;
  const pos = cropState?.pos || { x: 0, y: 0 };
  const drawWidth = imageWidth * baseScale * zoom * factor;
  const drawHeight = imageHeight * baseScale * zoom * factor;
  const dx = pos.x * factor;
  const dy = pos.y * factor;

  ctx.fillStyle = "#071127";
  ctx.fillRect(0, 0, canvasSize, canvasSize);
  ctx.drawImage(image, dx, dy, drawWidth, drawHeight);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  if (!blob) return file;
  const baseName = file.name ? file.name.replace(/\.[^.]+$/, "") : "profile-photo";
  return new File([blob], `${baseName}-avatar.jpg`, { type: "image/jpeg" });
}
async function ensureAuthCsrfCookie() {
  if (isNativeApp()) return;
  try {
    await api.get("/accounts/csrf/", getPublicAuthRequestConfig());
  } catch {
    await api.get("/csrf/", getPublicAuthRequestConfig());
  }
}

function readAdminRefVisitorId() {
  try {
    return String(localStorage.getItem(ADMIN_REF_VISITOR_ID_STORAGE_KEY) || "").trim();
  } catch {
    return "";
  }
}

function readAssistantSessionId() {
  try {
    return String(localStorage.getItem("assistant_session_id") || "").trim();
  } catch {
    return "";
  }
}

function trackMetaCompleteRegistration(userId) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  if (window.__vgMetaPixelConsentGranted !== true) return;
  const payload = userId ? { content_name: "platform_registration", user_id: String(userId) } : { content_name: "platform_registration" };
  window.fbq("track", "CompleteRegistration", payload);
}

const getRegistrationCityOptionKey = (option, country, name) =>
  option?.municipality_id ? `municipality:${option.municipality_id}` : `${country}:${name}`;

/** UA guards (Telegram iOS WebView имеет особое поведение) */
const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const isIOS = /iPhone|iPad|iPod/i.test(ua);
const isAndroid = /Android/i.test(ua);

export default function AuthPage({ onAuth, registrationOnboardingFlowPreview = false }) {
  const { t } = useTranslation("auth");
  const { language, setCountry, setLanguage } = useLocale();
  const mobileSignupIntroSteps = useMemo(
    () => buildMobileSignupIntroSteps(t),
    [t]
  );
  const getRegistrationCityDisplayName = useCallback((city) => {
    const cityName = String(city || "").trim();
    const cityKey = REGISTRATION_CITY_I18N_KEYS[cityName];
    return cityKey
      ? t(`registration_flow.city.cities.${cityKey}`, { defaultValue: cityName })
      : cityName;
  }, [t]);
  const { isInstagramIAB, isTelegramEnv } = useInstagramIAB();
  const hasTelegramWebAppInitData =
    typeof window !== "undefined" &&
    Boolean(window.Telegram?.WebApp?.initData);
  const isTelegram = isTelegramEnv || hasTelegramWebAppInitData;
  const isTelegramIOS = isIOS && isTelegram;
  const isDesktop = useIsDesktop();
  const nativeApp = isNativeApp();
  const useStableMobileAuth = !isDesktop;
  const useStableMobileWebAuth = useStableMobileAuth && !nativeApp;
  const useStableNativeAuth = useStableMobileAuth && nativeApp;
  // Mobile browsers need to resize/scroll the page while the keyboard is opening.
  // A document-level `height: 100%` + `overflow: hidden` lock can make Chromium
  // and Firefox drop the input focus, especially on small iPhones.
  const shouldAvoidAuthLock = isTelegram || isAndroid || isIOS;

  /** Подготовка корня документа под AuthPage */
  useEffect(() => {
    if (useStableMobileWebAuth) return undefined;

    // Высотный фикс и 16px для форм-контролов
    document.documentElement.classList.add('auth-height-fix');
    document.body.classList.add('auth-height-fix');

    // Не лочим документ в мобильных браузерах — иначе клавиатура может схлопываться.
    if (!shouldAvoidAuthLock) {
      document.documentElement.classList.add('auth-lock');
      document.body.classList.add('auth-lock');
    }

    // Защита от iOS автозума
    const styleEl = document.createElement('style');
    styleEl.id = 'auth-zoom-fix';
    styleEl.innerHTML = `
      body.auth-height-fix input,
      body.auth-height-fix textarea,
      body.auth-height-fix select {
        font-size: 16px !important;
      }
    `;
    document.head.appendChild(styleEl);

    return () => {
      document.documentElement.classList.remove('auth-height-fix');
      document.body.classList.remove('auth-height-fix');
      if (!shouldAvoidAuthLock) {
        document.documentElement.classList.remove('auth-lock');
        document.body.classList.remove('auth-lock');
      }
      const existing = document.getElementById('auth-zoom-fix');
      if (existing) document.head.removeChild(existing);
    };
  }, [shouldAvoidAuthLock, useStableMobileWebAuth]);

  /** Высота визуального вьюпорта / признак клавиатуры */
  const [viewportH, setViewportH] = useState(() =>
    typeof window !== "undefined"
      ? (window.visualViewport ? window.visualViewport.height : window.innerHeight)
      : 0
  );
  const [viewportW, setViewportW] = useState(() =>
    typeof window !== "undefined"
      ? (window.visualViewport ? window.visualViewport.width : window.innerWidth)
      : 0
  );
  const [isKbOpen, setIsKbOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const isInputFocusedRef = useRef(false);

  const rootRef = useRef(null);
  const contentRef = useRef(null);
  const authFieldScrollerRef = useRef(null);
  if (!authFieldScrollerRef.current) {
    authFieldScrollerRef.current = createAuthFieldScroller();
  }
  const [authStageSize, setAuthStageSize] = useState(() => ({
    width: 0,
    height: 0,
    viewportHeight:
      typeof window !== "undefined" ? window.innerHeight || 0 : 0,
    bottomInset: 0,
  }));
  const [bottomSpacer, setBottomSpacer] = useState(0);

  useEffect(() => {
    if (!useStableMobileAuth) return undefined;
    const root = rootRef.current;
    const cancel = authFieldScrollerRef.current.cancel;
    root?.addEventListener("pointerdown", cancel, { passive: true });
    root?.addEventListener("touchstart", cancel, { passive: true });
    root?.addEventListener("wheel", cancel, { passive: true });
    return () => {
      cancel();
      root?.removeEventListener("pointerdown", cancel);
      root?.removeEventListener("touchstart", cancel);
      root?.removeEventListener("wheel", cancel);
    };
  }, [useStableMobileAuth]);

  useEffect(() => {
    if (!useStableMobileWebAuth || typeof window === "undefined") return undefined;

    const visualViewport = window.visualViewport;
    let largestViewportHeight = Math.max(
      window.innerHeight || 0,
      visualViewport?.height || 0
    );
    let settleTimer = null;

    const getVisibleHeight = () =>
      Math.min(
        window.innerHeight || largestViewportHeight,
        visualViewport?.height || window.innerHeight || largestViewportHeight
      );

    const settleActiveField = () => {
      const visibleHeight = getVisibleHeight();
      largestViewportHeight = Math.max(largestViewportHeight, visibleHeight);
      window.clearTimeout(settleTimer);

      // Browser chrome changes the viewport by a few pixels too. Only treat a
      // substantial reduction as the software keyboard, then perform one
      // minimal imperative scroll after its animation settles. No React state
      // or nested auth scroller participates in the keyboard animation.
      if (visibleHeight >= largestViewportHeight - 120) {
        authFieldScrollerRef.current.cancel();
        return;
      }

      settleTimer = window.setTimeout(() => {
        const activeElement = document.activeElement;
        if (
          !activeElement ||
          !rootRef.current?.contains(activeElement) ||
          !activeElement.matches("input, textarea, select")
        ) {
          return;
        }
        const workspaceScroller = rootRef.current.closest("#app-workspace");
        const scroller = workspaceScroller || document.scrollingElement;
        if (!scroller) return;
        const scrollerRect = workspaceScroller?.getBoundingClientRect?.();
        const viewportTop = Math.max(scrollerRect?.top || 0, visualViewport?.offsetTop || 0);
        const nextScrollTop = getAuthKeyboardScrollTarget({
          field: activeElement,
          scroller,
          viewportTop,
          viewportHeight: Math.min(
            Math.max(0, (visualViewport?.offsetTop || 0) + visibleHeight - viewportTop),
            scroller.clientHeight || visibleHeight
          ),
        });
        authFieldScrollerRef.current.scroll(scroller, nextScrollTop, { smooth: true });
      }, 80);
    };

    visualViewport?.addEventListener("resize", settleActiveField);
    window.addEventListener("resize", settleActiveField);
    document.addEventListener("focusin", settleActiveField);

    return () => {
      window.clearTimeout(settleTimer);
      visualViewport?.removeEventListener("resize", settleActiveField);
      window.removeEventListener("resize", settleActiveField);
      document.removeEventListener("focusin", settleActiveField);
    };
  }, [useStableMobileWebAuth]);
  const [contentNeedsScroll, setContentNeedsScroll] = useState(false);
  const [isSmallPhone, setIsSmallPhone] = useState(false);
  const [geoCountry, setGeoCountry] = useState(null);
  const [geoCity, setGeoCity] = useState("");
  const [geoLoaded, setGeoLoaded] = useState(false);
  const [registrationRegion, setRegistrationRegion] = useState(() =>
    nativeApp
      ? getNativeRoutingSnapshot().registrationRegion
      : getRegistrationRegionFromHostname(
          typeof window !== "undefined" ? window.location?.hostname : ""
        )
  );

  const location = useLocation();
  const { search } = location;
  const useNativeIosSafeStage = nativeApp && isNativeIOS() && !isDesktop;
  const nativeAppleSignInEnabled =
    nativeApp &&
    isNativeIOS() &&
    process.env.REACT_APP_APPLE_SIGN_IN_ENABLED === "true";
  const [nativeKeyboardState, setNativeKeyboardState] = useState({
    isOpen: false,
    height: 0,
    listenersReady: false,
  });
  const nativeKeyboardOpenRef = useRef(false);
  const scrollFocusedNativeFieldIntoView = useCallback((field = document.activeElement) => {
    const scroller = contentRef.current;
    if (
      !useStableNativeAuth ||
      !nativeKeyboardOpenRef.current ||
      !scroller ||
      !field ||
      field !== document.activeElement ||
      !scroller.contains(field) ||
      !field.matches?.("input, textarea, select")
    ) {
      return;
    }

    const viewportRect = scroller.getBoundingClientRect();
    const nextScrollTop = getAuthKeyboardScrollTarget({
      field,
      scroller,
      viewportTop: viewportRect.top,
      viewportHeight: scroller.clientHeight || viewportRect.height,
      topInset: parseFloat(window.getComputedStyle(scroller).paddingTop) || 0,
    });

    authFieldScrollerRef.current.scroll(scroller, nextScrollTop, { smooth: true });
  }, [useStableNativeAuth]);
  const localMobileSignupPreviewEnabled = isLocalDevelopmentHost();
  const localRegistrationOnboardingFlowPreview =
    registrationOnboardingFlowPreview && localMobileSignupPreviewEnabled;
  const params = new URLSearchParams(search);
  const localRegistrationWalkthroughPreview =
    localRegistrationOnboardingFlowPreview && params.get("preview") === "walkthrough";
  const localPhotoStepPreview =
    localRegistrationOnboardingFlowPreview && params.get("step") === "photo";
  const directMobileSignupIntroPreview =
    localMobileSignupPreviewEnabled && params.get("preview") === "signup-intro";
  const refCode = params.get('ref');

  const nextRaw = params.get("next");
  const nextFromQuery = nextRaw ? decodeURIComponent(nextRaw) : null;
  const nextFromState =
    location?.state?.from
      ? `${location.state.from.pathname || ""}${location.state.from.search || ""}${location.state.from.hash || ""}`
      : null;
  const nextPath = nextFromQuery || nextFromState;

  const modeParam = params.get("mode");
  const gTokenParam = params.get("g_token");
  const tabParam = params.get("tab");
  const googleErrorParam = params.get("google_error");
  const authReasonParam = params.get("reason");
  const authRequiredNoticeRequested =
    authReasonParam === AUTH_REQUIRED_REASON ||
    location?.state?.authReason === AUTH_REQUIRED_REASON;

  const [tab, setTab] = useState(() => {
    if (localRegistrationOnboardingFlowPreview) {
      return "register";
    }
    if (tabParam === "login" || tabParam === "register") {
      return tabParam;
    }
    if (authRequiredNoticeRequested) {
      return "login";
    }
    if (typeof window !== "undefined") {
      try {
        const storedTab = window.sessionStorage.getItem(AUTH_TAB_STORAGE_KEY);
        if (storedTab === "login" || storedTab === "register") {
          return storedTab;
        }
      } catch {
        // ignore storage failures
      }
    }
    return "register";
  });
  const showAuthRedesign = tab === "login" || tab === "forgot" || tab === "register";
  const [errorMsg, setErrorMsg] = useState("");
  const [legalBundle, setLegalBundle] = useState(null);
  const [legalBundleError, setLegalBundleError] = useState("");
  const isRussianLegalBundle = legalBundle?.contour === "RU";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refInfo, setRefInfo] = useState(null);
  const navigate = useNavigate();
  const [welcomeIndex, setWelcomeIndex] = useState(0);
  const [welcomeVisible, setWelcomeVisible] = useState(!authRequiredNoticeRequested);
  const [authRequiredNoticeActive, setAuthRequiredNoticeActive] = useState(authRequiredNoticeRequested);
  const [authRequiredNoticeVisible, setAuthRequiredNoticeVisible] = useState(false);
  const [registrationCode, setRegistrationCode] = useState(() =>
    localRegistrationWalkthroughPreview ? "RAT" : readStoredRegistrationCode());
  const [registrationCodeLoading, setRegistrationCodeLoading] = useState(false);
  const [registrationCodeError, setRegistrationCodeError] = useState("");
  const [registrationCodeAutoFetched, setRegistrationCodeAutoFetched] = useState(false);
  const [registrationCodeRemaining, setRegistrationCodeRemaining] = useState(null);
  const [registrationCodeLimit, setRegistrationCodeLimit] = useState(REGISTRATION_CODE_LIMIT_DEFAULT);
  const [registrationCodeResetAt, setRegistrationCodeResetAt] = useState(null);
  const [registrationCodeFlashLabel, setRegistrationCodeFlashLabel] = useState("");
  const [registrationCodeHistory, setRegistrationCodeHistory] = useState(() => readStoredRegistrationCodeHistory());
  const [registrationCodeHistoryIndex, setRegistrationCodeHistoryIndex] = useState(() => {
    const storedHistory = readStoredRegistrationCodeHistory();
    const storedCode = readStoredRegistrationCode();
    const storedIndex = storedCode ? storedHistory.indexOf(storedCode) : -1;
    return storedIndex >= 0 ? storedIndex : storedHistory.length ? storedHistory.length - 1 : -1;
  });
  const registrationCodeFlashTimerRef = useRef(null);
  const [registrationStep, setRegistrationStep] = useState(() => {
    if (localPhotoStepPreview) return "photo";
    return localRegistrationOnboardingFlowPreview && !localRegistrationWalkthroughPreview ? "city" : "form";
  });
  const registrationRoutePromiseRef = useRef(Promise.resolve());
  const selectedRegistrationCountryRef = useRef("");
  const selectedCityNeedsLegalReviewRef = useRef(false);
  const [registrationCity, setRegistrationCity] = useState("");
  const [registrationMunicipality, setRegistrationMunicipality] = useState(null);
  const [registrationCityQuery, setRegistrationCityQuery] = useState("");
  const [registrationCitySearchActive, setRegistrationCitySearchActive] = useState(false);
  const [registrationAge, setRegistrationAge] = useState("");
  const [registrationStepError, setRegistrationStepError] = useState(() =>
    localPhotoStepPreview && params.get("preview_error") === "try_another"
      ? t("registration_flow.photo.errors.try_another")
      : ""
  );
  const [registrationEmailCodeInput, setRegistrationEmailCodeInput] = useState("");
  const [registrationEmailSentTo, setRegistrationEmailSentTo] = useState(
    localRegistrationWalkthroughPreview ? "preview@vaultgrid.test" : "");
  const [registrationEmailVerificationToken, setRegistrationEmailVerificationToken] = useState("");
  const [registrationEmailSending, setRegistrationEmailSending] = useState(false);
  const [registrationEmailResendCooldown, setRegistrationEmailResendCooldown] = useState(0);
  const [registrationEmailResendStatus, setRegistrationEmailResendStatus] = useState("");
  const [registrationPhotoFile, setRegistrationPhotoFile] = useState(null);
  const [registrationPhotoPreview, setRegistrationPhotoPreview] = useState("");
  const [registrationPhotoReady, setRegistrationPhotoReady] = useState(false);
  const [registrationPhotoImageSize, setRegistrationPhotoImageSize] = useState({ w: 0, h: 0 });
  const [registrationPhotoBaseScale, setRegistrationPhotoBaseScale] = useState(1);
  const [registrationPhotoZoom, setRegistrationPhotoZoom] = useState(1);
  const [registrationPhotoPos, setRegistrationPhotoPos] = useState({ x: 0, y: 0 });
  const [registrationPhotoDragging, setRegistrationPhotoDragging] = useState(false);
  const [registrationPhotoChecking, setRegistrationPhotoChecking] = useState(false);
  const [registrationFlowId] = useState(() => readOrCreateRegistrationFlowId());
  const [registrationFinalIntroActive, setRegistrationFinalIntroActive] = useState(false);
  const [registrationFinalIntroStage, setRegistrationFinalIntroStage] = useState(0);
  const [registrationFinalIntroMode, setRegistrationFinalIntroMode] = useState("real");
  const [registrationFinalIntroComplete, setRegistrationFinalIntroComplete] = useState(false);
  const [registrationFinalIntroRun, setRegistrationFinalIntroRun] = useState(null);
  const directMobileSignupIntroPreviewStartedRef = useRef(false);
  const registrationPhotoInputRef = useRef(null);
  const registrationPhotoDragStartRef = useRef({ x: 0, y: 0 });
  const registrationPhotoPointersRef = useRef(new Map());
  const registrationPhotoPinchRef = useRef(null);
  const registrationPhotoCropRef = useRef({
    imageSize: { w: 0, h: 0 },
    baseScale: 1,
    zoom: 1,
    pos: { x: 0, y: 0 },
  });
  const registrationCitySearchInputRef = useRef(null);
  const commitRegistrationRouteTransition = useCallback((commitNavigation) => (
    transitionRegistrationRoute(rootRef.current, commitNavigation, "[data-registration-destination='cards']")
  ), []);

  const [forgotStep, setForgotStep] = useState(1);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCode, setForgotCode] = useState("");

  const [avatars, setAvatars] = useState([]);
  const [loadingAvatars, setLoadingAvatars] = useState(false);
  const [avatarIdx, setAvatarIdx] = useState(null);
  const avatarsScrollRef = useRef(null);
  const avatarsWrapRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(5);

  const [pwVisible, setPwVisible] = useState({});

  useEffect(() => {
    if (!nativeApp || isDesktop) return undefined;

    let disposed = false;
    const listenerHandles = [];
    let restoreNativeResizeMode = null;
    let closingTimer = null;
    const setKeyboardOpen = (info = {}) => {
      if (disposed) return;
      window.clearTimeout(closingTimer);
      nativeKeyboardOpenRef.current = true;
      setNativeKeyboardState((previous) => ({
        ...previous,
        isOpen: true,
        height: getStableAuthKeyboardHeight({
          previousHeight: previous.height,
          keyboardHeight: info.keyboardHeight,
          wasOpen: previous.isOpen,
          keepLayoutStable: useNativeIosSafeStage && Boolean(
            document.activeElement?.closest('[data-testid="stable-mobile-web-login"]')
          ),
        }),
      }));
    };
    const commitKeyboardClosing = () => {
      if (disposed) return;
      authFieldScrollerRef.current.cancel();
      nativeKeyboardOpenRef.current = false;
      // Start restoring the form with the first native hide frame. Waiting for
      // keyboardDidHide leaves the lifted layout hanging over an empty area.
      setNativeKeyboardState((previous) => ({
        ...previous,
        isOpen: false,
        height: 0,
      }));
    };
    const setKeyboardClosing = () => {
      window.clearTimeout(closingTimer);
      // iOS can send willHide immediately before willShow while switching
      // keyboard types. Do not briefly restore the full layout in that gap.
      if (rootRef.current?.contains(document.activeElement) &&
          document.activeElement?.matches("input, textarea, select")) {
        closingTimer = window.setTimeout(commitKeyboardClosing, 80);
      } else {
        commitKeyboardClosing();
      }
    };
    const setKeyboardClosed = () => {
      if (disposed) return;
      window.clearTimeout(closingTimer);
      commitKeyboardClosing();

      // WKWebView can retain a temporary focus scroll offset after dismissal.
      // Return both possible scrollers to the auth canvas origin once the
      // keyboard is actually gone, never during its closing animation.
      window.requestAnimationFrame(() => {
        if (disposed || nativeKeyboardOpenRef.current) return;
        contentRef.current?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
        rootRef.current?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
        window.scrollTo?.(0, 0);
      });
    };

    const registerKeyboardListeners = async () => {
      try {
        const { Keyboard, KeyboardResize } = await import("@capacitor/keyboard");

        if (useNativeIosSafeStage) {
          const previousResizeMode = await Keyboard.getResizeMode()
            .then((result) => result?.mode || KeyboardResize.Native)
            .catch(() => KeyboardResize.Native);

          // Keep the full auth page behind the translucent iOS keyboard. The
          // global Native mode resizes WKWebView after the keyboard animation,
          // which visibly cuts off the gradient and the rounded keyboard
          // corners. Auth already owns its precise keyboard avoidance, so it
          // must use overlay mode and restore the global mode on unmount.
          await Keyboard.setResizeMode({ mode: KeyboardResize.None });
          restoreNativeResizeMode = () =>
            Keyboard.setResizeMode({ mode: previousResizeMode }).catch(() => {});
        }

        const handles = await Promise.all([
          Keyboard.addListener("keyboardWillShow", setKeyboardOpen),
          Keyboard.addListener("keyboardDidShow", setKeyboardOpen),
          Keyboard.addListener("keyboardWillHide", setKeyboardClosing),
          Keyboard.addListener("keyboardDidHide", setKeyboardClosed),
        ]);
        if (disposed) {
          handles.forEach((handle) => handle?.remove?.());
          restoreNativeResizeMode?.();
          return;
        }
        listenerHandles.push(...handles);
        setNativeKeyboardState((previous) => ({
          ...previous,
          listenersReady: true,
        }));
      } catch (error) {
        console.warn("Native auth keyboard listeners are unavailable", error);
      }
    };

    registerKeyboardListeners();
    return () => {
      disposed = true;
      window.clearTimeout(closingTimer);
      nativeKeyboardOpenRef.current = false;
      listenerHandles.forEach((handle) => handle?.remove?.());
      restoreNativeResizeMode?.();
    };
  }, [isDesktop, nativeApp, useNativeIosSafeStage]);

  useEffect(() => {
    if (!useStableNativeAuth || !nativeKeyboardState.isOpen) return undefined;

    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        scrollFocusedNativeFieldIntoView();
      });
    });
    const settleTimer = window.setTimeout(() => {
      scrollFocusedNativeFieldIntoView();
    }, 90);

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      window.clearTimeout(settleTimer);
    };
  }, [
    nativeKeyboardState.height,
    nativeKeyboardState.isOpen,
    scrollFocusedNativeFieldIntoView,
    useStableNativeAuth,
  ]);

  // Social providers authenticate identity. VaultGrid still collects the
  // product-specific public profile fields required by its social experience.
  const [registerMode, setRegisterMode] = useState("classic"); // "classic" | "google" | "apple"
  const [googleToken, setGoogleToken] = useState(null);
  const [appleToken, setAppleToken] = useState(null);
  const socialProvider =
    registerMode === "google"
      ? "google"
      : registerMode === "apple"
        ? "apple"
        : null;
  const socialToken =
    socialProvider === "google"
      ? googleToken
      : socialProvider === "apple"
        ? appleToken
        : null;
  const isSocialRegistration = Boolean(socialProvider);
  const registrationStepOrder = isSocialRegistration
    ? ["form", "city", "age", "photo"]
    : ["form", "email", "city", "age", "photo"];
  const trackedSignupViewsRef = useRef(new Set());

  const COUNTRIES = PROFILE_LOCATION_COUNTRIES;

  /** Удерживаем поле над клавиатурой */
  const ensureFieldVisible = (el) => {
    if (!el) return;
    if (showAuthRedesign && useStableMobileWebAuth) return;
    if (showAuthRedesign && useStableNativeAuth) {
      window.requestAnimationFrame(() => scrollFocusedNativeFieldIntoView(el));
      return;
    }
    const vv = window.visualViewport;
    const vh = vv ? vv.height : window.innerHeight;
    const bottomPadding = 16; // зазор от клавиатуры
    const rect = el.getBoundingClientRect();
    const safeBottom = vh - bottomPadding;
    const overlap = Math.max(0, rect.bottom - safeBottom);

    if (overlap > 0) {
      setBottomSpacer((prev) => Math.max(prev, overlap + 24));
      requestAnimationFrame(() => {
        try {
          el.scrollIntoView({ block: 'center', inline: 'nearest' });
        } catch (_) {
          const scroller = rootRef.current || document.scrollingElement;
          if (scroller) scroller.scrollTop += overlap;
        }
      });
    }
  };

  /** События фокуса/резайза/ориентации */
  useEffect(() => {
    if (useStableMobileWebAuth) return undefined;

    const recompute = () => {
      const rawH = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const rawW = window.visualViewport ? window.visualViewport.width : window.innerWidth;
      setViewportH(rawH);
      setViewportW(rawW);

      const isMobileWidth = typeof window !== 'undefined' && window.innerWidth < 768;
      const baseH = window.innerHeight || rawH;
      const vv = window.visualViewport || null;

      // Детект клавиатуры (Android иногда даёт маленькую дельту, поэтому порог ниже + учитываем offsetTop)
      const threshold = 70;
      const delta = baseH - rawH;
      const kbByDelta = delta > threshold;
      const kbByOffset = vv ? vv.offsetTop > 0 : false;
      setIsKbOpen(isMobileWidth && (kbByDelta || kbByOffset || isInputFocusedRef.current));

      // Очень маленькие телефоны (типа iPhone SE) — уменьшаем вертикальные отступы
      setIsSmallPhone(isMobileWidth && baseH <= 700);

      // Учитываем высоту нижней навигации при расчёте необходимости прокрутки
      const NAV_OVERLAY = isMobileWidth ? 80 : 0;
      const visibleH = Math.max(rawH - NAV_OVERLAY, 320);

      const el = contentRef.current;
      if (el) {
        const needs = el.scrollHeight > visibleH + 8;
        setContentNeedsScroll(needs);
      }
    };

    let focusRetryTimer = null;
    let focusOutTimer = null;

    const isEditableElement = (element) => {
      if (!element) return false;
      const tagName = String(element.tagName || "").toUpperCase();
      return tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT";
    };

    const onFocusIn = (e) => {
      const tEl = e.target;
      if (!isEditableElement(tEl)) return;

      window.clearTimeout(focusOutTimer);
      isInputFocusedRef.current = true;
      setIsInputFocused(true);
      window.setTimeout(() => {
        if (tEl.isConnected && document.activeElement === tEl) ensureFieldVisible(tEl);
      }, 120);

      // Android WebViews sometimes drop focus during the first viewport resize.
      // Do not do this on iOS browsers: a synthetic focus shortly after a real
      // tap can make Chrome/Firefox collapse the keyboard again.
      window.clearTimeout(focusRetryTimer);
      if (isAndroid || isTelegramIOS) {
        focusRetryTimer = window.setTimeout(() => {
          if (!isInputFocusedRef.current || !tEl.isConnected) return;
          if (document.activeElement !== tEl) {
            try {
              tEl.focus({ preventScroll: true });
            } catch (_) {
              try { tEl.focus(); } catch (_) {}
            }
          }
        }, 260);
      }
    };

    const onFocusOut = () => {
      // Wait for a possible focus transition to another field before changing
      // keyboard layout state. A delayed stale focusout must not override a new
      // focusin event.
      window.clearTimeout(focusOutTimer);
      focusOutTimer = window.setTimeout(() => {
        if (isEditableElement(document.activeElement)) return;
        isInputFocusedRef.current = false;
        setIsInputFocused(false);
        setBottomSpacer(0);
        // Re-evaluate the viewport with the updated focus ref. Without this,
        // `isKbOpen` can retain the value captured during focus even after the
        // keyboard has closed, leaving the login form permanently lifted.
        recompute();
      }, 150);
    };

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);

    const onOrientationChange = () => {
      setTimeout(() => { setBottomSpacer(0); recompute(); }, 250);
    };

    const onVisibilityChange = () => {
      // Возврат из бэкграунда — пересчёт
      if (document.visibilityState === 'visible') setTimeout(recompute, 150);
    };

    // visualViewport есть не везде
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', recompute);
    }
    window.addEventListener('resize', recompute);
    window.addEventListener('orientationchange', onOrientationChange);
    document.addEventListener('visibilitychange', onVisibilityChange);

    // начальный замер
    recompute();

    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      window.clearTimeout(focusRetryTimer);
      window.clearTimeout(focusOutTimer);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', recompute);
      }
      window.removeEventListener('resize', recompute);
      window.removeEventListener('orientationchange', onOrientationChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [useStableMobileWebAuth]);

  /** Когда скролл запрещён — удерживаем страницу у верха, чтобы не было «застывшего белого поля» */
  useEffect(() => {
    if (useStableMobileWebAuth) return undefined;
    const el = rootRef.current;
    if (!el) return;
    const shouldClamp = !isKbOpen && !isInputFocused && !contentNeedsScroll;
    const clamp = () => { if (shouldClamp && el.scrollTop !== 0) el.scrollTop = 0; };
    clamp();
    const onScroll = () => clamp();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [contentNeedsScroll, isInputFocused, isKbOpen, useStableMobileWebAuth]);

  useLayoutEffect(() => {
    if (!showAuthRedesign || useStableMobileWebAuth) return undefined;
    const el = contentRef.current;
    if (!el) return undefined;

    const updateStageSize = () => {
      const root = rootRef.current;
      const nextWidth = el.clientWidth || 0;
      const contentStyle = window.getComputedStyle(el);
      const topPadding = parseFloat(contentStyle.paddingTop) || 0;
      const bottomPadding = parseFloat(contentStyle.paddingBottom) || 0;
      const measuredHeight = Math.max(0, el.clientHeight - topPadding - bottomPadding);
      const measuredViewportHeight = root?.clientHeight || 0;
      const rootRect = root?.getBoundingClientRect?.();
      const contentRect = el.getBoundingClientRect?.();
      const measuredBottomInset =
        rootRect && contentRect
          ? Math.max(0, rootRect.bottom - contentRect.bottom) + bottomPadding
          : 0;
      setAuthStageSize((previous) =>
        getStableMobileAuthStageMetrics({
          previousWidth: previous.width,
          previousHeight: previous.height,
          previousViewportHeight: previous.viewportHeight,
          previousBottomInset: previous.bottomInset,
          measuredWidth: nextWidth,
          measuredHeight,
          measuredViewportHeight,
          measuredBottomInset,
          isDesktop,
          isKeyboardActive:
            isInputFocusedRef.current || nativeKeyboardOpenRef.current,
        })
      );
    };

    updateStageSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateStageSize);
      return () => window.removeEventListener("resize", updateStageSize);
    }

    const observer = new ResizeObserver(updateStageSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isDesktop, showAuthRedesign, useStableMobileWebAuth]);

  /** Загрузка аватаров (по табу Регистрация) */
  useEffect(() => {
    if (tab !== "register") return;
    setLoadingAvatars(true);
    api.get("/miniapp/get-default-avatars/", getPublicAuthRequestConfig())
      .then(res => {
        const loaded = res.data.map(skin => ({
          id: skin.name,
          name: skin.name,
          display: skin.display_name,
          rarity: skin.rarity,
          src: `/assets/avatars/${skin.name}.webp`
        }));
        setAvatars(loaded);
        setAvatarIdx(null);
      })
      .catch(err => console.error("Failed to load default avatars:", err))
      .finally(() => setLoadingAvatars(false));
  }, [tab]);

  /** Видимый счётчик аватаров (адаптив) */
  useEffect(() => {
    const compute = () => {
      if (!avatarsWrapRef.current) return;
      const wrapWidth = avatarsWrapRef.current.clientWidth || 0;
      const item = isDesktop ? 80 : 64;
      const gap = 12;
      const maxToShow = Math.min(avatars.length || 0, 5);
      if (wrapWidth <= 0 || maxToShow === 0) { setVisibleCount(0); return; }
      const fit = Math.max(1, Math.min(maxToShow, Math.floor((wrapWidth + gap) / (item + gap))));
      setVisibleCount(fit);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [isDesktop, avatars]);

  /** Валидация */
  const loginSchema = yup.object({
    username: yup.string().required(t("validation.username_or_email_required")),
    password: yup.string().required(t("validation.password_required_full")),
  });

  const registerSchema = yup.object({
    username: yup.string()
      .required(t("validation.username_required"))
      .min(USERNAME_MIN_LENGTH, t("validation.username_min", { count: USERNAME_MIN_LENGTH }))
      .max(16)
      .matches(/^[a-z0-9_]+$/, t("validation.only_latin")),
    email: yup.string().required(t("validation.email_required")).email(),
    password1: yup.string()
      .required(t("validation.password_required_full"))
      .min(8, t("validation.password_min")),
    password2: yup.string()
      .oneOf([yup.ref("password1")], t("validation.passwords_must_match"))
      .required(t("validation.repeat_password_required")),
    // GeoIP may be unavailable in Instagram/iOS private-relay traffic. The
    // required city step sets the country before the final registration POST.
    country: isRussianLegalBundle
      ? yup.string().required(t("validation.country_required"))
      : yup.string().notRequired(),
    terms: yup.boolean().oneOf([true], t("validation.terms_required")).required(),
    personal_data_consent: isRussianLegalBundle
      ? yup.boolean().oneOf([true], t("validation.personal_data_consent_required")).required()
      : yup.boolean().notRequired(),
  });

  // Social sign-up never asks again for a provider-verified e-mail or password.
  const socialRegisterSchema = yup.object({
    username: yup.string()
      .required(t("validation.username_required"))
      .min(USERNAME_MIN_LENGTH, t("validation.username_min", { count: USERNAME_MIN_LENGTH }))
      .max(16)
      .matches(/^[a-z0-9_]+$/, t("validation.only_latin")),
    email: yup.string().required(t("validation.email_required")).email(),
    country: isRussianLegalBundle
      ? yup.string().required(t("validation.country_required"))
      : yup.string().notRequired(),
    terms: yup.boolean().oneOf([true], t("validation.terms_required")).required(),
    personal_data_consent: isRussianLegalBundle
      ? yup.boolean().oneOf([true], t("validation.personal_data_consent_required")).required()
      : yup.boolean().notRequired(),
  });

  const forgotSchemaStep1 = yup.object({ email: yup.string().required(t("validation.email_required")).email() });
  const forgotSchemaStep2 = yup.object({
    email: yup.string().required(t("validation.email_required")).email(),
    code: yup.string().required(t("validation.code_required")).length(6, t("validation.six_digit_code")),
  });
  const forgotSchemaStep3 = yup.object({
    email: yup.string().required(t("validation.email_required")).email(),
    code: yup.string().required(t("validation.code_required")).length(6, t("validation.six_digit_code")),
    new_password1: yup.string()
      .required(t("validation.password_required_full"))
      .min(8, t("validation.password_min")),
    new_password2: yup.string()
      .oneOf([yup.ref("new_password1")], t("validation.passwords_must_match"))
      .required(t("validation.repeat_new_password_required")),
  });

  const schema =
    tab === "login" ? loginSchema :
    tab === "register" ? (isSocialRegistration ? socialRegisterSchema : registerSchema) :
    (forgotStep === 1 ? forgotSchemaStep1 : forgotStep === 2 ? forgotSchemaStep2 : forgotSchemaStep3);

  const { register, handleSubmit, reset, setError, clearErrors, watch, setValue, getValues, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { terms: false, personal_data_consent: false },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    criteriaMode: "all",
  });

  const selectedCountry = watch("country");
  const selectedMunicipalityId =
    registrationMunicipality?.name === registrationCity.trim()
    && registrationMunicipality?.country === selectedCountry
      ? registrationMunicipality.id
      : null;
  const acceptedTerms = watch("terms");
  const acceptedPersonalDataConsent = watch("personal_data_consent");
  const registerEmailValue = watch("email");

  useEffect(() => {
    if (tab !== "register") return undefined;
    let active = true;
    setLegalBundleError("");
    setLegalBundle(null);
    api
      .get("/accounts/legal/documents/", {
        ...getPublicAuthRequestConfig(),
        params: { language: language || "en", ...(registrationCity ? { country: selectedCountry } : {}) },
      })
      .then((response) => {
        if (active) setLegalBundle(response.data || null);
      })
      .catch(() => {
        if (active) {
          setLegalBundle(null);
          setLegalBundleError(t("legal_bundle_load_error"));
        }
      });
    return () => {
      active = false;
    };
  }, [language, registrationRegion, registrationCity, selectedCountry, t, tab]);

  useEffect(() => {
    if (!showAuthRedesign || tab !== "register" || registerMode !== "classic") return;
    setValue("email_repeat", registerEmailValue || "", { shouldValidate: false });
  }, [showAuthRedesign, tab, registerMode, registerEmailValue, setValue]);

  useEffect(() => {
    if (!showAuthRedesign || tab !== "register" || registerMode !== "classic") return;
    if (!localMobileSignupPreviewEnabled || selectedCountry) return;
    setGeoCountry(LOCAL_MOBILE_SIGNUP_PREVIEW_COUNTRY);
    setGeoLoaded(true);
    setValue("country", LOCAL_MOBILE_SIGNUP_PREVIEW_COUNTRY, { shouldValidate: false });
  }, [
    localMobileSignupPreviewEnabled,
    registerMode,
    selectedCountry,
    setValue,
    showAuthRedesign,
    tab,
  ]);

  useEffect(() => {
    if (!showAuthRedesign || tab !== "register" || registrationStep !== "photo") return;
    REGISTRATION_ONBOARDING_PRELOAD_IMAGES.forEach((src) => {
      const image = new Image();
      image.src = src;
    });
    void import("../special/RegistrationOnboardingPreviewPage.jsx");
  }, [registrationStep, showAuthRedesign, tab]);

  useEffect(() => {
    if (!authRequiredNoticeRequested) {
      setAuthRequiredNoticeVisible(false);
      setAuthRequiredNoticeActive(false);
      setWelcomeVisible(true);
      return undefined;
    }

    let restoreTimer;
    setAuthRequiredNoticeActive(true);
    setWelcomeVisible(false);

    const showTimer = setTimeout(() => {
      setAuthRequiredNoticeVisible(true);
    }, 40);

    const hideTimer = setTimeout(() => {
      setAuthRequiredNoticeVisible(false);
      restoreTimer = setTimeout(() => {
        setAuthRequiredNoticeActive(false);
        setWelcomeVisible(true);
      }, 350);
    }, AUTH_REQUIRED_NOTICE_MS);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      if (restoreTimer) clearTimeout(restoreTimer);
    };
  }, [authRequiredNoticeRequested]);

  useEffect(() => {
    if (authRequiredNoticeActive) return undefined;
    let fadeTimer;
    const interval = setInterval(() => {
      setWelcomeVisible(false);
      fadeTimer = setTimeout(() => {
        setWelcomeIndex((idx) => (idx + 1) % WELCOME_ROTATION_MESSAGES.length);
        setWelcomeVisible(true);
      }, 350);
    }, 2400);

    return () => {
      clearInterval(interval);
      if (fadeTimer) clearTimeout(fadeTimer);
    };
  }, [authRequiredNoticeActive]);

  useEffect(() => {
    if (!registrationFinalIntroActive) {
      setRegistrationFinalIntroStage(0);
      setRegistrationFinalIntroComplete(false);
      return undefined;
    }
    if (!registrationFinalIntroRun) return undefined;

    setRegistrationFinalIntroStage(0);
    let elapsedMs = 0;
    const timers = [
      ...registrationFinalIntroRun.stepDurations.slice(0, -1).map((duration, index) => {
        elapsedMs += duration;
        return setTimeout(() => setRegistrationFinalIntroStage(index + 1), elapsedMs);
      }),
    ];
    timers.push(
      setTimeout(
        () => setRegistrationFinalIntroStage(registrationFinalIntroRun.steps.length),
        registrationFinalIntroRun.welcomeAtMs
      )
    );

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [registrationFinalIntroActive, registrationFinalIntroRun]);

  useEffect(() => {
    if (
      !directMobileSignupIntroPreview ||
      directMobileSignupIntroPreviewStartedRef.current
    ) {
      return undefined;
    }

    let completeTimer;
    // LocaleProvider restores the saved language in an effect. Waiting for one
    // short quiet period lets this dev-only preview freeze the restored copy,
    // rather than the initial English fallback from the first render.
    const startTimer = setTimeout(() => {
      directMobileSignupIntroPreviewStartedRef.current = true;
      const introRun = createMobileSignupIntroRun(mobileSignupIntroSteps);
      setRegistrationFinalIntroMode("local-preview");
      setRegistrationFinalIntroComplete(false);
      setRegistrationFinalIntroRun(introRun);
      setRegistrationFinalIntroActive(true);

      completeTimer = setTimeout(() => {
        setRegistrationFinalIntroComplete(true);
      }, introRun.completeAtMs);
    }, 250);

    return () => {
      clearTimeout(startTimer);
      if (completeTimer) clearTimeout(completeTimer);
    };
  }, [directMobileSignupIntroPreview, mobileSignupIntroSteps]);

  useEffect(() => {
    if (registrationEmailResendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setRegistrationEmailResendCooldown((value) => Math.max(0, value - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [registrationEmailResendCooldown]);

  useEffect(() => {
    if (!registrationEmailResendStatus) return undefined;
    const timer = setTimeout(() => {
      setRegistrationEmailResendStatus("");
    }, 1800);
    return () => clearTimeout(timer);
  }, [registrationEmailResendStatus]);

  const resetMobileRegistrationFlow = ({ clearPhoto = true, keepCity = false } = {}) => {
    setRegistrationStep("form");
    if (!keepCity) {
      if (nativeApp) clearNativeSelectedRegistrationCountry();
      selectedRegistrationCountryRef.current = "";
      selectedCityNeedsLegalReviewRef.current = false;
      registrationRoutePromiseRef.current = Promise.resolve();
      setRegistrationCity("");
      setRegistrationCityQuery("");
      setRegistrationCitySearchActive(false);
    }
    setRegistrationEmailCodeInput("");
    setRegistrationEmailSentTo("");
    setRegistrationEmailVerificationToken("");
    setRegistrationEmailSending(false);
    setRegistrationEmailResendCooldown(0);
    setRegistrationEmailResendStatus("");
    setRegistrationStepError("");
    setRegistrationPhotoChecking(false);
    if (clearPhoto) {
      setRegistrationPhotoFile(null);
    }
  };

  const returnFromLocalMobileSignupPreview = () => {
    setRegistrationFinalIntroActive(false);
    setRegistrationFinalIntroMode("real");
    setRegistrationFinalIntroComplete(false);
    setRegistrationPhotoChecking(false);
    setIsSubmitting(false);
    setErrorMsg("");
    setRegistrationStepError("");
    setRegistrationStep("photo");
  };

  const showRegistrationCodeFlash = (label) => {
    if (registrationCodeFlashTimerRef.current) {
      clearTimeout(registrationCodeFlashTimerRef.current);
    }
    setRegistrationCodeFlashLabel(label);
    registrationCodeFlashTimerRef.current = setTimeout(() => {
      setRegistrationCodeFlashLabel("");
      registrationCodeFlashTimerRef.current = null;
    }, 1800);
  };

  const rememberRegistrationCode = (nextCode) => {
    const normalized = normalizeCachedRegistrationCode(nextCode);
    if (!normalized) return;

    setRegistrationCode(normalized);
    const withoutDuplicate = registrationCodeHistory.filter((code) => code !== normalized);
    const nextHistory = [...withoutDuplicate, normalized].slice(-10);
    setRegistrationCodeHistory(nextHistory);
    setRegistrationCodeHistoryIndex(nextHistory.length - 1);
    persistRegistrationCodeState(normalized, nextHistory);
  };

  const applyFallbackRegistrationCode = (excludeCodes = []) => {
    const excluded = new Set((excludeCodes || []).map(normalizeCachedRegistrationCode).filter(Boolean));
    const storedCode = readStoredRegistrationCode();
    const fallbackHistory = registrationCodeHistory.filter((code) => !excluded.has(code));
    const fallbackCode =
      (!excluded.has(registrationCode) && registrationCode) ||
      fallbackHistory[fallbackHistory.length - 1] ||
      (!excluded.has(storedCode) && storedCode) ||
      createRegistrationCodeFallback([...registrationCodeHistory, ...excluded]);
    rememberRegistrationCode(fallbackCode);
    setRegistrationCodeError("");
    return fallbackCode;
  };

  const selectRegistrationCodeFromHistory = (direction) => {
    const historyLength = registrationCodeHistory.length;
    if (historyLength < 2) return;
    const safeIndex = registrationCodeHistoryIndex >= 0 ? registrationCodeHistoryIndex : historyLength - 1;
    const nextIndex = Math.min(historyLength - 1, Math.max(0, safeIndex + direction));
    if (nextIndex === safeIndex) return;
    setRegistrationCode(registrationCodeHistory[nextIndex]);
    setRegistrationCodeHistoryIndex(nextIndex);
    setRegistrationCodeError("");
    persistRegistrationCodeState(registrationCodeHistory[nextIndex], registrationCodeHistory);
  };

  const removeRegistrationCodeFromHistory = (codeToRemove) => {
    const normalized = String(codeToRemove || "").trim().toUpperCase();
    if (!normalized) return false;

    const nextHistory = registrationCodeHistory.filter((code) => code !== normalized);
    const fallbackCode = nextHistory[nextHistory.length - 1] || "";
    setRegistrationCodeHistory(nextHistory);
    setRegistrationCodeHistoryIndex(nextHistory.length ? nextHistory.length - 1 : -1);
    setRegistrationCode(fallbackCode);
    persistRegistrationCodeState(fallbackCode, nextHistory);
    return Boolean(fallbackCode);
  };

  const fetchRegistrationCode = async ({ auto = false } = {}) => {
    if (!auto && registrationCodeRemaining === 0 && registrationCode) {
      showRegistrationCodeFlash("Limit reached");
      return;
    }
    if (auto) {
      setRegistrationCodeAutoFetched(true);
    }
    setRegistrationCodeLoading(true);
    setRegistrationCodeError("");
    try {
      const response = await api.get("/accounts/registration-code/", getPublicAuthRequestConfig());
      const nextCode = String(response.data?.code || "").trim().toUpperCase();
      if (!nextCode) {
        throw new Error("empty_code");
      }
      rememberRegistrationCode(nextCode);
      if (Number.isFinite(Number(response.data?.remaining))) {
        setRegistrationCodeRemaining(Number(response.data.remaining));
      }
      if (Number.isFinite(Number(response.data?.limit))) {
        setRegistrationCodeLimit(Number(response.data.limit));
      }
      setRegistrationCodeResetAt(response.data?.reset_at || null);
      if (!auto && Number.isFinite(Number(response.data?.remaining))) {
        const remaining = Number(response.data.remaining);
        showRegistrationCodeFlash(remaining > 0 ? `${remaining} left` : "Code ready");
      }
    } catch (err) {
      const data = err?.response?.data || {};
      if (Number.isFinite(Number(data.remaining))) {
        setRegistrationCodeRemaining(Number(data.remaining));
      }
      if (Number.isFinite(Number(data.limit))) {
        setRegistrationCodeLimit(Number(data.limit));
      }
      setRegistrationCodeResetAt(data.reset_at || null);
      if (err?.response?.status === 429 || data.code === "registration_code_limit") {
        applyFallbackRegistrationCode();
        if (!auto) showRegistrationCodeFlash("Code ready");
      } else {
        applyFallbackRegistrationCode();
        if (!auto) showRegistrationCodeFlash("Code ready");
      }
    } finally {
      setRegistrationCodeLoading(false);
    }
  };

  useEffect(() => {
    if (!showAuthRedesign || tab !== "register") return;
    if (registrationCode || registrationCodeLoading || registrationCodeAutoFetched) return;
    fetchRegistrationCode({ auto: true });
  }, [showAuthRedesign, tab, registrationCode, registrationCodeLoading, registrationCodeAutoFetched]);

  useEffect(() => {
    return () => {
      if (registrationCodeFlashTimerRef.current) {
        clearTimeout(registrationCodeFlashTimerRef.current);
      }
    };
  }, []);

  // Авто-определение страны по IP и автопроставление country
  useEffect(() => {
    // Только при регистрации и только один раз (пока geoLoaded === false)
    if (tab !== "register" || geoLoaded) return;

    if (nativeApp) {
      const routing = getNativeRoutingSnapshot();
      const detectedRegion = routing.registrationRegion === REGISTRATION_REGION_RU
        ? REGISTRATION_REGION_RU
        : registrationRegion;
      const launchCountry = String(routing.networkCountry || "").trim().toUpperCase() || null;

      setGeoCountry(launchCountry);
      setGeoCity(String(routing.city || "").trim());
      if (!selectedRegistrationCountryRef.current) setRegistrationRegion(detectedRegion);
      if (!selectedRegistrationCountryRef.current && PROFILE_LOCATION_COUNTRY_CODES.has(launchCountry)) {
        setValue("country", launchCountry, { shouldValidate: true });
      }
      setGeoLoaded(true);
      return;
    }

    api.get("/accounts/ui_language/", getPublicAuthRequestConfig())
      .then(res => {
        const ipCountry = String(res.data?.country || "").trim().toUpperCase() || null;
        const ipCity = String(res.data?.city || "").trim();
        const detectedRegion =
          res.data?.registration_region === REGISTRATION_REGION_RU
            ? REGISTRATION_REGION_RU
            : registrationRegion;
        const launchCountry = detectedRegion === REGISTRATION_REGION_RU
          ? RU_REGISTRATION_COUNTRY_CODE
          : ipCountry;

        setGeoCountry(ipCountry);
        setGeoCity(ipCity);
        if (!selectedRegistrationCountryRef.current) setRegistrationRegion(detectedRegion);

        if (!selectedRegistrationCountryRef.current && PROFILE_LOCATION_COUNTRY_CODES.has(launchCountry)) {
          setValue("country", launchCountry, { shouldValidate: true });
        }

        setGeoLoaded(true);
      })
      .catch(() => {
        // Ошибка или неизвестная страна — просто помечаем как загруженное без страны
        setGeoCountry(null);
        setGeoCity("");
        if (!selectedRegistrationCountryRef.current && registrationRegion === REGISTRATION_REGION_RU) {
          setValue("country", RU_REGISTRATION_COUNTRY_CODE, { shouldValidate: true });
        }
        setGeoLoaded(true);
      });
  }, [tab, geoLoaded, nativeApp, registrationRegion, setValue]);

  useEffect(() => {
    if (!nativeApp || typeof window === "undefined") return undefined;

    const handleNativeRoutingChange = (event) => {
      const routing = event?.detail || getNativeRoutingSnapshot();
      const detectedRegion = routing.registrationRegion === REGISTRATION_REGION_RU
        ? REGISTRATION_REGION_RU
        : "EU";
      const launchCountry = String(routing.networkCountry || "").trim().toUpperCase() || null;

      setRegistrationRegion(detectedRegion);
      setGeoCountry(launchCountry);
      setGeoCity(String(routing.city || "").trim());
      setGeoLoaded(true);
      if (tab === "register" && !selectedRegistrationCountryRef.current && PROFILE_LOCATION_COUNTRY_CODES.has(launchCountry)) {
        setValue("country", launchCountry, { shouldValidate: true });
      }
    };

    window.addEventListener("vg:native-routing-changed", handleNativeRoutingChange);
    return () => window.removeEventListener("vg:native-routing-changed", handleNativeRoutingChange);
  }, [nativeApp, setValue, tab]);

  const { options: remoteRegistrationCityOptions, loading: remoteRegistrationCityLoading,
    error: remoteRegistrationCitySearchError } = useRegistrationCities({
    enabled: tab === "register" && registrationStep === "city" && !localRegistrationWalkthroughPreview,
    registrationRegion, geoCountry, geoCity, selectedCountry,
    query: registrationCitySearchActive ? registrationCityQuery : "",
    localizeCity: getRegistrationCityDisplayName,
  });
  const registrationCityOptions = localRegistrationWalkthroughPreview
    ? buildRegistrationPreviewCityOptions(registrationCitySearchActive ? registrationCityQuery : "")
    : remoteRegistrationCityOptions;
  const registrationCityLoading = localRegistrationWalkthroughPreview ? false : remoteRegistrationCityLoading;
  const registrationCitySearchError = localRegistrationWalkthroughPreview ? false : remoteRegistrationCitySearchError;

  const selectRegistrationCityOption = useCallback((option) => {
    const name = String(option?.name || option || "").trim();
    const country = String(option?.country || selectedCountry || "").trim().toUpperCase();
    if (!name || !PROFILE_LOCATION_COUNTRY_CODES.has(country)) return;
    setRegistrationCity(name);
    selectedRegistrationCountryRef.current = country;
    const chosenContour = country === RU_REGISTRATION_COUNTRY_CODE ? REGISTRATION_REGION_RU : "EU";
    selectedCityNeedsLegalReviewRef.current = nativeApp && legalBundle?.contour !== chosenContour;
    setRegistrationMunicipality(option?.municipality_id
      ? { id: option.municipality_id, name, country }
      : null);
    setValue("country", country, { shouldValidate: true });
    setRegistrationRegion(chosenContour);
    setLegalBundle(null);
    if (selectedCityNeedsLegalReviewRef.current) {
      setValue("terms", false, { shouldValidate: false });
      setValue("personal_data_consent", false, { shouldValidate: false });
    }
    if (nativeApp) {
      registrationRoutePromiseRef.current = activateNativeRegistrationRoute(country)
        .then(() => null, (error) => error);
    }
    setRegistrationCityQuery("");
    setRegistrationCitySearchActive(false);
    setRegistrationStepError("");
  }, [nativeApp, selectedCountry, legalBundle?.contour, setValue]);

  useEffect(() => {
    if (!registrationCitySearchActive || registrationStep !== "city") return;
    const timer = setTimeout(() => {
      registrationCitySearchInputRef.current?.focus({ preventScroll: true });
    }, 80);
    return () => clearTimeout(timer);
  }, [registrationCitySearchActive, registrationStep]);

  useEffect(() => {
    if (!registrationPhotoFile) {
      registrationPhotoCropRef.current = {
        imageSize: { w: 0, h: 0 },
        baseScale: 1,
        zoom: 1,
        pos: { x: 0, y: 0 },
      };
      setRegistrationPhotoPreview("");
      setRegistrationPhotoReady(false);
      setRegistrationPhotoImageSize({ w: 0, h: 0 });
      setRegistrationPhotoBaseScale(1);
      setRegistrationPhotoZoom(1);
      setRegistrationPhotoPos({ x: 0, y: 0 });
      setRegistrationPhotoDragging(false);
      registrationPhotoPointersRef.current.clear();
      registrationPhotoPinchRef.current = null;
      return undefined;
    }

    setRegistrationPhotoReady(false);
    setRegistrationPhotoImageSize({ w: 0, h: 0 });
    setRegistrationPhotoBaseScale(1);
    setRegistrationPhotoZoom(1);
    setRegistrationPhotoPos({ x: 0, y: 0 });
    registrationPhotoCropRef.current = {
      imageSize: { w: 0, h: 0 },
      baseScale: 1,
      zoom: 1,
      pos: { x: 0, y: 0 },
    };
    setRegistrationPhotoDragging(false);
    registrationPhotoPointersRef.current.clear();
    registrationPhotoPinchRef.current = null;

    const url = URL.createObjectURL(registrationPhotoFile);
    setRegistrationPhotoPreview(url);

    const image = new Image();
    image.onload = () => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      if (!width || !height) return;
      const coverScale = REGISTRATION_PHOTO_FRAME_SIZE / Math.min(width, height);
      const baseWidth = width * coverScale;
      const baseHeight = height * coverScale;
      const initialPos = {
        x: (REGISTRATION_PHOTO_FRAME_SIZE - baseWidth) / 2,
        y: (REGISTRATION_PHOTO_FRAME_SIZE - baseHeight) / 2,
      };
      registrationPhotoCropRef.current = {
        imageSize: { w: width, h: height },
        baseScale: coverScale,
        zoom: 1,
        pos: initialPos,
      };
      setRegistrationPhotoImageSize({ w: width, h: height });
      setRegistrationPhotoBaseScale(coverScale);
      setRegistrationPhotoZoom(1);
      setRegistrationPhotoPos(initialPos);
      setRegistrationPhotoReady(true);
    };
    image.src = url;

    return () => URL.revokeObjectURL(url);
  }, [registrationPhotoFile]);

  const registrationPhotoDistance = (firstPointer, secondPointer) =>
    Math.hypot(firstPointer.x - secondPointer.x, firstPointer.y - secondPointer.y);

  const clampRegistrationPhotoPosFor = (x, y, nextZoom, cropState = registrationPhotoCropRef.current) => {
    const drawWidth = cropState.imageSize.w * cropState.baseScale * nextZoom;
    const drawHeight = cropState.imageSize.h * cropState.baseScale * nextZoom;
    const minX = Math.min(0, REGISTRATION_PHOTO_FRAME_SIZE - drawWidth);
    const minY = Math.min(0, REGISTRATION_PHOTO_FRAME_SIZE - drawHeight);
    return {
      x: clampRegistrationPhotoValue(x, minX, 0),
      y: clampRegistrationPhotoValue(y, minY, 0),
    };
  };

  const applyRegistrationPhotoZoomAt = (nextZoom, cx, cy) => {
    const safeZoom = clampRegistrationPhotoValue(
      nextZoom,
      REGISTRATION_PHOTO_MIN_SCALE,
      REGISTRATION_PHOTO_MAX_SCALE
    );
    const cropState = registrationPhotoCropRef.current;
    const oldScale = cropState.baseScale * cropState.zoom;
    const nextScale = cropState.baseScale * safeZoom;
    if (!oldScale || !nextScale) {
      registrationPhotoCropRef.current = { ...cropState, zoom: safeZoom };
      setRegistrationPhotoZoom(safeZoom);
      return;
    }
    const imageX = (cx - cropState.pos.x) / oldScale;
    const imageY = (cy - cropState.pos.y) / oldScale;
    const nextX = cx - imageX * nextScale;
    const nextY = cy - imageY * nextScale;
    const nextPos = clampRegistrationPhotoPosFor(nextX, nextY, safeZoom, cropState);
    registrationPhotoCropRef.current = { ...cropState, zoom: safeZoom, pos: nextPos };
    setRegistrationPhotoPos(nextPos);
    setRegistrationPhotoZoom(safeZoom);
  };

  const handleRegistrationPhotoPointerDown = (event) => {
    if (!registrationPhotoPreview || !registrationPhotoReady) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    const target = event.currentTarget;
    try {
      target.setPointerCapture(event.pointerId);
    } catch {
      // Ignore pointer capture issues.
    }
    const rect = target.getBoundingClientRect();
    const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    registrationPhotoPointersRef.current.set(event.pointerId, point);

    if (registrationPhotoPointersRef.current.size === 1) {
      registrationPhotoPinchRef.current = null;
      registrationPhotoDragStartRef.current = { x: event.clientX, y: event.clientY };
      setRegistrationPhotoDragging(true);
    } else if (registrationPhotoPointersRef.current.size === 2) {
      const [firstPointer, secondPointer] = Array.from(registrationPhotoPointersRef.current.values());
      registrationPhotoPinchRef.current = {
        startDist: registrationPhotoDistance(firstPointer, secondPointer),
        startZoom: registrationPhotoCropRef.current.zoom,
        center: {
          x: (firstPointer.x + secondPointer.x) / 2,
          y: (firstPointer.y + secondPointer.y) / 2,
        },
      };
      setRegistrationPhotoDragging(false);
    }
  };

  const handleRegistrationPhotoPointerMove = (event) => {
    if (!registrationPhotoPreview || !registrationPhotoReady) return;
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    if (registrationPhotoPointersRef.current.has(event.pointerId)) {
      registrationPhotoPointersRef.current.set(event.pointerId, point);
    }

    if (registrationPhotoPointersRef.current.size === 1 && registrationPhotoDragging) {
      event.preventDefault();
      const dx = event.clientX - registrationPhotoDragStartRef.current.x;
      const dy = event.clientY - registrationPhotoDragStartRef.current.y;
      registrationPhotoDragStartRef.current = { x: event.clientX, y: event.clientY };
      const cropState = registrationPhotoCropRef.current;
      const nextPos = clampRegistrationPhotoPosFor(
        cropState.pos.x + dx,
        cropState.pos.y + dy,
        cropState.zoom,
        cropState
      );
      registrationPhotoCropRef.current = { ...cropState, pos: nextPos };
      setRegistrationPhotoPos(nextPos);
    } else if (registrationPhotoPointersRef.current.size === 2 && registrationPhotoPinchRef.current) {
      event.preventDefault();
      const [firstPointer, secondPointer] = Array.from(registrationPhotoPointersRef.current.values());
      const nextDistance = registrationPhotoDistance(firstPointer, secondPointer);
      if (registrationPhotoPinchRef.current.startDist > 0) {
        applyRegistrationPhotoZoomAt(
          registrationPhotoPinchRef.current.startZoom *
            (nextDistance / registrationPhotoPinchRef.current.startDist),
          registrationPhotoPinchRef.current.center.x,
          registrationPhotoPinchRef.current.center.y
        );
      }
    }
  };

  const handleRegistrationPhotoPointerEnd = (event) => {
    if (!registrationPhotoPreview || !registrationPhotoReady) return;
    const target = event.currentTarget;
    try {
      target.releasePointerCapture(event.pointerId);
    } catch {
      // Ignore pointer capture issues.
    }
    registrationPhotoPointersRef.current.delete(event.pointerId);
    if (registrationPhotoPointersRef.current.size < 2) {
      registrationPhotoPinchRef.current = null;
    }
    if (registrationPhotoPointersRef.current.size === 0) {
      setRegistrationPhotoDragging(false);
    } else if (registrationPhotoPointersRef.current.size === 1) {
      const [onlyPointer] = Array.from(registrationPhotoPointersRef.current.values());
      const rect = target.getBoundingClientRect();
      registrationPhotoDragStartRef.current = {
        x: onlyPointer.x + rect.left,
        y: onlyPointer.y + rect.top,
      };
      setRegistrationPhotoDragging(true);
    }
  };

  const handleRegistrationPhotoWheel = (event) => {
    if (!registrationPhotoPreview || !registrationPhotoReady) return;
    event.preventDefault();
    const cropState = registrationPhotoCropRef.current;
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = event.clientX - rect.left;
    const centerY = event.clientY - rect.top;
    const nextZoom = Math.exp(-event.deltaY / 400) * cropState.zoom;
    applyRegistrationPhotoZoomAt(nextZoom, centerX, centerY);
  };

  const isCountrySupported = PROFILE_LOCATION_COUNTRY_CODES.has(selectedCountry);
  const isRuRegistrationContext =
    registrationRegion === REGISTRATION_REGION_RU || geoCountry === RU_REGISTRATION_COUNTRY_CODE;
  const isRuAuthContext = registrationRegion === REGISTRATION_REGION_RU;
  const hideCountrySelect =
    nativeApp || !geoLoaded || isRuRegistrationContext || PROFILE_LOCATION_COUNTRY_CODES.has(geoCountry);

  // Keep the detected launch-country value in the registration form.
  useEffect(() => {
    if (tab !== "register" || !geoLoaded) return;
    if (registrationCity) return;
    if (isRuRegistrationContext) {
      setValue("country", RU_REGISTRATION_COUNTRY_CODE, { shouldValidate: true });
    } else if (PROFILE_LOCATION_COUNTRY_CODES.has(geoCountry)) {
      setValue("country", geoCountry, { shouldValidate: true });
    }
  }, [tab, geoLoaded, geoCountry, isRuRegistrationContext, registrationCity, setValue]);

  // инициализация режима google из URL
  useEffect(() => {
    if (modeParam === "google" && gTokenParam) {
      setTab("register");
      setRegisterMode("google");
      setGoogleToken(gTokenParam);
      setAppleToken(null);
    }
  }, [modeParam, gTokenParam]);

  useEffect(() => {
    if (tabParam === "login" || tabParam === "register") {
      setTab(tabParam);
    } else if (authRequiredNoticeRequested) {
      setTab("login");
    }
  }, [authRequiredNoticeRequested, tabParam]);

  useEffect(() => {
    if (tab !== "login" && tab !== "register") return;
    try {
      window.sessionStorage.setItem(AUTH_TAB_STORAGE_KEY, tab);
    } catch {
      // ignore storage failures
    }
  }, [tab]);

  useEffect(() => {
    if (!googleErrorParam) return;
    let nextError = t("errors.google_oauth_failed");
    if (googleErrorParam === "in_app_browser_blocked") {
      nextError = t("errors.google_requires_browser");
    } else if (googleErrorParam === "oauth_cancelled") {
      nextError = t("errors.google_oauth_cancelled");
    } else if (googleErrorParam === "state_invalid" || googleErrorParam === "state_expired") {
      nextError = t("errors.google_oauth_interrupted");
    } else if (googleErrorParam === "account_mismatch") {
      nextError = t("errors.google_account_mismatch");
    } else if (googleErrorParam === "email_deactivated" || googleErrorParam === "email_in_use") {
      nextError = t(
        "errors.email_deactivated",
        "An account using this email was deactivated and cannot be registered again."
      );
    }
    setRegisterMode("classic");
    setGoogleToken(null);
    setAppleToken(null);
    setErrorMsg(nextError);
  }, [googleErrorParam, t]);

  // Load the verified e-mail from the short-lived social registration state.
  useEffect(() => {
    if (!isSocialRegistration || !socialToken || !socialProvider) return;

    api.get(`/accounts/${socialProvider}/pending/${socialToken}/`, getPublicAuthRequestConfig())
      .then(res => {
        const email = res.data?.email;
        if (email) {
          setValue("email", email, { shouldValidate: true });
        }
      })
      .catch(() => {
        setErrorMsg(prev => prev || "Social sign-in registration link is invalid or expired.");
      });
  }, [isSocialRegistration, socialProvider, socialToken, setValue]);

  /** CSRF */
  useEffect(() => {
    api.get("/accounts/csrf/", getPublicAuthRequestConfig()).catch(() => {});
  }, [tab]);

  /** Рефералка (регистрация) */
  useEffect(() => {
    if (tab === "register" && refCode) {
      api.get(`/miniapp/referral-info/${refCode}/`, getPublicAuthRequestConfig())
        .then(res => setRefInfo(res.data))
        .catch(() => setRefInfo(null));
    }
  }, [tab, refCode]);

  useEffect(() => {
    if (tab !== "register") return;

    const trackingKey = [
      registerMode,
      refCode ? "ref" : "noref",
      nextPath || "",
    ].join(":");

    if (trackedSignupViewsRef.current.has(trackingKey)) return;
    trackedSignupViewsRef.current.add(trackingKey);

    trackSignupView({
      register_mode: registerMode,
      referral_present: Boolean(refCode),
      next_path: nextPath || "",
    });
  }, [tab, registerMode, refCode, nextPath]);

  useEffect(() => {
    if (tab !== "forgot") {
      setForgotStep(1);
      setForgotEmail("");
      setForgotCode("");
    }
  }, [tab]);

  const persistAssistantSessionId = (assistantPayload) => {
    const nextSessionId = String(assistantPayload?.session_id || "").trim();
    if (!nextSessionId) return;
    try {
      localStorage.setItem("assistant_session_id", nextSessionId);
    } catch {
      // ignore
    }
    try {
      window.dispatchEvent(
        new CustomEvent("vg-assistant-session-updated", {
          detail: { sessionId: nextSessionId },
        })
      );
    } catch {
      // ignore
    }
  };

  const applyAuthServerFieldErrors = (serverErrors, { shouldFocus = true } = {}) => {
    let firstServerError = "";
    const fields = new Set();
    if (!serverErrors || typeof serverErrors !== "object") {
      return { firstServerError, fields };
    }

    Object.entries(serverErrors).forEach(([field, messages]) => {
      fields.add(field);
      (Array.isArray(messages) ? messages : [messages]).forEach((msg) => {
        const message = String(msg || "");
        if (!firstServerError && message) firstServerError = message;
        setError(field, { type: "server", message, shouldFocus });
      });
    });

    return { firstServerError, fields };
  };

  const checkRegistrationIdentityAvailability = async (values) => {
    await ensureAuthCsrfCookie();
    if (isSocialRegistration) {
      await api.post(
        "/accounts/social/register/availability/",
        {
          provider: socialProvider,
          token: socialToken,
          username: values.username,
          language: language || "en",
          registration_flow_id: registrationFlowId,
          registration_method: socialProvider,
        },
        getPublicAuthRequestConfig()
      );
      return;
    }
    await api.post(
      "/accounts/register/availability/",
      buildClassicRegistrationAvailabilityPayload(values, {
        language: language || "en",
        registrationFlowId,
      }),
      getPublicAuthRequestConfig()
    );
  };

  const requestRegistrationEmailCode = async (values, { resend = false } = {}) => {
    const email = String(values?.email || registerEmailValue || "").trim().toLowerCase();
    if (!email) {
      setRegistrationStepError(t("registration_flow.email.errors.email_required", { defaultValue: "Enter your email." }));
      return false;
    }
    if (resend && registrationEmailResendCooldown > 0) {
      return false;
    }

    setRegistrationEmailSending(true);
    setRegistrationEmailResendStatus("");
    setRegistrationStepError("");

    try {
      await ensureAuthCsrfCookie();
      const response = await api.post(
        "/accounts/register/email-code/request/",
        {
          username: values?.username,
          email,
          language: language || "en",
          registration_flow_id: registrationFlowId,
          registration_method: "classic",
        },
        getPublicAuthRequestConfig()
      );
      const resendAfter = Number(response.data?.resend_after || 45);
      setRegistrationEmailSentTo(email);
      setRegistrationEmailCodeInput("");
      setRegistrationEmailVerificationToken("");
      setRegistrationEmailResendCooldown(Math.max(0, resendAfter));
      if (resend) {
        setRegistrationEmailResendStatus(t("registration_flow.email.code_sent", { defaultValue: "Code sent" }));
      }
      return true;
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "cooldown") {
        const retryAfter = Math.max(1, Number(data.retry_after || 45));
        // The previous request may have sent the message successfully while its
        // response was lost (a common failure mode in in-app browsers). In that
        // case the server still has a valid code and answers the retry with a
        // cooldown. Let the user enter that code instead of trapping them on the
        // registration form.
        setRegistrationEmailSentTo(email);
        setRegistrationEmailCodeInput("");
        setRegistrationEmailVerificationToken("");
        setRegistrationEmailResendCooldown(retryAfter);
        return true;
      }
      if (data?.errors && typeof data.errors === "object") {
        applyAuthServerFieldErrors(data.errors);
        const first = Object.values(data.errors)
          .flat()
          .find(Boolean);
        setRegistrationStepError(first ? String(first) : t("server_error"));
      } else {
        setRegistrationStepError(getAuthServerErrorMessage(err));
      }
      return false;
    } finally {
      setRegistrationEmailSending(false);
    }
  };

  const verifyRegistrationEmailCode = async (email, code) => {
    await ensureAuthCsrfCookie();
    const response = await api.post(
      "/accounts/register/email-code/verify/",
      {
        email,
        code,
        language: language || "en",
        registration_flow_id: registrationFlowId,
        registration_method: "classic",
      },
      getPublicAuthRequestConfig()
    );
    const token = String(response.data?.verification_token || "").trim();
    if (!token) throw new Error("Missing email verification token");
    setRegistrationEmailVerificationToken(token);
    return token;
  };

  const getAuthServerErrorMessage = (err, fallback = t("server_error")) => {
    const data = err?.response?.data;
    if (typeof data === "string" && data.trim()) return data.trim();
    if (data?.error) return String(data.error);
    if (data?.errors) {
      if (typeof data.errors === "string") return data.errors;
      if (typeof data.errors === "object") {
        for (const messages of Object.values(data.errors)) {
          const first = Array.isArray(messages) ? messages[0] : messages;
          if (first) return String(first);
        }
      }
    }
    return fallback;
  };

  const checkRegistrationPhotoWithAi = async () => {
    const adjustedAvatarFile = await createAdjustedRegistrationPhotoFile(
      registrationPhotoFile,
      registrationPhotoCropRef.current
    ).catch(() => registrationPhotoFile);
    const formPayload = new FormData();
    formPayload.append("avatar_file", adjustedAvatarFile || registrationPhotoFile);
    formPayload.append("language", language || "en");
    formPayload.append("registration_flow_id", registrationFlowId);
    formPayload.append("registration_method", isSocialRegistration ? socialProvider : "classic");
    formPayload.append("country", selectedCountry || "");
    formPayload.append("city", registrationCity.trim());
    if (selectedMunicipalityId) formPayload.append("municipality_id", selectedMunicipalityId);

    await ensureAuthCsrfCookie();
    const newcomerAvailabilityRequest = api.get(
      "/events/registration-onboarding/newcomer-event/",
      {
        ...getPublicAuthRequestConfig(),
        params: {
          preflight: "1",
          country: selectedCountry || "",
          city: registrationCity.trim(),
        },
        meta: { suppressGlobalError: true, suppressUnauthorizedRedirect: true },
      }
    ).catch(() => ({ data: { show_first_step: false, event: null } }));

    const [response, newcomerAvailability] = await Promise.all([
      api.post(
        "/accounts/register/photo-check/",
        formPayload,
        {
          ...getPublicAuthRequestConfig(),
          headers: {
            "Content-Type": "multipart/form-data",
            "X-UI-Language": language || "en",
          },
        }
      ),
      newcomerAvailabilityRequest,
    ]);
    storeRegistrationOnboardingPreparation({
      genderAssumption: response.data?.gender_assumption,
      newcomerEventResponse: newcomerAvailability.data,
    });

    return adjustedAvatarFile || registrationPhotoFile;
  };

  const continueLocalRegistrationOnboardingFlow = async () => {
    setErrorMsg("");
    setRegistrationStepError("");

    if (localRegistrationWalkthroughPreview && registrationStep === "form") {
      setRegistrationStep("email");
      return;
    }
    if (localRegistrationWalkthroughPreview && registrationStep === "email") {
      setRegistrationStep("city");
      return;
    }

    if (registrationStep === "city") {
      if (!localRegistrationWalkthroughPreview && !registrationCity.trim()) {
        setRegistrationStepError(t("registration_flow.city.errors.choose_city", { defaultValue: "Choose your city." }));
        return;
      }
      setRegistrationStep("age");
      return;
    }

    if (registrationStep === "age") {
      if (!localRegistrationWalkthroughPreview && !isValidRegistrationAge(registrationAge)) {
        setRegistrationStepError(t("registration_flow.age.error", { defaultValue: "Choose an age from 18 to 60." }));
        return;
      }
      setRegistrationStep("photo");
      return;
    }
    if (registrationStep !== "photo") return;
    if (!localRegistrationWalkthroughPreview && !registrationPhotoFile) {
      setRegistrationStepError(t("registration_flow.photo.errors.required", { defaultValue: "Add your profile photo." }));
      return;
    }

    if (localRegistrationWalkthroughPreview) {
      storeRegistrationOnboardingPreparation({ genderAssumption: "unknown", newcomerEventResponse: { show_first_step: false, event: null } });
      navigate("/local-debug/registration-onboarding/cards", { state: { fromRegistrationFlowPreview: true } });
      return;
    }

    setIsSubmitting(true);
    setRegistrationPhotoChecking(true);
    const newcomerAvailabilityRequest = api.get(
      "/events/registration-onboarding/newcomer-event/",
      {
        ...getPublicAuthRequestConfig(),
        params: {
          preflight: "1",
          preview: "1",
          country: selectedCountry || LOCAL_MOBILE_SIGNUP_PREVIEW_COUNTRY,
          city: registrationCity.trim(),
        },
        meta: { suppressGlobalError: true, suppressUnauthorizedRedirect: true },
      }
    ).catch(() => ({ data: { show_first_step: false, event: null } }));

    const [newcomerAvailability] = await Promise.all([
      newcomerAvailabilityRequest,
      wait(550),
    ]);
    storeRegistrationOnboardingPreparation({
      genderAssumption: params.get("gender") || "unknown",
      newcomerEventResponse: newcomerAvailability.data,
    });

    const commitNavigation = () => navigate(
      "/local-debug/registration-onboarding/cards",
      {
        state: { fromRegistrationFlowPreview: true },
        flushSync: true,
      }
    );
    await commitRegistrationRouteTransition(commitNavigation);
  };

	  const onSubmit = async (values) => {
	    if (isSubmitting) return;
	    setErrorMsg("");
	    setRegistrationStepError("");
	    if (tab === "register" && registrationStep !== "city" && !legalBundle?.bundle_id) {
	      setErrorMsg(legalBundleError || t("legal_bundle_load_error"));
	      return;
	    }

	    const usesAuthRegistrationWizard = showAuthRedesign && tab === "register";
	    const shouldPrepareRegistrationOnboarding =
	      usesAuthRegistrationWizard && registrationStep === "photo";
	    if (usesAuthRegistrationWizard) {
	      if (registrationStep === "city" && nativeApp) {
	        if (!registrationCity.trim()) {
	          setRegistrationStepError(t("registration_flow.city.errors.choose_city", { defaultValue: "Choose your city." }));
	          return;
	        }
	        if (registerMode === "classic" && !registrationEmailVerificationToken) {
	          setRegistrationStep("email");
	          setRegistrationStepError(t("registration_flow.email.errors.confirm_first", { defaultValue: "Confirm your email first." }));
	          return;
	        }
	        setIsSubmitting(true);
	        const routeError = await registrationRoutePromiseRef.current;
	        setIsSubmitting(false);
	        if (routeError) {
	          setRegistrationStepError(t("server_error", { defaultValue: "Connection unavailable. Try again." }));
	          return;
	        }
	        setRegistrationStep(selectedCityNeedsLegalReviewRef.current ? "form" : "age");
	        return;
	      }
      if (registrationStep === "form") {
        setIsSubmitting(true);
        try {
          if (nativeApp && registrationCity.trim()) {
            const routeError = await registrationRoutePromiseRef.current;
            if (routeError) throw routeError;
          }
          if (!localMobileSignupPreviewEnabled) {
            await checkRegistrationIdentityAvailability(values);
          }
          if (registrationCity.trim() && (isSocialRegistration || registrationEmailVerificationToken)) {
            setRegistrationStep("age");
            selectedCityNeedsLegalReviewRef.current = false;
            return;
          }
          if (isSocialRegistration) {
            setRegistrationStep("city");
          } else {
            const codeSent = await requestRegistrationEmailCode(values);
            if (!codeSent) return;
            setRegistrationStep("email");
          }
          selectedCityNeedsLegalReviewRef.current = false;
	        } catch (err) {
	          const data = err.response?.data;
	          let general = "";
	          if (data?.errors && typeof data.errors === "object") {
	            applyAuthServerFieldErrors(normalizeRegistrationAvailabilityErrors(data.errors));
	          } else if (data?.errors) {
	            general = data.errors;
	          } else if (data?.error) {
	            general = data.error;
	          } else {
	            general = t("server_error");
	          }
	          setErrorMsg(general);
	          return;
	        } finally {
	          setIsSubmitting(false);
	        }
	        return;
	      }
	      if (registrationStep === "email") {
	        const normalizedCode = registrationEmailCodeInput.replace(/\D/g, "");
	        const currentEmail = String(values.email || "").trim().toLowerCase();
	        if (currentEmail !== registrationEmailSentTo) {
	          await requestRegistrationEmailCode(values);
	          return;
	        }
	        if (!/^\d{6}$/.test(normalizedCode)) {
	          setRegistrationStepError(t("registration_flow.email.errors.six_digit_code", { defaultValue: "Enter the 6-digit code." }));
	          return;
	        }
	        setIsSubmitting(true);
	        try {
	          await verifyRegistrationEmailCode(currentEmail, normalizedCode);
	        } catch (err) {
	          setRegistrationStepError(getAuthServerErrorMessage(
              err,
              t("registration_flow.email.errors.invalid_or_expired", { defaultValue: "Invalid or expired confirmation code." })
            ));
	          return;
	        } finally {
	          setIsSubmitting(false);
	        }
	        setRegistrationStepError("");
        setRegistrationStep("city");
	        return;
	      }
	      if (registrationStep === "city") {
	        const city = registrationCity.trim();
	        if (!city) {
	          setRegistrationStepError(t("registration_flow.city.errors.choose_city", { defaultValue: "Choose your city." }));
	          return;
	        }
	        if (registerMode === "classic" && !registrationEmailVerificationToken) {
	          setRegistrationStep("email");
	          setRegistrationStepError(t("registration_flow.email.errors.confirm_first", { defaultValue: "Confirm your email first." }));
	          return;
	        }
	        setRegistrationStep("age");
	        return;
	      }
	      if (registrationStep === "age") {
	        if (!isValidRegistrationAge(registrationAge)) {
	          setRegistrationStepError(t("registration_flow.age.error", { defaultValue: "Choose an age from 18 to 60." }));
	          return;
	        }
	        setRegistrationStep("photo");
	        return;
	      }
	      if (registrationStep === "photo" && !registrationPhotoFile) {
	        setRegistrationStepError(t("registration_flow.photo.errors.required", { defaultValue: "Add your profile photo." }));
	        return;
	      }
	    }

	    let verifiedRegistrationAvatarFile = null;
	    if (shouldPrepareRegistrationOnboarding) {
	      setIsSubmitting(true);
	      setRegistrationPhotoChecking(true);
	      try {
	        verifiedRegistrationAvatarFile = await checkRegistrationPhotoWithAi();
	      } catch (err) {
	        setErrorMsg("");
	        setRegistrationStepError(getAuthServerErrorMessage(
            err,
            t("registration_flow.photo.errors.try_another", { defaultValue: "Please upload another profile photo." })
          ));
	        return;
	      } finally {
	        setRegistrationPhotoChecking(false);
	        setIsSubmitting(false);
	      }
	    }
	    setIsSubmitting(true);

	    let payload = values;
	    let isMultipartRegisterPayload = false;
	    if (tab === "register") {
      const { terms, personal_data_consent, email_repeat, ...rest } = values;
	      payload = {
	        ...rest,
	        legal_accepted: Boolean(terms),
	        personal_data_consent_accepted:
	          isRussianLegalBundle && Boolean(personal_data_consent),
	        legal_bundle_id: legalBundle?.bundle_id,
	        legal_source_path: "/auth?tab=register",
	        preferred_code: registrationCode || undefined,
        age_reported: registrationAge,
        referral_code: refCode,
        visitor_id: readAdminRefVisitorId() || undefined,
        assistant_session_id: readAssistantSessionId() || undefined,
        language: language || "en",
        email_verification_token: registrationEmailVerificationToken || undefined,
	        registration_flow_id: registrationFlowId,
	        registration_method: isSocialRegistration ? socialProvider : "classic",
	        avatar: avatarIdx != null ? avatars[avatarIdx]?.id : undefined,
	        country: values.country,
	        ...(selectedMunicipalityId ? { municipality_id: selectedMunicipalityId } : {}),
	        event_discovery_radius_km: DEFAULT_EVENT_DISCOVERY_RADIUS_KM,
	      };
	      if (nativeApp) payload.native = "1";
	      if (isSocialRegistration) {
	        payload.token = socialToken;
	      }
		      if (usesAuthRegistrationWizard) {
		        payload.city = registrationCity.trim();
		        payload.require_city = "1";
		        payload.require_custom_avatar = "1";
		        const adjustedAvatarFile = verifiedRegistrationAvatarFile || await createAdjustedRegistrationPhotoFile(
		          registrationPhotoFile,
		          registrationPhotoCropRef.current
		        ).catch(() => registrationPhotoFile);
		        const formPayload = new FormData();
		        Object.entries(payload).forEach(([key, value]) => {
		          if (value !== undefined && value !== null && value !== "") {
		            formPayload.append(key, value);
		          }
		        });
		        formPayload.append("avatar_file", adjustedAvatarFile || registrationPhotoFile);
		        payload = formPayload;
		        isMultipartRegisterPayload = true;
		      }
	    }
	    const authSubmitConfig = {
      meta: {
        suppressGlobalError: true,
      },
    };

	    try {
	      if (!nativeApp) await ensureAuthCsrfCookie();

      let authResponse = null;
      if (tab === "login") {
        authResponse = nativeApp
          ? { data: await loginNative(values) }
          : await api.post("/accounts/login/", values, authSubmitConfig);
      } else if (tab === "register") {
        trackSignupStart({
          register_mode: registerMode,
          referral_present: Boolean(refCode),
          country: values.country || "",
          language: language || "en",
          next_path: nextPath || "",
        });

	        if (isSocialRegistration) {
	          authResponse = await api.post(
	            `/accounts/${socialProvider}/register/`,
	            payload,
	            isMultipartRegisterPayload
	              ? {
	                  ...authSubmitConfig,
	                  headers: {
	                    "Content-Type": "multipart/form-data",
	                    "X-UI-Language": language || "en",
	                  },
	                }
	              : authSubmitConfig
	          );
	          if (nativeApp) await setNativeAuthTokens(authResponse?.data, `${socialProvider}_registration`);
	        } else {
	          authResponse = await api.post(
	            "/accounts/register/",
	            payload,
	            isMultipartRegisterPayload
	              ? {
	                  ...authSubmitConfig,
	                  headers: {
	                    "Content-Type": "multipart/form-data",
	                    "X-UI-Language": language || "en",
	                  },
	                }
	              : authSubmitConfig
	          );
	          if (nativeApp) {
	            await loginNative({
	              username: values.username || values.email,
	              password: values.password1,
	            });
	          }
	        }
      } else if (tab === "forgot") {
        if (forgotStep === 1) {
          await api.post("/accounts/password-reset/request/", { email: values.email }, authSubmitConfig);
          setForgotEmail(values.email);
          setForgotCode("");
          setForgotStep(2);
          reset({ email: values.email, code: "" });
          setIsSubmitting(false);
          return;
        }
        if (forgotStep === 2) {
          setForgotEmail(values.email);
          setForgotCode(values.code);
          setForgotStep(3);
          reset({
            email: values.email,
            code: values.code,
            new_password1: "",
            new_password2: "",
          });
          setIsSubmitting(false);
          return;
        }

        await api.post(
          "/accounts/password-reset/confirm/",
          {
            email: values.email,
            code: values.code,
            new_password1: values.new_password1,
            new_password2: values.new_password2,
          },
          authSubmitConfig
        );
        if (nativeApp) {
          await loginNative({
            username: values.email,
            password: values.new_password1,
          });
        }
        const profile = await api.get("/accounts/profile/");

        const profileCountry = profile.data?.country || null;
        if (profileCountry) {
          try {
            setCountry(profileCountry);
          } catch (e) {
            console.error("Failed to set locale country from profile (forgot step)", e);
          }
        }
        const profileLanguage = profile.data?.language || null;
        if (profileLanguage) {
          try {
            setLanguage(profileLanguage, { persist: true, source: "profile" });
          } catch (e) {
            console.error("Failed to set locale language from profile (forgot step)", e);
          }
        }

        const redirectTo = getPostAuthRedirectPath({ nextPath });
        navigate(redirectTo, { replace: true });
        onAuth(profile.data);
        return;
      }
      persistAssistantSessionId(authResponse?.data?.assistant);
      const profile = await api.get("/accounts/profile/");

      const profileCountry = profile.data?.country || null;
      if (profileCountry) {
        try {
          setCountry(profileCountry);
        } catch (e) {
          console.error("Failed to set locale country from profile", e);
        }
      }
      const profileLanguage = profile.data?.language || null;
      if (profileLanguage) {
        try {
          setLanguage(profileLanguage, { persist: true, source: "profile" });
        } catch (e) {
          console.error("Failed to set locale language from profile", e);
        }
      }

	      if (tab === "register") {
	        writeEventLocationFilter(
	          profileCountry === "RU" ? "RU" : "EU",
	          profile.data?.city || registrationCity,
	          DEFAULT_EVENT_DISCOVERY_RADIUS_KM
	        );
	        clearRegistrationFlowId();
	        trackMetaCompleteRegistration(profile.data?.id);
	        trackSignupSuccess({
          register_mode: registerMode,
          referral_present: Boolean(refCode),
          country: profileCountry || values.country || "",
          language: profileLanguage || language || "en",
          next_path: nextPath || "",
          registered_user_id: profile.data?.id ? String(profile.data.id) : "",
        });
      }

	      const redirectTo = getPostAuthRedirectPath({
	        isRegistration: tab === "register",
	        nextPath,
	      });
	      const commitAuthNavigation = () => {
	        onAuth(profile.data);
	        navigate(redirectTo, { replace: true, flushSync: true });
	      };
	      if (tab === "register") {
	        await commitRegistrationRouteTransition(commitAuthNavigation);
	      } else {
	        commitAuthNavigation();
	      }
	    } catch (err) {
	      const data = err.response?.data;
      let general = "";
	      if (data?.code === "code_taken" || data?.code === "code_unavailable") {
	        removeRegistrationCodeFromHistory(registrationCode);
	        applyFallbackRegistrationCode([registrationCode]);
	        setRegistrationCodeAutoFetched(true);
	        setRegistrationStep("form");
	        setErrorMsg("");
	        return;
	      }
      const responseText = typeof data === "string" ? data : "";
      const isCsrfHtmlError =
        err.response?.status === 403 &&
        /csrf/i.test(responseText);
	      if (data?.errors) {
	        if (typeof data.errors === "object") {
	          const { firstServerError, fields } = applyAuthServerFieldErrors(data.errors);
	          const hasRegistrationIdentityError = Array.from(REGISTRATION_IDENTITY_FIELDS)
	            .some((field) => fields.has(field));
	          if (
	            hasRegistrationIdentityError &&
	            tab === "register" &&
	            usesAuthRegistrationWizard &&
	            registrationStep !== "form"
	          ) {
	            setRegistrationStep("form");
	            setRegistrationStepError("");
	            setErrorMsg("");
	            return;
	          }
	          if (firstServerError && tab === "register" && registrationStep !== "form") {
	            setRegistrationStepError(firstServerError);
	          }
	        } else {
	          general = data.errors;
	        }
      } else if (data?.error) {
        general = data.error;
        if (tab === "register" && usesAuthRegistrationWizard && registrationStep === "photo") {
          setRegistrationStepError(general);
          setErrorMsg("");
          return;
        }
      } else if (isCsrfHtmlError) {
        general = "Session check failed. Please try again.";
      } else {
        general = t("server_error");
      }
      setErrorMsg(general);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Google OAuth must be started with a real browser navigation (NOT axios),
  // otherwise the redirect to Google stays inside XHR and ничего не происходит визуально.
  const getBackendBase = () => {
    const baseURL = api?.defaults?.baseURL || "";
    if (!baseURL) return "";
    return baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
  };

  const handleGoogleClick = async () => {
    if (isRuAuthContext) return;
    if (!nativeApp && (isInstagramIAB || isTelegram)) {
      setErrorMsg(t("errors.google_requires_browser"));
      return;
    }

    if (nativeApp && (isNativeIOS() || isNativeAndroid())) {
      if (isSubmitting) return;
      setErrorMsg("");
      setIsSubmitting(true);

      try {
        const { authorizeNativeGoogleSignIn } = await import("../../native/googleSignIn");
        let nativeGoogleOptions = {};
        if (isNativeAndroid()) {
          const configResponse = await api.get(
            "/accounts/native/google/config/",
            getPublicAuthRequestConfig()
          );
          const serverClientId = String(configResponse?.data?.server_client_id || "").trim();
          if (!serverClientId) {
            throw new Error("Google server client ID is missing.");
          }
          nativeGoogleOptions = { serverClientId };
        }
        const credential = await authorizeNativeGoogleSignIn(nativeGoogleOptions);
        if (!credential.identityToken) {
          throw new Error("Google did not return an identity token.");
        }

        const response = await api.post(
          "/accounts/native/google/authorize/",
          {
            identity_token: credential.identityToken,
            platform: isNativeAndroid() ? "android" : "ios",
            nonce: credential.nonce || "",
            language: language || "en",
          },
          getPublicAuthRequestConfig()
        );
        const result = response?.data || {};

        if (result.requires_registration) {
          const registrationToken = String(result.registration_token || "").trim();
          if (!registrationToken) {
            throw new Error("Google registration token is missing.");
          }

          const verifiedEmail = String(result.email || "").trim().toLowerCase();
          resetMobileRegistrationFlow({ keepCity: nativeApp && Boolean(registrationCity) });
          setRegisterMode("google");
          setGoogleToken(registrationToken);
          setAppleToken(null);
          setTab("register");
          reset({
            username: "",
            email: verifiedEmail,
            email_repeat: verifiedEmail,
            password1: "",
            password2: "",
            country: selectedCountry || geoCountry || "",
            terms: false,
            personal_data_consent: false,
          });
          setAvatarIdx(null);
          setErrorMsg("");
          return;
        }

        await setNativeAuthTokens(result, "google_login");
        const profileResponse = await api.get("/accounts/profile/");
        const profile = profileResponse?.data || {};
        if (profile.country) setCountry(profile.country);
        if (profile.language) {
          setLanguage(profile.language, { persist: true, source: "profile" });
        }
        navigate(nextPath && nextPath.startsWith("/") ? nextPath : "/events/search", { replace: true });
        onAuth(profile);
      } catch (error) {
        if (isNativeGoogleSignInCancelled(error)) {
          setErrorMsg(t("errors.google_oauth_cancelled"));
        } else if (
          error?.response?.data?.code === "email_deactivated" ||
          error?.response?.data?.error === "account_unavailable"
        ) {
          setErrorMsg(
            error.response.data?.error ||
              t(
                "errors.email_deactivated",
                "An account using this email was deactivated and cannot be registered again."
              )
          );
        } else {
          setErrorMsg(t("errors.google_oauth_failed"));
        }
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setErrorMsg("");
    const basePath = "/accounts/google/login/";
    const p = new URLSearchParams();

    if (nextPath && nextPath.startsWith("/")) {
      p.set("next", nextPath);
    }
    if (language) {
      p.set("lang", language);
    }

    const relativeUrl = p.toString() ? `${basePath}?${p.toString()}` : basePath;
    const backend = getBackendBase();

    // If axios has baseURL (e.g., http://localhost:8000), use it for a full navigation target.
    // Otherwise fallback to relative path (works when frontend and backend share origin).
    const targetUrl = nativeApp
      ? `${getNativeApiBaseUrl()}${relativeUrl}${relativeUrl.includes("?") ? "&" : "?"}native=1`
      : (backend ? `${backend}${relativeUrl}` : relativeUrl);

    if (nativeApp) {
      openExternalUrl(targetUrl).catch(() => {
        setErrorMsg(t("errors.google_oauth_failed"));
      });
      return;
    }
    window.location.href = targetUrl;
  };

  const handleAppleClick = async () => {
    if (isRuAuthContext || !nativeAppleSignInEnabled || isSubmitting) return;
    setErrorMsg("");
    setIsSubmitting(true);

    try {
      const rawNonce = createNativeAppleNonce();
      const nonce = await sha256Hex(rawNonce);
      const { authorizeNativeAppleSignIn } = await import("../../native/appleSignIn");
      const credential = await authorizeNativeAppleSignIn({ nonce });
      if (!credential.identityToken) throw new Error("Apple did not return an identity token.");

      const response = await api.post(
        "/accounts/native/apple/authorize/",
        {
          identity_token: credential.identityToken,
          nonce: rawNonce,
          apple_user: credential.user || "",
          given_name: credential.givenName || "",
          family_name: credential.familyName || "",
          language: language || "en",
        },
        getPublicAuthRequestConfig()
      );

      const result = response?.data || {};
      if (result.requires_registration) {
        const registrationToken = String(result.registration_token || "").trim();
        if (!registrationToken) {
          throw new Error("Apple registration token is missing.");
        }

        const verifiedEmail = String(result.email || "").trim().toLowerCase();
        resetMobileRegistrationFlow({ keepCity: nativeApp && Boolean(registrationCity) });
        setRegisterMode("apple");
        setAppleToken(registrationToken);
        setGoogleToken(null);
        setTab("register");
        reset({
          username: "",
          email: verifiedEmail,
          email_repeat: verifiedEmail,
          password1: "",
          password2: "",
          country: selectedCountry || geoCountry || "",
          terms: false,
          personal_data_consent: false,
        });
        setAvatarIdx(null);
        setErrorMsg("");
        return;
      }

      await setNativeAuthTokens(result, "apple_login");
      const profileResponse = await api.get("/accounts/profile/");
      const profile = profileResponse?.data || {};
      if (profile.country) setCountry(profile.country);
      if (profile.language) {
        setLanguage(profile.language, { persist: true, source: "profile" });
      }
      navigate(nextPath && nextPath.startsWith("/") ? nextPath : "/events/search", { replace: true });
      onAuth(profile);
    } catch (error) {
      if (isNativeAppleSignInCancelled(error)) {
        setErrorMsg(t("errors.apple_oauth_cancelled", "Apple sign-in was cancelled."));
      } else if (
        error?.response?.data?.code === "email_deactivated" ||
        error?.response?.data?.error === "account_unavailable"
      ) {
        setErrorMsg(
          error.response.data?.error ||
            t(
              "errors.email_deactivated",
              "An account using this email was deactivated and cannot be registered again."
            )
        );
      } else {
        setErrorMsg(t("errors.apple_oauth_failed", "Apple sign-in failed. Please try again."));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTabs = () => (
    <div
      className={clsx(
        "flex justify-center gap-8",
        isDesktop
          ? "mb-8" // десктоп — как было
          : (isSmallPhone ? "mb-2" : "mb-6") // маленький телефон vs крупный телефон
      )}
    >
      <button
	        onClick={() => {
	          setTab("register");
	          resetMobileRegistrationFlow();
	          reset();
	          setErrorMsg("");
	          setAvatarIdx(null);
	          setRegisterMode(modeParam === "google" && gTokenParam ? "google" : "classic");
	          setGoogleToken(modeParam === "google" && gTokenParam ? gTokenParam : null);
	          setAppleToken(null);
	        }}
        className={`w-32 h-9 rounded-full flex items-center justify-center text-base font-extrabold ${
          tab === "register" ? "bg-blue-600 text-white" : "border border-white/50 text-white opacity-50"
        }`}
      >
        {t("tabs.register")}
      </button>
      <button
        onClick={() => {
          setTab("login");
          reset();
          setErrorMsg("");
          setAvatarIdx(null);
          setRegisterMode("classic");
          setGoogleToken(null);
          setAppleToken(null);
        }}
        className={`w-32 h-9 rounded-full flex items-center justify-center text-base font-extrabold ${
          tab === "login" ? "bg-blue-600 text-white" : "border border-white/50 text-white opacity-50"
        }`}
      >
        {t("tabs.login")}
      </button>
    </div>
  );

  const renderField = (
    name,
    type = "text",
    placeholderKey = "",
    maxLength,
    readOnly = false,
    options = {}
  ) => {
    const {
      wrapperClassName = "mb-4",
      wrapperStyle,
      inputClassName = "w-full h-12 bg-zinc-300 bg-opacity-25 rounded-2xl px-4 placeholder-white/50 text-white pr-10",
      errorClassName = "",
      placeholder = t(placeholderKey),
      inputMode,
      enterKeyHint,
      onKeyDown,
      showError = true,
    } = options;
    const forceLower =
      name === "username" ||
      (tab === "register" && (name === "email" || name === "email_repeat")) ||
      (tab === "forgot" && name === "email");
    const isPassword = type === "password";
    const registerProps = register(name);
    let autoComp = "off";
    if (tab === "login") {
      if (name === "username") autoComp = "username";
      if (name === "password") autoComp = "current-password";
    } else if (tab === "register") {
      // Do not let browsers/password managers copy an e-mail identity into the
      // new account's username. Login keeps autocomplete="username" above.
      if (name === "username") autoComp = "off";
      if (name === "email") autoComp = "email";
      if (name === "password1") autoComp = "new-password";
      if (name === "password2") autoComp = "new-password";
    } else if (tab === "forgot") {
      if (name === "email") autoComp = "email";
      if (name.startsWith("new_password")) autoComp = "new-password";
    }
    const handleFieldChange = (e) => {
      if (forceLower) {
        let v = e.target.value;

        // Login field may contain either username OR email.
        // For login we must allow '@' (and common email chars) so people can type emails.
        if (name === "username") {
          if (tab === "login") {
            // keep as-is except: lowercase + remove spaces
            v = v.toLowerCase().replace(/\s+/g, "");
            // optional light sanitize for obvious bad chars, but allow email/username chars
            v = v.replace(/[^a-z0-9_@.+-]/g, "");
          } else {
            // Register username: only latin letters, digits and underscore; force lowercase
            v = v.toLowerCase().replace(/[^a-z0-9_]/g, "");
          }
        }

        // Email fields (register tab only): force lowercase
        if (
          (tab === "register" && (name === "email" || name === "email_repeat")) ||
          (tab === "forgot" && name === "email")
        ) {
          v = v.toLowerCase();
        }

        if (e.target.value !== v) e.target.value = v;
      }

      registerProps.onChange(e);

      if (tab === "login" && (name === "username" || name === "password")) {
        if (errors.username || errors.password) {
          clearErrors(["username", "password"]);
        }
        if (errorMsg) {
          setErrorMsg("");
        }
      }

      if (
        tab === "forgot" &&
        ["email", "code", "new_password1", "new_password2"].includes(name)
      ) {
        if (errors[name]) {
          clearErrors(name);
        }
        if (errorMsg) {
          setErrorMsg("");
        }
      }
    };
    return (
      <div className={wrapperClassName} style={wrapperStyle}>
        <div className="relative">
          <input
            onFocus={(e) => ensureFieldVisible(e.currentTarget)}
            {...registerProps}
            type={isPassword && pwVisible[name] ? "text" : type}
            placeholder={placeholder}
            maxLength={maxLength}
            readOnly={readOnly}
            inputMode={inputMode}
            enterKeyHint={enterKeyHint}
            onKeyDown={onKeyDown}
            onChange={handleFieldChange}
            className={inputClassName}
            autoComplete={autoComp}
            data-auth-field={name}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setPwVisible(p => ({ ...p, [name]: !p[name] }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 opacity-60"
              aria-label={pwVisible[name] ? t("aria.hide_password") : t("aria.show_password")}
            >
              <img src={pwVisible[name] ? "/assets/menu/eye_off.svg" : "/assets/menu/eye.svg"} alt="" className="w-5 h-5 pointer-events-none" />
            </button>
          )}
        </div>
        {showError && errors[name] && (
          <div className={errorClassName} style={{ color: "red", fontSize: "0.8rem" }}>
            {errors[name].message}
          </div>
        )}
      </div>
    );
  };

  const handleStableAuthFieldKeyDown = (event) => {
    if (event.key !== "Enter") return;

    const fields = Array.from(
      contentRef.current?.querySelectorAll?.("[data-auth-field]") || []
    ).filter((field) => !field.disabled && !field.readOnly && field.offsetParent !== null);
    const currentIndex = fields.indexOf(event.currentTarget);
    const nextField = currentIndex >= 0 ? fields[currentIndex + 1] : null;

    event.preventDefault();
    if (nextField) {
      nextField.focus({ preventScroll: true });
      ensureFieldVisible(nextField);
      return;
    }
    event.currentTarget.blur();
  };

  const handleForgotBack = () => {
    if (forgotStep === 1) {
      setTab("login");
      reset();
      setErrorMsg("");
      return;
    }
    if (forgotStep === 2) {
      setForgotStep(1);
      setForgotCode("");
      reset({ email: forgotEmail || "" });
      setErrorMsg("");
      return;
    }
    setForgotStep(2);
    reset({ email: forgotEmail || "", code: forgotCode || "" });
    setErrorMsg("");
  };

  const getMobileActionLayout = (availableH, scale) => {
    const designVisibleH = availableH / Math.max(scale, 0.01);
    const actionButtonGap = 10;
    const bottomSafeGap = 48;
    const createAccountTop = Math.min(
      AUTH_DESIGN_HEIGHT - 55 - bottomSafeGap,
      designVisibleH - 55 - bottomSafeGap
    );
    const appleTop = nativeAppleSignInEnabled && !isRuAuthContext
      ? createAccountTop - 55 - actionButtonGap
      : null;
    const googleTop = (appleTop ?? createAccountTop) - 55 - actionButtonGap;
    return { actionButtonGap, createAccountTop, googleTop, appleTop };
  };

  const mobileAuthTransition = {
    type: "spring",
    stiffness: 360,
    damping: 34,
    mass: 0.8,
  };

  const mobileAuthStageSurfaceStyle = useNativeIosSafeStage
    ? {
        backgroundColor: "transparent",
        backgroundImage: "none",
      }
    : AUTH_GRADIENT_STYLE;

  const getAuthStageAvailableSize = () => ({
    width: authStageSize.width || viewportW || AUTH_DESIGN_WIDTH,
    height: authStageSize.height || viewportH || AUTH_DESIGN_HEIGHT,
  });

  const getMobileAuthScale = (availableW, availableH) => {
    const widthScale = availableW / AUTH_DESIGN_WIDTH;
    const fitScale = Math.min(widthScale, availableH / AUTH_DESIGN_HEIGHT);
    return isDesktop ? fitScale : widthScale;
  };
  const mobileKeyboardActive = nativeApp
    ? nativeKeyboardState.listenersReady
      ? nativeKeyboardState.isOpen
      : isInputFocused
    : isKbOpen || isInputFocused;
  const nativeAuthVisibleHeight = useNativeIosSafeStage
    ? getNativeAuthViewportHeight({
        stageHeight: authStageSize.height,
        keyboardHeight: nativeKeyboardState.height,
        keyboardBottomInset: authStageSize.bottomInset,
        keyboardOpen: nativeKeyboardState.isOpen,
      })
    : 0;
  useEffect(() => {
    if (!useStableNativeAuth || nativeKeyboardState.isOpen) return undefined;
    const frame = window.requestAnimationFrame(() => {
      contentRef.current?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [forgotStep, registrationStep, tab, nativeKeyboardState.isOpen, useStableNativeAuth]);
  const mobileKeyboardShiftY = getMobileAuthKeyboardShiftY({
    isDesktop,
    isKeyboardOpen: mobileKeyboardActive,
    isInputFocused,
  });

  const stableAuthFieldClassName =
    "h-[55px] w-full rounded-full border border-white/25 bg-white/10 px-[20px] pr-12 text-[16px] font-medium leading-[20px] text-white placeholder:text-white/50 outline-none backdrop-blur-[18px]";
  const stableAuthSurfaceClassName = useNativeIosSafeStage ? "bg-transparent" : "bg-[#050812]";
  const stableAuthSurfaceStyle = useNativeIosSafeStage
    ? mobileAuthStageSurfaceStyle
    : AUTH_GRADIENT_STYLE;
  const stableAuthBottomPaddingClassName = useNativeIosSafeStage
    ? "pb-[24px]"
    : "pb-[max(24px,env(safe-area-inset-bottom,0px))]";

  const renderStableMobileWebLoginView = () => {
    const welcomeCopy = authRequiredNoticeActive
      ? t("messages.account_required_notice")
      : WELCOME_ROTATION_MESSAGES[welcomeIndex];

    return (
      <div
        className={clsx(
          "relative h-full min-h-[523px] w-full overflow-visible text-white",
          stableAuthSurfaceClassName
        )}
        style={{
          ...stableAuthSurfaceStyle,
          // QuickType changes must not reflow the login flex layout, including
          // when Password is the first field opened in this keyboard session.
          height: useNativeIosSafeStage && nativeKeyboardState.isOpen ? 523 : undefined,
        }}
        data-testid="stable-mobile-web-login"
      >
        <AuthLanguageToggle />

        <div className={clsx(
          "mx-auto flex h-full min-h-[523px] w-full max-w-[402px] flex-col px-[30px] pt-[78px]",
          stableAuthBottomPaddingClassName
        )}>
          <div
            className={clsx(
              "mx-auto flex min-h-[64px] max-w-[320px] flex-1 items-center justify-center text-center font-semibold",
              authRequiredNoticeActive
                ? "text-[22px] leading-[27px]"
                : "text-[38px] leading-[42px]"
            )}
          >
            {welcomeCopy}
          </div>

          <div className="flex shrink-0 flex-col">
            {errorMsg && (
              <div className="mb-[10px] text-center text-[12px] font-medium leading-[15px] text-red-300">
                {errorMsg}
              </div>
            )}

            {renderField("username", "text", "", undefined, false, {
              wrapperClassName: "w-full",
              placeholder: t("placeholders.username"),
              inputClassName: stableAuthFieldClassName,
              enterKeyHint: "next",
              onKeyDown: handleStableAuthFieldKeyDown,
              showError: false,
            })}
            {errors.username && (
              <div className="mt-[4px] px-[20px] text-[12px] font-medium leading-[15px] text-red-400">
                {errors.username.message}
              </div>
            )}

            <div className={errors.username ? "mt-[8px]" : "mt-[10px]"}>
              {renderField("password", "password", "placeholders.password", undefined, false, {
                wrapperClassName: "w-full",
                inputClassName: stableAuthFieldClassName,
                enterKeyHint: "done",
                onKeyDown: handleStableAuthFieldKeyDown,
                showError: false,
              })}
            </div>
            {errors.password && (
              <div className="mt-[4px] px-[20px] text-[12px] font-medium leading-[15px] text-red-400">
                {errors.password.message}
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                setTab("forgot");
                setForgotStep(1);
                setForgotEmail("");
                setForgotCode("");
                reset();
                setErrorMsg("");
              }}
              className="mx-auto mt-[12px] whitespace-nowrap text-center text-[15px] font-normal leading-[22px] text-white/50 underline underline-offset-2"
            >
              {t("buttons.forgot_password")}
            </button>

          </div>

          <div className="mt-[18px] grid shrink-0 gap-[10px]">
            <motion.button
              type="submit"
              data-auth-keyboard-context-end="login"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              whileTap={isSubmitting ? undefined : PRESS_TAP}
              transition={PRESS_TRANSITION}
              className={clsx(
                "flex h-[55px] w-full items-center justify-center gap-[10px] rounded-full border border-white/20 px-5 text-[15px] font-semibold leading-[16px] text-white",
                isSubmitting ? "bg-[#4f7cff]/90" : "bg-[#3e6ffb]"
              )}
            >
              <img
                src="/assets/landing/castle-logo.svg"
                alt=""
                aria-hidden="true"
                className="h-[24px] w-[24px] brightness-0 invert"
              />
              <span>{isSubmitting ? t("buttons.entering") : t("buttons.enter")}</span>
            </motion.button>

            {/* Do not hide this block with :focus-within. On iOS a tapped
                button receives focus before click; removing it from layout at
                that moment cancels Google, Apple and registration actions. */}
            {!mobileKeyboardActive && (
              <div className="contents">
                {!isRuAuthContext && (
                  <motion.button
                    type="button"
                    onClick={handleGoogleClick}
                    disabled={isSubmitting}
                    whileTap={isSubmitting ? undefined : PRESS_TAP}
                    transition={PRESS_TRANSITION}
                    className="flex h-[55px] w-full items-center justify-center gap-[14px] rounded-full bg-white px-5 text-[15px] font-semibold leading-[16px] text-black"
                  >
                    <img src="/assets/landing/google-color.png" alt="" className="h-[22px] w-[20px] object-contain" />
                    <span>{t("buttons.continue_with_google")}</span>
                  </motion.button>
                )}

                {!isRuAuthContext && nativeAppleSignInEnabled && (
                  <motion.button
                    type="button"
                    onClick={handleAppleClick}
                    disabled={isSubmitting}
                    whileTap={isSubmitting ? undefined : PRESS_TAP}
                    transition={PRESS_TRANSITION}
                    className="flex h-[55px] w-full items-center justify-center gap-[14px] rounded-full bg-white px-5 text-[15px] font-semibold leading-[16px] text-black"
                  >
                    <span aria-hidden="true" className="text-[28px] leading-[24px]"></span>
                    <span>{t("buttons.continue_with_apple", "Continue with Apple")}</span>
                  </motion.button>
                )}

              <motion.button
                type="button"
                onClick={() => {
                  setTab("register");
                  resetMobileRegistrationFlow();
                  reset();
                  setErrorMsg("");
                  setAvatarIdx(null);
                  setRegisterMode(modeParam === "google" && gTokenParam ? "google" : "classic");
                  setGoogleToken(modeParam === "google" && gTokenParam ? gTokenParam : null);
                  setAppleToken(null);
                }}
                whileTap={PRESS_TAP}
                transition={PRESS_TRANSITION}
                className="flex h-[55px] w-full items-center justify-center gap-[14px] rounded-full border border-white/25 bg-white/10 px-5 text-[15px] font-semibold leading-[16px] text-white backdrop-blur-[18px]"
              >
                <img src="/assets/landing/new_account_icon.svg" alt="" className="h-[24px] w-[24px]" />
                <span>{t("buttons.create_new_account", "Create new account")}</span>
              </motion.button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderMobileLoginView = () => {
    if (useStableMobileAuth) return renderStableMobileWebLoginView();

    const { width: availableW, height: availableH } = getAuthStageAvailableSize();
    const loginScale = getMobileAuthScale(availableW, availableH);
    const hasLoginErrors = Boolean(errors.username || errors.password);
    const hasGeneralError = Boolean(errorMsg);
    const fixedWelcomeTop = hasGeneralError ? 248 : 274;
    const fixedUsernameTop = hasLoginErrors
      ? 437
      : hasGeneralError
        ? 427
        : 437;
    const fixedPasswordTop = hasLoginErrors
      ? 530
      : hasGeneralError
        ? 492
        : 502;
    const fixedForgotTop = hasLoginErrors ? 609 : 567;
    const { actionButtonGap, createAccountTop, googleTop, appleTop } = getMobileActionLayout(availableH, loginScale);
    const enterTop = googleTop - 55 - actionButtonGap;
    const compactForgotTop = enterTop - (hasLoginErrors ? 42 : 34);
    const forgotTop = Math.min(fixedForgotTop, compactForgotTop);
    const compactPasswordTop = forgotTop - (hasLoginErrors ? 79 : 65);
    const passwordTop = Math.min(fixedPasswordTop, compactPasswordTop);
    const compactUsernameTop = passwordTop - (hasLoginErrors ? 93 : 65);
    const usernameTop = Math.min(fixedUsernameTop, compactUsernameTop);
    const welcomeGap = authRequiredNoticeActive ? 132 : 164;
    const welcomeTop = Math.max(96, Math.min(fixedWelcomeTop, usernameTop - welcomeGap));
    const loginKeyboardShiftY = getMobileAuthKeyboardShiftY({
      isDesktop,
      isKeyboardOpen: mobileKeyboardActive,
      isInputFocused:
        nativeApp && nativeKeyboardState.listenersReady
          ? false
          : isInputFocused,
      stageHeight: availableH,
      keyboardHeight: nativeKeyboardState.height,
      keyboardBottomInset: authStageSize.bottomInset,
      scale: loginScale,
      contentBottom: Math.max(passwordTop + 55, enterTop + 55),
      allowFallback: !(nativeApp && nativeKeyboardState.listenersReady),
    });
    const welcomeHeight = authRequiredNoticeActive ? 108 : 78;
    const usernameVisualTop = usernameTop + loginKeyboardShiftY;
    const welcomeVisualTop = mobileKeyboardActive
      ? Math.max(
          64,
          Math.min(welcomeTop, (48 + usernameVisualTop - welcomeHeight) / 2)
        )
      : welcomeTop;
    const welcomeKeyboardShiftY = welcomeVisualTop - welcomeTop;
    const usernameErrorTop = usernameTop + 61;
    const passwordErrorTop = passwordTop + 61;
    const googleBrowserError = errorMsg === t("errors.google_requires_browser");
    const generalErrorTop = usernameTop - (googleBrowserError ? 78 : 42);
    // Keep the active field above the mobile keyboard on short viewports. The
    // auth screen is a scaled design canvas, so relying on browser auto-scroll
    // is unreliable when the outer shell is fixed.
    const welcomeCopy = authRequiredNoticeActive
      ? t("messages.account_required_notice")
      : WELCOME_ROTATION_MESSAGES[welcomeIndex];

    return (
      <div
        className="relative h-full w-full overflow-hidden bg-[#050812]"
        data-figma-node="305:5591"
        style={{
          width: "100%",
          maxWidth: "none",
          ...mobileAuthStageSurfaceStyle,
        }}
      >
        <div
          className="absolute left-1/2 top-0 h-[874px] w-[402px] origin-top"
          style={{
            transform: `translateX(-50%) scale(${loginScale})`,
            height: `${AUTH_DESIGN_HEIGHT}px`,
            width: `${AUTH_DESIGN_WIDTH}px`,
          }}
        >
      <AuthLanguageToggle />

      <div
        className="absolute inset-0 transition-transform duration-200"
        style={{ transform: `translateY(${loginKeyboardShiftY}px)` }}
      >
        <div
          className={clsx(
            "absolute left-1/2 flex -translate-x-1/2 items-center justify-center text-center font-semibold text-white transition-[opacity,transform] duration-200",
            authRequiredNoticeActive
              ? "h-[108px] w-[320px] text-[24px] leading-[29px]"
              : "h-[78px] w-[247px] text-[40px] leading-[1.1]",
            (authRequiredNoticeActive ? authRequiredNoticeVisible : welcomeVisible)
              ? "opacity-100"
              : "opacity-0"
          )}
          style={{
            top: `${welcomeTop}px`,
            transform: `translateX(-50%) translateY(${welcomeKeyboardShiftY - loginKeyboardShiftY}px)`,
          }}
        >
          {welcomeCopy}
        </div>

        {errorMsg && (
          <div
            className="absolute left-[34px] w-[335px] text-center text-[12px] font-medium leading-[15px] text-red-300"
            style={{ top: `${generalErrorTop}px` }}
          >
            {errorMsg}
          </div>
        )}

        {renderField("username", "text", "", undefined, false, {
          wrapperClassName: "absolute left-[30px] w-[343px]",
          wrapperStyle: { top: `${usernameTop}px` },
          placeholder: t("placeholders.username"),
          inputClassName:
            "h-[55px] w-full rounded-full border border-white/25 bg-white/10 px-[20px] pr-12 text-[15px] font-medium leading-[16px] text-white placeholder:text-white/50 outline-none backdrop-blur-[18px]",
          showError: false,
        })}
        {errors.username && (
          <div
            className="absolute left-[50px] whitespace-nowrap text-[13px] font-medium leading-[16px] text-red-500"
            style={{ top: `${usernameErrorTop}px` }}
          >
            {errors.username.message}
          </div>
        )}
        {renderField("password", "password", "placeholders.password", undefined, false, {
          wrapperClassName: "absolute left-[30px] w-[343px]",
          wrapperStyle: { top: `${passwordTop}px` },
          inputClassName:
            "h-[55px] w-full rounded-full border border-white/25 bg-white/10 px-[20px] pr-12 text-[15px] font-medium leading-[16px] text-white placeholder:text-white/50 outline-none backdrop-blur-[18px]",
          showError: false,
        })}
        {errors.password && (
          <div
            className="absolute left-[50px] whitespace-nowrap text-[13px] font-medium leading-[16px] text-red-500"
            style={{ top: `${passwordErrorTop}px` }}
          >
            {errors.password.message}
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            setTab("forgot");
            setForgotStep(1);
            setForgotEmail("");
            setForgotCode("");
            reset();
            setErrorMsg("");
          }}
          className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-center text-[15px] font-normal leading-[22px] text-white/50 underline underline-offset-2"
          style={{ top: `${forgotTop}px` }}
        >
          {t("buttons.forgot_password")}
        </button>

        <motion.button
          type="submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
          whileHover={isSubmitting ? undefined : PRESS_HOVER}
          whileTap={isSubmitting ? undefined : PRESS_TAP}
          transition={PRESS_TRANSITION}
          className={clsx(
            "absolute left-[30px] flex h-[55px] w-[343px] items-center justify-center gap-[10px] rounded-full border border-white/20 px-5 text-[15px] font-semibold leading-[16px] text-white transition-all",
            isSubmitting ? "bg-[#4f7cff]/90" : "bg-[#3e6ffb]"
          )}
          style={{ top: `${enterTop}px` }}
        >
          <img
            src="/assets/landing/castle-logo.svg"
            alt=""
            aria-hidden="true"
            className="h-[24px] w-[24px] brightness-0 invert"
          />
          <span>{isSubmitting ? t("buttons.entering") : t("buttons.enter")}</span>
        </motion.button>
      </div>

      {!isRuAuthContext && (
        <motion.button
          type="button"
          onClick={handleGoogleClick}
          disabled={isSubmitting}
          whileHover={isSubmitting ? undefined : PRESS_HOVER}
          whileTap={isSubmitting ? undefined : PRESS_TAP}
          transition={PRESS_TRANSITION}
          className="absolute left-[30px] top-[733px] flex h-[55px] w-[343px] items-center justify-center gap-[14px] rounded-full bg-white px-5 text-[15px] font-semibold leading-[16px] text-black"
          style={{ top: `${googleTop}px` }}
        >
          <img
            src="/assets/landing/google-color.png"
            alt=""
            className="h-[22px] w-[20px] object-contain"
          />
          <span>{t("buttons.continue_with_google")}</span>
        </motion.button>
      )}

      {!isRuAuthContext && nativeAppleSignInEnabled ? (
        <motion.button
          type="button"
          onClick={handleAppleClick}
          disabled={isSubmitting}
          whileHover={isSubmitting ? undefined : PRESS_HOVER}
          whileTap={isSubmitting ? undefined : PRESS_TAP}
          transition={PRESS_TRANSITION}
          className="absolute left-[30px] flex h-[55px] w-[343px] items-center justify-center gap-[14px] rounded-full bg-white px-5 text-[15px] font-semibold leading-[16px] text-black"
          style={{ top: `${appleTop}px` }}
        >
          <span
            aria-hidden="true"
            className="flex h-[24px] w-[24px] items-center justify-center text-[28px] leading-[24px] text-black"
          >
            
          </span>
          <span>{t("buttons.continue_with_apple", "Continue with Apple")}</span>
        </motion.button>
      ) : null}

      <motion.button
        type="button"
	        onClick={() => {
	          setTab("register");
	          resetMobileRegistrationFlow();
	          reset();
	          setErrorMsg("");
          setAvatarIdx(null);
          setRegisterMode(modeParam === "google" && gTokenParam ? "google" : "classic");
          setGoogleToken(modeParam === "google" && gTokenParam ? gTokenParam : null);
          setAppleToken(null);
        }}
        whileHover={PRESS_HOVER}
        whileTap={PRESS_TAP}
        transition={PRESS_TRANSITION}
        className="absolute left-[30px] top-[798px] flex h-[55px] w-[343px] items-center justify-center gap-[14px] rounded-full border border-white/25 bg-white/10 px-5 text-[15px] font-semibold leading-[16px] text-white backdrop-blur-[18px]"
        style={{ top: `${createAccountTop}px` }}
      >
	        <img src="/assets/landing/new_account_icon.svg" alt="" className="h-[24px] w-[24px]" />
        <span>{t("buttons.create_new_account", "Create new account")}</span>
      </motion.button>
	        </div>
		      </div>
		    );
		  };

  const renderStableMobileWebForgotView = () => {
    const isCodeStep = forgotStep === 2;
    const isPasswordStep = forgotStep === 3;
    const title = isPasswordStep
      ? t("headings.set_new_password")
      : isCodeStep
        ? t("headings.sounds_better")
        : t("headings.not_cool");
    const subtitle = isPasswordStep
      ? t("messages.password_reset_choose_new")
      : isCodeStep
        ? t("messages.password_reset_code_sent")
        : t("messages.password_reset_intro");
    const primaryLabel = isSubmitting
      ? t("buttons.processing")
      : forgotStep === 1
        ? t("buttons.send_reset_code")
        : forgotStep === 2
          ? t("buttons.continue")
          : t("buttons.reset_password");

    const stableForgotField = (name, type, placeholderKey, inputMode) => (
      <div className="w-full">
        {renderField(name, type, placeholderKey, name === "code" ? 6 : undefined, false, {
          wrapperClassName: "w-full",
          inputClassName: stableAuthFieldClassName,
          inputMode,
          enterKeyHint: name === "new_password1" ? "next" : "done",
          onKeyDown: handleStableAuthFieldKeyDown,
          showError: false,
        })}
        {errors[name] && (
          <div className="mt-[4px] px-[20px] text-[12px] font-medium leading-[15px] text-red-400">
            {errors[name].message}
          </div>
        )}
      </div>
    );

    return (
      <div
        className={clsx(
          "relative h-full min-h-[500px] w-full overflow-visible text-white",
          stableAuthSurfaceClassName
        )}
        style={stableAuthSurfaceStyle}
        data-testid="stable-mobile-web-forgot"
      >
        <motion.button
          type="button"
          onClick={handleForgotBack}
          whileTap={SUBTLE_PRESS_TAP}
          transition={PRESS_TRANSITION}
          className={clsx(
            "absolute left-[24px] z-20 flex h-[24px] w-[24px] items-center justify-center",
            useNativeIosSafeStage
              ? "top-[calc(var(--vg-safe-area-top)+24px)]"
              : "top-[24px]"
          )}
          aria-label={t("buttons.go_back")}
        >
          <img src="/assets/landing/back_arrow.svg" alt="" className="h-[24px] w-[24px]" />
        </motion.button>

        <AuthLanguageToggle
          className={useNativeIosSafeStage ? "top-[calc(var(--vg-safe-area-top)+24px)]" : ""}
        />

        <div className={clsx(
          "mx-auto flex h-full min-h-[500px] w-full max-w-[402px] flex-col px-[30px]",
          useNativeIosSafeStage
            ? "pt-[calc(var(--vg-safe-area-top)+78px)] pb-[calc(var(--vg-safe-area-bottom)+24px)]"
            : clsx("pt-[78px]", stableAuthBottomPaddingClassName)
        )}>
          <div className="flex min-h-[72px] flex-1 items-center justify-center py-[18px]">
            <h1 className="text-center text-[38px] font-semibold leading-[42px] text-white">
              {title}
            </h1>
          </div>

          <div className="flex shrink-0 flex-col">
            <p className="mx-auto max-w-[270px] text-center text-[15px] font-semibold leading-[20px] text-white">
              {subtitle}
            </p>

            <div className="mt-[18px] flex flex-col gap-[10px]">
              {forgotStep === 1 && stableForgotField("email", "text", "placeholders.email", "email")}
              {forgotStep === 2 && stableForgotField("code", "text", "placeholders.reset_code", "numeric")}
              {forgotStep === 3 && (
                <>
                  {stableForgotField("new_password1", "password", "placeholders.new_password")}
                  {stableForgotField("new_password2", "password", "placeholders.new_password_repeat")}
                </>
              )}
            </div>

            {errorMsg && (
              <div className="mt-[8px] text-center text-[12px] font-semibold leading-[15px] text-[#ffb0b0]">
                {errorMsg}
              </div>
            )}

            <motion.button
              type="submit"
              data-auth-keyboard-context-end="forgot"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              whileTap={isSubmitting ? undefined : PRESS_TAP}
              transition={PRESS_TRANSITION}
              className={clsx(
                "mt-[10px] flex h-[55px] w-full shrink-0 items-center justify-center rounded-full px-5 text-[15px] font-semibold leading-[16px] text-white shadow-[0_14px_34px_rgba(62,111,251,0.35)]",
                isSubmitting ? "bg-[#4f7cff]/90" : "bg-[#3e6ffb]"
              )}
            >
              {primaryLabel}
            </motion.button>
          </div>
        </div>
      </div>
    );
  };

		  const renderMobileForgotView = () => {
    if (useStableMobileAuth) return renderStableMobileWebForgotView();

    const { width: availableW, height: availableH } = getAuthStageAvailableSize();
    const forgotScale = getMobileAuthScale(availableW, availableH);
    const isCodeStep = forgotStep === 2;
    const isPasswordStep = forgotStep === 3;
    const title = isPasswordStep
      ? t("headings.set_new_password")
      : isCodeStep
        ? t("headings.sounds_better")
        : t("headings.not_cool");
    const subtitle = isPasswordStep
      ? t("messages.password_reset_choose_new")
      : isCodeStep
        ? t("messages.password_reset_code_sent")
        : t("messages.password_reset_intro");
    const { createAccountTop } = getMobileActionLayout(availableH, forgotScale);
    const primaryButtonTop = createAccountTop;
    const titleTop = 274;
    const subtitleTop = isPasswordStep ? 523 : isCodeStep ? 589 : 576;
    const singleFieldTop = 639;
    const passwordFieldsTop = Boolean(errors.new_password1 || errors.new_password2) ? 586 : 604;
    const passwordRepeatTop = passwordFieldsTop + 65 + (errors.new_password1 ? 22 : 0);
    const fieldClassName =
      "h-[55px] w-[343px] rounded-full border border-white/25 bg-white/10 px-[20px] pr-12 text-[15px] font-medium leading-[16px] text-white outline-none backdrop-blur-[18px] placeholder:text-white/50";
    const forgotErrorClassName =
      "absolute left-[50px] z-10 w-[303px] truncate text-left text-[12px] font-medium leading-[15px] text-[#ff4a4a]";
    const primaryLabel = isSubmitting
      ? t("buttons.processing")
      : forgotStep === 1
        ? t("buttons.send_reset_code")
        : forgotStep === 2
          ? t("buttons.continue")
          : t("buttons.reset_password");

    return (
      <div
        className="relative h-full w-full overflow-hidden bg-[#050812]"
        style={{
          width: "100%",
          maxWidth: "none",
          ...mobileAuthStageSurfaceStyle,
        }}
      >
        <div
          className="absolute left-1/2 top-0 h-[874px] w-[402px] origin-top overflow-hidden"
          style={{
            transform: `translateX(-50%) translateY(${mobileKeyboardShiftY}px) scale(${forgotScale})`,
            height: `${AUTH_DESIGN_HEIGHT}px`,
            width: `${AUTH_DESIGN_WIDTH}px`,
          }}
        >
          <motion.button
            type="button"
            onClick={handleForgotBack}
            whileTap={SUBTLE_PRESS_TAP}
            transition={PRESS_TRANSITION}
            className="absolute left-[24px] top-[24px] z-20 flex h-[24px] w-[24px] items-center justify-center"
            aria-label={t("buttons.go_back")}
          >
            <img src="/assets/landing/back_arrow.svg" alt="" className="h-[24px] w-[24px]" />
          </motion.button>

          <motion.h1
            key={`forgot-title-${forgotStep}`}
            className="absolute left-[51px] w-[300px] text-center text-[40px] font-semibold leading-[1.1] text-white"
            style={{ top: `${titleTop}px` }}
            initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.32, ease: "easeOut" }}
          >
            {title}
          </motion.h1>

          <motion.p
            key={`forgot-subtitle-${forgotStep}`}
            className="absolute left-[76px] w-[250px] text-center text-[15px] font-semibold leading-[1.25] text-white"
            style={{ top: `${subtitleTop}px` }}
            initial={{ opacity: 0, y: 8, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.28, ease: "easeOut", delay: 0.05 }}
          >
            {subtitle}
          </motion.p>

          {forgotStep === 1 && (
            <>
              {renderField("email", "text", "placeholders.email", undefined, false, {
                wrapperClassName: "absolute left-[30px] w-[343px]",
                wrapperStyle: { top: `${singleFieldTop}px` },
                inputClassName: fieldClassName,
                inputMode: "email",
                showError: false,
              })}
              {errors.email && (
                <div className={forgotErrorClassName} style={{ top: `${singleFieldTop + 62}px` }}>
                  {errors.email.message}
                </div>
              )}
            </>
          )}

          {forgotStep === 2 && (
            <>
              {renderField("code", "text", "placeholders.reset_code", 6, false, {
                wrapperClassName: "absolute left-[30px] w-[343px]",
                wrapperStyle: { top: `${singleFieldTop}px` },
                inputClassName: fieldClassName,
                inputMode: "numeric",
                showError: false,
              })}
              {errors.code && (
                <div className={forgotErrorClassName} style={{ top: `${singleFieldTop + 62}px` }}>
                  {errors.code.message}
                </div>
              )}
            </>
          )}

          {forgotStep === 3 && (
            <>
              {renderField("new_password1", "password", "placeholders.new_password", undefined, false, {
                wrapperClassName: "absolute left-[30px] w-[343px]",
                wrapperStyle: { top: `${passwordFieldsTop}px` },
                inputClassName: fieldClassName,
                showError: false,
              })}
              {errors.new_password1 && (
                <div className={forgotErrorClassName} style={{ top: `${passwordFieldsTop + 62}px` }}>
                  {errors.new_password1.message}
                </div>
              )}

              {renderField("new_password2", "password", "placeholders.new_password_repeat", undefined, false, {
                wrapperClassName: "absolute left-[30px] w-[343px]",
                wrapperStyle: { top: `${passwordRepeatTop}px` },
                inputClassName: fieldClassName,
                showError: false,
              })}
              {errors.new_password2 && (
                <div className={forgotErrorClassName} style={{ top: `${passwordRepeatTop + 62}px` }}>
                  {errors.new_password2.message}
                </div>
              )}
            </>
          )}

          {errorMsg && (
            <div
              className="absolute left-[30px] z-10 w-[343px] text-center text-[12px] font-semibold leading-[15px] text-[#ffb0b0]"
              style={{ top: `${primaryButtonTop - 25}px` }}
            >
              {errorMsg}
            </div>
          )}

          <motion.button
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            whileHover={isSubmitting ? undefined : PRESS_HOVER}
            whileTap={isSubmitting ? undefined : PRESS_TAP}
            transition={PRESS_TRANSITION}
            className={clsx(
              "absolute left-[30px] flex h-[55px] w-[343px] items-center justify-center rounded-full px-5 text-[15px] font-semibold leading-[16px] text-white shadow-[0_14px_34px_rgba(62,111,251,0.35)]",
              isSubmitting ? "bg-[#4f7cff]/90" : "bg-[#3e6ffb]"
            )}
            style={{ top: `${primaryButtonTop}px` }}
          >
            {primaryLabel}
          </motion.button>
        </div>
      </div>
    );
	  };

  const renderMobileSignupFinalIntro = () => {
    const isLocalPreviewIntro = registrationFinalIntroMode === "local-preview";
    const introSteps = registrationFinalIntroRun?.steps || mobileSignupIntroSteps;
    const activeIntroStep =
      registrationFinalIntroStage < introSteps.length
        ? introSteps[registrationFinalIntroStage]
        : null;
    const activeIntroBlocks = activeIntroStep?.bodyBlocks || [];
    const activeIntroAnimatedBlocks = buildMobileSignupIntroBlocks(activeIntroBlocks);
    const activeIntroBody = getMobileSignupIntroBody(activeIntroStep);
    const showWelcome = registrationFinalIntroStage >= introSteps.length;

    return (
      <AnimatePresence>
        {registrationFinalIntroActive && (
          <motion.div
            key="mobile-signup-final-intro"
            data-testid="mobile-signup-final-intro"
            className="absolute inset-0 z-50 flex items-center justify-center overflow-hidden text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, filter: "blur(12px)" }}
            transition={{ duration: 0.42, ease: "easeOut" }}
            style={{
              ...AUTH_GRADIENT_STYLE,
              backgroundColor: "#050812",
            }}
          >
            <motion.div
              className="absolute left-1/2 top-[-10%] h-[36%] w-[72%] -translate-x-1/2 rounded-full bg-white/28 blur-[70px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
            <motion.div
              className="absolute bottom-[-12%] left-1/2 h-[32%] w-[78%] -translate-x-1/2 rounded-full bg-black/55 blur-[52px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />

            <AnimatePresence mode="sync">
              {activeIntroStep && (
                <motion.div
                  key={activeIntroStep.title}
                  className="absolute inset-0 text-center tracking-normal"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 1.08, y: -24, filter: "blur(12px)" }}
                  transition={{ duration: 0.72, ease: [0.18, 0.9, 0.22, 1] }}
                >
                  <div
                    className="absolute left-0 flex w-full -translate-y-1/2 items-center justify-center px-[24px]"
                    style={{ top: `calc(50% + ${MOBILE_SIGNUP_INTRO_TITLE_OFFSET_PX}px)` }}
                  >
                    <motion.div
                      className="max-w-[min(360px,calc(100vw-48px))] whitespace-normal text-balance text-[clamp(30px,9.2vw,40px)] font-semibold leading-[1.1]"
                      initial={{ opacity: 0, scale: 0.82, y: 28, filter: "blur(16px)" }}
                      animate={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
                      transition={{ duration: 0.86, ease: [0.18, 0.9, 0.22, 1] }}
                    >
                      {activeIntroStep.title}
                    </motion.div>
                  </div>
                  <p
                    className="absolute left-1/2 top-[calc(50%+72px)] m-0 w-[min(340px,calc(100%-58px))] -translate-x-1/2 text-center text-[15px] font-medium leading-[22px] text-white/76"
                    aria-label={activeIntroBody}
                  >
                    {activeIntroAnimatedBlocks.map((block, blockIndex) => (
                      <span
                        key={`${activeIntroStep.title}-block-${blockIndex}`}
                        aria-hidden="true"
                        className={clsx("block", blockIndex > 0 && "mt-[8px]")}
                      >
                        {block.lines.map((line, lineIndex) => (
                          <span
                            key={`${activeIntroStep.title}-${blockIndex}-${lineIndex}-${line.line}`}
                            className={clsx("block whitespace-normal", lineIndex > 0 && "mt-[1px]")}
                          >
                            {line.words.map((word, wordIndex) => (
                              <span
                                key={`${line.line}-${wordIndex}-${word.word}`}
                                className="mr-[0.24em] inline-block whitespace-nowrap"
                              >
                                {word.chars.map(({ char, delaySec }, charIndex) => (
                                  <motion.span
                                    key={`${word.word}-${charIndex}-${char}`}
                                    className="inline-block"
                                    initial={{ opacity: 0, filter: "blur(7px)" }}
                                    animate={{ opacity: 1, filter: "blur(0px)" }}
                                    transition={{
                                      delay: delaySec,
                                      duration: 0.42,
                                      ease: [0.18, 0.9, 0.22, 1],
                                    }}
                                  >
                                    {char}
                                  </motion.span>
                                ))}
                              </span>
                            ))}
                          </span>
                        ))}
                      </span>
                    ))}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showWelcome && (
                <motion.div
                  key="mobile-signup-final-welcome"
                  className="absolute inset-0 flex flex-col items-center justify-center px-[32px] text-center"
                  initial={{ opacity: 0, y: 34, scale: 0.9, filter: "blur(14px)" }}
                  animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -20, scale: 1.04, filter: "blur(10px)" }}
                  transition={{ duration: 0.72, ease: [0.18, 0.9, 0.22, 1] }}
                >
                  <span className="text-[35px] font-semibold leading-[39px]">
                    {t("registration_flow.intro.welcome.line_1", { defaultValue: "Welcome to" })}
                  </span>
                  <span className="mt-[4px] text-[35px] font-semibold leading-[39px]">
                    {t("registration_flow.intro.welcome.line_2", { defaultValue: "the community" })}
                  </span>
                  {isLocalPreviewIntro && registrationFinalIntroComplete ? (
                    <motion.button
                      type="button"
                      onClick={returnFromLocalMobileSignupPreview}
                      whileTap={SUBTLE_PRESS_TAP}
                      className="mt-[30px] h-[44px] min-w-[170px] rounded-full border border-white/26 bg-white/10 px-[22px] text-[13px] font-semibold leading-[18px] text-white shadow-[0_14px_34px_rgba(0,0,0,0.18)] backdrop-blur-[18px]"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.18, duration: 0.36 }}
                    >
                      {t("registration_flow.intro.welcome.back_to_photo", { defaultValue: "Back to photo" })}
                    </motion.button>
                  ) : (
                    <motion.div
                      className="mt-[30px] flex items-center gap-[10px] text-[13px] font-medium leading-[18px] text-white/64"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.36, duration: 0.45 }}
                    >
                      <span>{t("registration_flow.intro.welcome.creating_account", { defaultValue: "Creating your account" })}</span>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  const renderStableMobileWebRegisterView = () => {
    const codeLetters = (registrationCode || "---").padEnd(3, "-").slice(0, 3).split("");
    const canBrowsePreviousRegistrationCode =
      registrationCodeHistory.length > 1 && registrationCodeHistoryIndex > 0;
    const canBrowseNextRegistrationCode =
      registrationCodeHistory.length > 1 &&
      registrationCodeHistoryIndex >= 0 &&
      registrationCodeHistoryIndex < registrationCodeHistory.length - 1;
    const canSubmitRegisterForm = canSubmitRegistrationIdentityStep({
      isSubmitting,
      registrationCodeLoading,
      registrationCode,
      acceptedTerms,
      isRussianLegalBundle,
      acceptedPersonalDataConsent,
    });
    const canSubmitRegister = localRegistrationWalkthroughPreview || (
      registrationStep === "form"
        ? canSubmitRegisterForm
        : registrationStep === "email"
          ? !isSubmitting &&
            !registrationEmailSending &&
            /^\d{6}$/.test(registrationEmailCodeInput.replace(/\D/g, ""))
          : registrationStep === "city"
            ? !isSubmitting && Boolean(registrationCity.trim())
            : !isSubmitting && Boolean(registrationPhotoFile));
    const codeLimitReached = registrationCodeRemaining === 0 && Boolean(registrationCode);
    const codeGenerateDisabled = registrationCodeLoading || isSubmitting || codeLimitReached;
    const codeButtonLabel = registrationCodeLoading
      ? t("buttons.generating")
      : registrationCodeFlashLabel
        ? registrationCodeFlashLabel
        : codeLimitReached
          ? t("buttons.limit_reached")
          : t("buttons.generate_new");
    const codeButtonTitle = registrationCodeResetAt
      ? `Limit ${registrationCodeLimit} per 24h. Remaining: ${registrationCodeRemaining ?? "unknown"}. Resets at ${registrationCodeResetAt}.`
      : `Limit ${registrationCodeLimit} per 24h. Remaining: ${registrationCodeRemaining ?? "unknown"}.`;
    const primaryButtonLabel = localRegistrationWalkthroughPreview
      ? t("buttons.continue")
      : registrationStep === "form" && isSocialRegistration
        ? t("buttons.continue")
        : registrationStep === "email"
          ? t("registration_flow.email.confirm", { defaultValue: "Confirm email" })
          : registrationStep === "city"
            ? t("buttons.continue")
            : localRegistrationOnboardingFlowPreview
              ? t("buttons.continue")
              : t("buttons.create_account");
    const primaryButtonProgressLabel =
      registrationStep === "email"
        ? t("registration_flow.email.confirming", { defaultValue: "Confirming..." })
        : registrationPhotoChecking
          ? t("registration_flow.photo.checking", { defaultValue: "Checking photo..." })
          : t("buttons.creating_account", "Creating account...");
    const resendEmailDisabled = registrationEmailSending || registrationEmailResendCooldown > 0;
    const resendEmailLabel = registrationEmailSending
      ? t("registration_flow.email.sending", { defaultValue: "Sending..." })
      : registrationEmailResendStatus
        ? registrationEmailResendStatus
        : registrationEmailResendCooldown > 0
          ? t("registration_flow.email.resend_in", {
              seconds: registrationEmailResendCooldown,
              defaultValue: `Resend in ${registrationEmailResendCooldown}s`,
            })
          : t("registration_flow.email.resend_code", { defaultValue: "Resend code" });
    const registerFieldNames = isSocialRegistration
      ? ["username"]
      : ["username", "email", "password1", "password2"];

    const goBack = () => {
      if (registrationStepOrder.indexOf(registrationStep) > 0) {
        const stepOrder = registrationStepOrder;
        const currentIndex = stepOrder.indexOf(registrationStep);
        setRegistrationStep(stepOrder[Math.max(0, currentIndex - 1)] || "form");
        setRegistrationStepError("");
        return;
      }
      setTab("login");
      resetMobileRegistrationFlow();
      reset();
      setErrorMsg("");
      setRegisterMode("classic");
      setGoogleToken(null);
      setAppleToken(null);
    };

    const renderPrimaryButton = (className = "mt-[14px]") => (
      <motion.button
        type="submit"
        data-auth-keyboard-context-end={registrationStep === "form" ? "register-form" : undefined}
        disabled={!canSubmitRegister}
        whileTap={canSubmitRegister ? PRESS_TAP : undefined}
        transition={PRESS_TRANSITION}
        className={clsx(
          className,
          "flex h-[55px] w-full shrink-0 items-center justify-center rounded-full border border-white/20 px-5 text-[15px] font-semibold leading-[16px] text-white transition-all",
          canSubmitRegister ? "bg-[#3e6ffb]" : "bg-[#2e58d0]/70 text-white/50"
        )}
      >
        {isSubmitting ? primaryButtonProgressLabel : primaryButtonLabel}
      </motion.button>
    );

    const renderBackButton = () => (
      <motion.button
        type="button"
        onClick={goBack}
        whileTap={SUBTLE_PRESS_TAP}
        transition={PRESS_TRANSITION}
        className="absolute left-[24px] top-[24px] z-30 flex h-[24px] w-[24px] items-center justify-center"
        aria-label={t("buttons.go_back")}
      >
        <img src="/assets/landing/back_arrow.svg" alt="" className="h-[24px] w-[24px] opacity-90" />
      </motion.button>
    );

    const renderRegisterFormStep = () => (
      <div className={clsx(
        "mx-auto flex h-full min-h-[630px] w-full max-w-[402px] flex-col px-[30px] pt-[64px]",
        stableAuthBottomPaddingClassName
      )}>
        <div className="flex min-h-[260px] flex-1 flex-col justify-center py-0">
          <h1 className="text-center text-[27px] font-semibold leading-[32px] text-white">
            {t("headings.vaultgrid_code", { defaultValue: "Your VaultGrid code" })}
          </h1>

          <div className="mx-auto mt-[10px] flex h-[120px] w-[290px] items-stretch justify-between">
            {codeLetters.map((letter, idx) => (
              <div
                key={`${letter}-${idx}`}
                className="flex h-[120px] w-[90px] shrink-0 items-center justify-center rounded-[16px] border border-white/25 bg-black/10 text-[64px] font-black leading-none text-white"
              >
                {letter}
              </div>
            ))}
          </div>

          <div className="mt-[15px] flex items-center justify-center gap-[10px]">
            {canBrowsePreviousRegistrationCode ? (
              <motion.button
                type="button"
                onClick={() => selectRegistrationCodeFromHistory(-1)}
                whileTap={SUBTLE_PRESS_TAP}
                className="flex h-[35px] w-[35px] items-center justify-center rounded-full border border-white/20 bg-white/10"
                aria-label="Previous generated code"
              >
                <img src="/assets/landing/back_arrow.svg" alt="" className="h-[18px] w-[18px] opacity-90" />
              </motion.button>
            ) : null}
            <motion.button
              type="button"
              onClick={() => localRegistrationWalkthroughPreview
                ? setRegistrationCode((current) => current === "RAT" ? "VGT" : "RAT")
                : fetchRegistrationCode()}
              disabled={codeGenerateDisabled}
              title={codeButtonTitle}
              whileTap={codeGenerateDisabled ? undefined : PRESS_TAP}
              transition={PRESS_TRANSITION}
              className="flex h-[35px] min-w-[190px] items-center justify-center gap-[9px] rounded-full border border-white/20 bg-[#3e6ffb] px-4 text-[12px] font-semibold leading-[16px] text-white disabled:opacity-45"
            >
              <span aria-hidden="true">↻</span>
              <span>{codeButtonLabel}</span>
            </motion.button>
            {canBrowseNextRegistrationCode ? (
              <motion.button
                type="button"
                onClick={() => selectRegistrationCodeFromHistory(1)}
                whileTap={SUBTLE_PRESS_TAP}
                className="flex h-[35px] w-[35px] items-center justify-center rounded-full border border-white/20 bg-white/10"
                aria-label="Next generated code"
              >
                <img src="/assets/landing/back_arrow.svg" alt="" className="h-[18px] w-[18px] rotate-180 opacity-90" />
              </motion.button>
            ) : null}
          </div>

          {registrationCodeError ? (
            <div className="mt-[4px] text-center text-[12px] font-medium leading-[15px] text-red-300">
              {registrationCodeError}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-col">
          <div className="flex flex-col gap-[10px]">
          {renderField("username", "text", "", 16, false, {
            wrapperClassName: "w-full",
            placeholder: isSocialRegistration
              ? t("placeholders.vaultgrid_username")
              : t("placeholders.username"),
            inputClassName: stableAuthFieldClassName,
            enterKeyHint: isSocialRegistration ? "done" : "next",
            onKeyDown: handleStableAuthFieldKeyDown,
            showError: false,
          })}
          {isSocialRegistration ? (
            <>
              <input type="hidden" {...register("email")} />
              <input type="hidden" {...register("email_repeat")} />
            </>
          ) : (
            <>
              {renderField("email", "text", "", undefined, false, {
                wrapperClassName: "w-full",
                placeholder: t("placeholders.email"),
                inputClassName: stableAuthFieldClassName,
                inputMode: "email",
                enterKeyHint: "next",
                onKeyDown: handleStableAuthFieldKeyDown,
                showError: false,
              })}
              <input type="hidden" {...register("email_repeat")} />
              {renderField("password1", "password", "", undefined, false, {
                wrapperClassName: "w-full",
                placeholder: t("placeholders.password"),
                inputClassName: stableAuthFieldClassName,
                enterKeyHint: "next",
                onKeyDown: handleStableAuthFieldKeyDown,
                showError: false,
              })}
              {renderField("password2", "password", "", undefined, false, {
                wrapperClassName: "w-full",
                placeholder: t("placeholders.password_repeat"),
                inputClassName: stableAuthFieldClassName,
                enterKeyHint: "done",
                onKeyDown: handleStableAuthFieldKeyDown,
                showError: false,
              })}
            </>
          )}
          <input type="hidden" {...register("country")} />
          </div>

          {registerFieldNames.map((fieldName) =>
            errors[fieldName] ? (
              <div
                key={fieldName}
                className="mt-[4px] truncate px-[20px] text-left text-[12px] font-medium leading-[15px] text-red-400"
              >
                {errors[fieldName]?.message}
              </div>
            ) : null
          )}

          <label className="mt-[10px] flex items-start gap-[8px] text-[12px] font-normal leading-[16px] text-white">
          <input
            {...register("terms")}
            type="checkbox"
            checked={Boolean(acceptedTerms)}
            readOnly
            className="sr-only"
            tabIndex={-1}
          />
          <motion.button
            type="button"
            onClick={() => {
              setValue("terms", !acceptedTerms, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: false,
              });
              if (errors.terms) clearErrors("terms");
            }}
            className={clsx(
              "mt-[1px] flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[4px] border text-white",
              acceptedTerms ? "border-[#3e6ffb] bg-[#3e6ffb]" : "border-white/60 bg-black/10"
            )}
            whileTap={SUBTLE_PRESS_TAP}
            aria-pressed={Boolean(acceptedTerms)}
            aria-label={t("validation.terms_required")}
          >
            {acceptedTerms ? <span className="h-[8px] w-[8px] rounded-[2px] bg-white" /> : null}
          </motion.button>
          <span>
            {isRussianLegalBundle ? (
              <Trans
                i18nKey="ru_public_offer_text"
                ns="auth"
                components={[
                  <a href="/legal/privacy?from=signup" className="underline" aria-label="Политика обработки персональных данных" />,
                  <a href="/legal/user-agreement?from=signup" className="underline" aria-label="Пользовательское соглашение" />,
                  <a href="/legal/terms?from=signup" className="underline" aria-label="Публичная оферта" />,
                ]}
              />
            ) : (
              <Trans
                i18nKey="terms_text"
                ns="auth"
                components={[
                  <a href="/legal/terms?from=signup" className="underline" aria-label="User Agreement" />,
                  <a href="/legal/community-rules?from=signup" className="underline" aria-label="Community Rules" />,
                  <a href="/legal/moderation?from=signup" className="underline" aria-label="Moderation Policy" />,
                  <a href="/legal/privacy?from=signup" className="underline" aria-label="Privacy Policy" />,
                  <a href="/legal/cookies?from=signup" className="underline" aria-label="Cookies Policy" />,
                ]}
              />
            )}
          </span>
          </label>
          {isRussianLegalBundle ? (
            <label className="mt-[14px] flex items-start gap-[8px] text-[12px] font-normal leading-[16px] text-white">
              <input
                {...register("personal_data_consent")}
                type="checkbox"
                checked={Boolean(acceptedPersonalDataConsent)}
                readOnly
                className="sr-only"
                tabIndex={-1}
              />
              <motion.button
                type="button"
                onClick={() => {
                  setValue("personal_data_consent", !acceptedPersonalDataConsent, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: false,
                  });
                  if (errors.personal_data_consent) clearErrors("personal_data_consent");
                }}
                className={clsx(
                  "mt-[1px] flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[4px] border text-white",
                  acceptedPersonalDataConsent
                    ? "border-[#3e6ffb] bg-[#3e6ffb]"
                    : "border-white/60 bg-black/10"
                )}
                whileTap={SUBTLE_PRESS_TAP}
                aria-pressed={Boolean(acceptedPersonalDataConsent)}
                aria-label={t("validation.personal_data_consent_required")}
              >
                {acceptedPersonalDataConsent ? <span className="h-[8px] w-[8px] rounded-[2px] bg-white" /> : null}
              </motion.button>
              <span>
                <Trans
                  i18nKey="ru_personal_data_consent_text"
                  ns="auth"
                  components={[
                    <a href="/legal/personal-data-consent?from=signup" className="underline" aria-label="Согласие на обработку персональных данных" />,
                  ]}
                />
              </span>
            </label>
          ) : null}
          {errors.terms ? (
            <div className="mt-[4px] px-[24px] text-[12px] font-medium leading-[15px] text-red-400">
              {errors.terms.message}
            </div>
          ) : null}
          {errors.personal_data_consent ? (
            <div className="mt-[4px] px-[24px] text-[12px] font-medium leading-[15px] text-red-400">
              {errors.personal_data_consent.message}
            </div>
          ) : null}
          {errorMsg ? (
            <div className="mt-[6px] text-center text-[12px] font-medium leading-[15px] text-red-300">
              {errorMsg}
            </div>
          ) : null}
          {registrationStepError ? (
            <div
              className="mt-[6px] text-center text-[12px] font-medium leading-[15px] text-red-300"
              role="alert"
            >
              {registrationStepError}
            </div>
          ) : null}
          {renderPrimaryButton("mt-[10px]")}
          <div className="h-[20px] shrink-0" aria-hidden="true" />
        </div>
      </div>
    );

    const renderRegisterEmailStep = () => (
      <div className={clsx(
        "mx-auto flex h-full min-h-[500px] w-full max-w-[402px] flex-col px-[30px] pt-[78px] text-white",
        stableAuthBottomPaddingClassName
      )}>
        <div className="flex min-h-[96px] flex-1 items-start justify-center pt-[clamp(80px,14vh,140px)]">
          <h1 className="text-center text-[27px] font-semibold leading-[32px]">
            <span className="mb-[8px] block text-[28px] leading-[28px]" aria-hidden="true">✉️</span>
            {t("registration_flow.email.title", { defaultValue: "Check your email" })}
          </h1>
        </div>
        <div className="flex shrink-0 flex-col">
          <p className="mx-auto w-full text-center text-[15px] font-semibold leading-[20px]">
            <span className="block">
              {t("registration_flow.email.prompt", { defaultValue: "Enter the confirmation code that we sent on:" })}
            </span>
            <span className="mt-[2px] block truncate">
              {registrationEmailSentTo || registerEmailValue || t("registration_flow.email.fallback_email", { defaultValue: "your email" })}
            </span>
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            data-auth-field="registration-email-code"
            value={registrationEmailCodeInput}
            onFocus={(event) => ensureFieldVisible(event.currentTarget)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.currentTarget.blur();
              }
            }}
            enterKeyHint="done"
            onChange={(event) => {
              setRegistrationEmailCodeInput(event.target.value.replace(/\D/g, "").slice(0, 6));
              setRegistrationStepError("");
            }}
            className={clsx(stableAuthFieldClassName, "mt-[18px]")}
            placeholder={t("registration_flow.email.code_placeholder", { defaultValue: "Confirmation code" })}
            aria-label={t("registration_flow.email.code_aria", { defaultValue: "Email confirmation code" })}
          />
          {registrationStepError ? (
            <div className="mt-[5px] truncate px-[20px] text-left text-[12px] font-medium leading-[15px] text-red-300">
              {registrationStepError}
            </div>
          ) : null}
          <button
            type="button"
            disabled={resendEmailDisabled || localRegistrationWalkthroughPreview}
            onClick={() => requestRegistrationEmailCode(getValues(), { resend: true })}
            className={clsx(
              "mx-auto mt-[10px] whitespace-nowrap text-[15px] leading-[22px] underline underline-offset-2",
              resendEmailDisabled ? "text-white/34" : "text-white/55"
            )}
          >
            {resendEmailLabel}
          </button>
          {errorMsg ? (
            <div className="mt-[8px] text-center text-[12px] font-medium leading-[15px] text-red-300">{errorMsg}</div>
          ) : null}
          {renderPrimaryButton("mt-[10px]")}
        </div>
      </div>
    );

    const renderRegisterCityStep = () => (
      <div className={clsx(
        "mx-auto flex min-h-full w-full max-w-[402px] flex-col px-[30px] pt-[76px] text-white",
        stableAuthBottomPaddingClassName
      )}>
        <h1 className="text-center text-[32px] font-semibold leading-[36px]">
          {t("registration_flow.city.title", { defaultValue: "Where are you?" })}
        </h1>
        <p className="mt-[4px] text-center text-[15px] leading-[20px] text-white/85">
          {t("registration_flow.city.subtitle", { defaultValue: "Select your city" })}
        </p>
        <div className="relative mt-[22px]">
          <input
            ref={registrationCitySearchInputRef}
            value={registrationCityQuery}
            onChange={(event) => {
              setRegistrationCityQuery(event.target.value);
              setRegistrationCity("");
              setRegistrationCitySearchActive(true);
              setRegistrationStepError("");
            }}
            onFocus={(event) => {
              setRegistrationCitySearchActive(true);
              ensureFieldVisible(event.currentTarget);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.currentTarget.blur();
              }
            }}
            enterKeyHint="done"
            className="h-[50px] w-full rounded-full border border-white/50 bg-white/10 px-[18px] pr-[44px] text-[14px] font-medium text-white outline-none placeholder:text-white/70"
            placeholder={t("registration_flow.city.search", { defaultValue: "Search city" })}
            autoComplete="address-level2"
          />
          <img
            src="/assets/menu/magnifying_glass_white.svg"
            alt=""
            className="pointer-events-none absolute right-[16px] top-1/2 h-[20px] w-[20px] -translate-y-1/2"
          />
        </div>
        <div className="mt-[14px] flex flex-col gap-[6px]">
          <CitySearchFeedback options={registrationCityOptions} loading={registrationCityLoading} error={registrationCitySearchError} query={registrationCitySearchActive ? registrationCityQuery : ""} t={t} />
          {registrationCityLoading ? (
            <div className="flex h-[56px] items-center rounded-[20px] border border-white/20 bg-white/10 px-[18px] text-white/70">
              {t("registration_flow.city.loading", { defaultValue: "Loading..." })}
            </div>
          ) : (
            registrationCityOptions.map((option) => {
              const name = String(option?.name || option || "").trim();
              if (!name) return null;
              const optionCountry = String(option?.country || selectedCountry || "").trim();
              const active = registrationCity === name && selectedCountry === optionCountry
                && (option.municipality_id
                  ? selectedMunicipalityId === option.municipality_id
                  : !selectedMunicipalityId);
              return (
                <motion.button
                  key={getRegistrationCityOptionKey(option, optionCountry, name)}
                  type="button"
                  onClick={() => selectRegistrationCityOption(option)}
                  whileTap={SUBTLE_PRESS_TAP}
                  className={clsx(
                    "flex min-h-[56px] w-full items-center justify-between rounded-[20px] border px-[18px] text-left text-[18px] font-semibold",
                    active ? "border-white/70 bg-white/20" : "border-white/25 bg-white/10"
                  )}
                >
                  <span className="truncate"><span className="block truncate">{getRegistrationCityDisplayName(option.display_name || name)}</span>
</span>
                  <span className={clsx("ml-[12px] h-[16px] w-[16px] rounded-full border", active ? "border-white bg-white" : "border-white/45")} />
                </motion.button>
              );
            })
          )}
        </div>
        {registrationStepError ? (
          <div className="mt-[8px] text-center text-[12px] font-medium leading-[15px] text-red-300">
            {registrationStepError}
          </div>
        ) : null}
        <div className="sticky bottom-[16px] mt-[16px] rounded-full shadow-[0_16px_36px_rgba(0,0,0,0.35)]">
          {renderPrimaryButton("mt-0")}
        </div>
      </div>
    );

    const renderRegisterPhotoStep = () => (
      <div className={clsx(
        "mx-auto flex h-full min-h-[620px] w-full max-w-[402px] flex-col items-center px-[30px] pt-[78px] text-white",
        stableAuthBottomPaddingClassName
      )}>
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center py-[14px]">
          <h1 className="text-center text-[32px] font-semibold leading-[36px]">
            {t("registration_flow.photo.title", { defaultValue: "Add a profile photo" })}
          </h1>
        <input
          ref={registrationPhotoInputRef}
          type="file"
          accept="image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0] || null;
            event.target.value = "";
            if (file && file.size > 10 * 1024 * 1024) {
              setRegistrationStepError(t("registration_flow.photo.errors.too_large", { defaultValue: "Please choose a photo under 10 MB." }));
              return;
            }
            setRegistrationPhotoChecking(false);
            setRegistrationPhotoFile(file);
            setRegistrationStepError("");
          }}
        />
        <motion.button
          type="button"
          onClick={() => {
            if (!registrationPhotoPreview) registrationPhotoInputRef.current?.click();
          }}
          onPointerDown={handleRegistrationPhotoPointerDown}
          onPointerMove={handleRegistrationPhotoPointerMove}
          onPointerUp={handleRegistrationPhotoPointerEnd}
          onPointerCancel={handleRegistrationPhotoPointerEnd}
          onWheel={handleRegistrationPhotoWheel}
          whileTap={registrationPhotoPreview ? undefined : SUBTLE_PRESS_TAP}
          className={clsx(
            "relative mt-[24px] flex h-[270px] w-[270px] shrink-0 items-center justify-center overflow-hidden rounded-full text-center",
            registrationPhotoPreview
              ? clsx(
                  "touch-none border-[4px] border-[#4b7cff] bg-black/10 shadow-[0_0_28px_rgba(62,111,251,0.35)]",
                  registrationPhotoDragging ? "cursor-grabbing" : "cursor-grab"
                )
              : "border border-dashed border-white/45 bg-white/[0.03]"
          )}
        >
          {registrationPhotoPreview ? (
            <>
              <div
                className="pointer-events-none absolute left-0 top-0"
                style={{
                  width: `${registrationPhotoImageSize.w * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
                  height: `${registrationPhotoImageSize.h * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
                  transform: `translate3d(${registrationPhotoPos.x}px, ${registrationPhotoPos.y}px, 0)`,
                  transformOrigin: "top left",
                  willChange: "transform",
                }}
              >
                <div
                  style={{
                    width: `${registrationPhotoImageSize.w * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
                    height: `${registrationPhotoImageSize.h * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
                    transform: `scale(${registrationPhotoZoom})`,
                    transformOrigin: "top left",
                    willChange: "transform",
                  }}
                >
                  <img
                    src={registrationPhotoPreview}
                    alt=""
                    className="block max-w-none select-none"
                    draggable="false"
                    style={{
                      width: `${registrationPhotoImageSize.w * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
                      height: `${registrationPhotoImageSize.h * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
                      userSelect: "none",
                      pointerEvents: "none",
                    }}
                  />
                </div>
              </div>
              <span className="pointer-events-none absolute inset-[38px] rounded-full border border-dashed border-white/55" />
            </>
          ) : (
            <span className="flex w-[170px] flex-col items-center">
              <img src="/assets/menu/upload_photo_Icon.svg" alt="" className="mb-[18px] h-[42px] w-[42px] opacity-90" />
              <span className="text-[18px] font-semibold">
                {t("registration_flow.photo.upload", { defaultValue: "Upload a photo" })}
              </span>
              <span className="mt-[8px] text-[13px] leading-[18px] text-white/62">
                {t("registration_flow.photo.formats", { defaultValue: "JPG, PNG or HEIC. Max 10 MB." })}
              </span>
            </span>
          )}
        </motion.button>
        {registrationPhotoPreview ? (
          <div className="mt-[12px] flex flex-col items-center gap-[8px] text-[14px] text-white/72">
            <span>{t("registration_flow.photo.adjust", { defaultValue: "Drag to adjust" })}</span>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => registrationPhotoInputRef.current?.click()}
              className="h-[30px] rounded-full border border-white/20 bg-white/10 px-[18px] text-[12px] font-semibold"
            >
              {t("registration_flow.photo.change", { defaultValue: "Change photo" })}
            </button>
          </div>
        ) : (
          <p className="mt-[14px] text-center text-[13px] leading-[18px] text-white/68">
            {t("registration_flow.photo.hint", { defaultValue: "Use a recent photo of only you with your whole face visible in good lighting." })}
          </p>
        )}
        {registrationStepError ? (
          <div className="mt-[8px] text-center text-[12px] font-semibold text-red-300">{registrationStepError}</div>
        ) : null}
        </div>
        <div className="w-full shrink-0">{renderPrimaryButton("mt-[10px]")}</div>
      </div>
    );

    return (
      <div
        className={clsx(
          "relative h-full w-full overflow-visible text-white",
          stableAuthSurfaceClassName,
          registrationStep === "form"
            ? "min-h-[630px]"
            : registrationStep === "photo"
              ? "min-h-[620px]"
              : "min-h-[500px]"
        )}
        style={stableAuthSurfaceStyle}
        data-testid="stable-mobile-web-register"
      >
        {renderBackButton()}
        {registrationStep === "form" ? (
          <AuthLanguageToggle safeAreaOffset={false} />
        ) : null}
        {registrationStep === "form" && renderRegisterFormStep()}
        {registrationStep === "email" && renderRegisterEmailStep()}
        {registrationStep === "city" && renderRegisterCityStep()}
        {registrationStep === "photo" && renderRegisterPhotoStep()}
        {renderMobileSignupFinalIntro()}
      </div>
    );
  };

  const renderStableMobileOnboardingView = () => {
    const isCityStep = registrationStep === "city";
    const isPhotoStep = registrationStep === "photo";
    if (!isCityStep && !isPhotoStep) return null;

    const canContinue = localRegistrationWalkthroughPreview || (isCityStep
      ? !isSubmitting && Boolean(registrationCity.trim())
      : !isSubmitting && Boolean(registrationPhotoFile));
    const actionLabel = isCityStep || localRegistrationOnboardingFlowPreview
      ? t("buttons.continue")
      : t("buttons.create_account");
    const progressLabel = registrationPhotoChecking
      ? t("registration_flow.photo.checking", { defaultValue: "Checking photo..." })
      : t("buttons.creating_account", "Creating account...");

    const goBack = () => {
      const stepOrder = registrationStepOrder;
      const currentIndex = stepOrder.indexOf(registrationStep);
      if (currentIndex === 0) {
        setTab("login");
        resetMobileRegistrationFlow();
        return;
      }
      setRegistrationStep(stepOrder[currentIndex - 1]);
      setRegistrationStepError("");
    };

    const renderHeader = ({ title, subtitle, withSearch = false }) => (
      <div
        className={clsx(
          "absolute inset-x-0 top-0 z-30",
          withSearch &&
            "rounded-b-[45px] bg-[linear-gradient(180deg,rgba(255,255,255,0.30),rgba(77,117,230,0.28)_52%,rgba(0,0,0,0.34))] shadow-[0_18px_34px_rgba(0,0,0,0.12)] backdrop-blur-[64px]",
          withSearch
            ? "h-[calc(var(--vg-safe-area-top)+173px)]"
            : "h-[calc(var(--vg-safe-area-top)+92px)]"
        )}
      >
        {withSearch ? (
          <div className="pointer-events-none absolute left-1/2 top-[-5px] h-[13px] w-[252px] -translate-x-1/2 rounded-full bg-white/45 blur-[38px]" />
        ) : null}
        <button
          type="button"
          onClick={goBack}
          className="absolute left-[24px] top-[calc(var(--vg-safe-area-top)+12px)] z-20 flex h-[40px] w-[40px] items-center justify-center"
          aria-label={t("buttons.go_back")}
        >
          <img src="/assets/landing/back_arrow.svg" alt="" className="h-[24px] w-[24px] opacity-90" />
        </button>
        <h1 className="absolute inset-x-[60px] top-[calc(var(--vg-safe-area-top)+12px)] flex h-[40px] items-center justify-center whitespace-nowrap text-center text-[clamp(22px,6.9vw,27px)] font-semibold leading-[32px] text-white">
          {title}
        </h1>
        {subtitle ? (
          <p className="absolute inset-x-[50px] top-[calc(var(--vg-safe-area-top)+52px)] text-center text-[15px] font-light leading-[18px] text-white/80">
            {subtitle}
          </p>
        ) : null}
        {withSearch ? (
          <div
            className="absolute left-[30px] right-[30px] top-[calc(var(--vg-safe-area-top)+91px)] flex h-[50px] items-center rounded-full border border-white/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.10))] px-[16px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07),0_10px_20px_rgba(0,0,0,0.12)] backdrop-blur-[20px]"
          >
            <input
              ref={registrationCitySearchInputRef}
              value={registrationCityQuery}
              onChange={(event) => {
                setRegistrationCityQuery(event.target.value);
                setRegistrationCity("");
                setRegistrationCitySearchActive(true);
                setRegistrationStepError("");
              }}
              onFocus={(event) => {
                setRegistrationCitySearchActive(true);
                ensureFieldVisible(event.currentTarget);
              }}
              enterKeyHint="done"
              autoComplete="address-level2"
              placeholder={t("registration_flow.city.search", { defaultValue: "Search city" })}
              className="h-full min-w-0 flex-1 bg-transparent text-[13px] font-medium text-white outline-none placeholder:text-white/80"
            />
            <img src="/assets/menu/magnifying_glass_white.svg" alt="" className="h-[20px] w-[20px] shrink-0" />
          </div>
        ) : null}
      </div>
    );

    return (
      <div
        className="relative h-full min-h-0 w-full overflow-hidden text-white"
        data-testid={`mobile-registration-${registrationStep}`}
      >
        {isCityStep
          ? renderHeader({
              title: t("registration_flow.city.title", { defaultValue: "Where are you?" }),
              subtitle: t("registration_flow.city.subtitle", { defaultValue: "Select your city" }),
              withSearch: true,
            })
          : renderHeader({
              title: t("registration_flow.photo.title", { defaultValue: "Add a profile photo" }),
            })}

        {isCityStep ? (
          <div className="absolute inset-x-0 bottom-0 top-[calc(var(--vg-safe-area-top)+173px)] overflow-y-auto px-[29px] pb-[calc(var(--vg-safe-area-bottom)+102px)] pt-[35px] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex w-full flex-col gap-[5px]">
              <CitySearchFeedback options={registrationCityOptions} loading={registrationCityLoading} error={registrationCitySearchError} query={registrationCitySearchActive ? registrationCityQuery : ""} t={t} />
              {registrationCityLoading ? (
                <div className="flex h-[70px] w-full items-center rounded-[24px] border border-white/20 bg-white/10 px-[20px] text-[20px] font-semibold text-white/70">
                  {t("registration_flow.city.loading", { defaultValue: "Loading..." })}
                </div>
              ) : (
                registrationCityOptions.map((option) => {
                  const name = String(option?.name || option || "").trim();
                  if (!name) return null;
                  const optionCountry = String(option?.country || selectedCountry || "").trim();
                  const active = registrationCity === name && selectedCountry === optionCountry
                    && (option.municipality_id
                      ? selectedMunicipalityId === option.municipality_id
                      : !selectedMunicipalityId);
                  return (
                    <motion.button
                      key={getRegistrationCityOptionKey(option, optionCountry, name)}
                      type="button"
                      onClick={() => selectRegistrationCityOption(option)}
                      whileTap={SUBTLE_PRESS_TAP}
                      className="relative flex h-[70px] w-full shrink-0 items-center justify-between rounded-[24px] border border-white/25 bg-[linear-gradient(90deg,rgba(153,153,153,0.15),rgba(255,255,255,0.15))] px-[20px] text-left text-white"
                    >
                      <span className="min-w-0 truncate pr-[20px] text-[24px] font-semibold leading-[30px]">
                        <span className="block truncate">{getRegistrationCityDisplayName(option.display_name || name)}</span>

                      </span>
                      <span className="relative h-[20px] w-[20px] shrink-0 rounded-full border border-white/45">
                        {active ? <span className="absolute inset-[3px] rounded-full bg-white" /> : null}
                      </span>
                    </motion.button>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          <div className="absolute inset-x-[30px] bottom-[calc(var(--vg-safe-area-bottom)+86px)] top-[calc(var(--vg-safe-area-top)+104px)] flex min-h-0 flex-col items-center justify-center">
            <input
              ref={registrationPhotoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0] || null;
                event.target.value = "";
                if (file && file.size > 10 * 1024 * 1024) {
                  setRegistrationStepError(t("registration_flow.photo.errors.too_large", { defaultValue: "Please choose a photo under 10 MB." }));
                  return;
                }
                setRegistrationPhotoChecking(false);
                setRegistrationPhotoFile(file);
                setRegistrationStepError("");
              }}
            />
            <motion.button
              type="button"
              onClick={() => {
                if (!registrationPhotoPreview) registrationPhotoInputRef.current?.click();
              }}
              onPointerDown={handleRegistrationPhotoPointerDown}
              onPointerMove={handleRegistrationPhotoPointerMove}
              onPointerUp={handleRegistrationPhotoPointerEnd}
              onPointerCancel={handleRegistrationPhotoPointerEnd}
              onWheel={handleRegistrationPhotoWheel}
              whileTap={registrationPhotoPreview ? undefined : SUBTLE_PRESS_TAP}
              className={clsx(
                "relative flex h-[270px] w-[270px] shrink-0 items-center justify-center overflow-hidden rounded-full text-center",
                registrationPhotoPreview
                  ? clsx(
                      "touch-none border-[4px] border-[#4b7cff] bg-black/10 shadow-[0_0_28px_rgba(62,111,251,0.35)]",
                      registrationPhotoDragging ? "cursor-grabbing" : "cursor-grab"
                    )
                  : "border border-dashed border-white/45 bg-white/[0.03] backdrop-blur-[12px]"
              )}
            >
              {registrationPhotoPreview ? (
                <>
                  <div
                    className="pointer-events-none absolute left-0 top-0"
                    style={{
                      width: `${registrationPhotoImageSize.w * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
                      height: `${registrationPhotoImageSize.h * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
                      transform: `translate3d(${registrationPhotoPos.x}px, ${registrationPhotoPos.y}px, 0)`,
                      transformOrigin: "top left",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        transform: `scale(${registrationPhotoZoom})`,
                        transformOrigin: "top left",
                      }}
                    >
                      <img
                        src={registrationPhotoPreview}
                        alt=""
                        draggable="false"
                        className="block h-full w-full max-w-none select-none"
                      />
                    </div>
                  </div>
                  <span className="pointer-events-none absolute inset-[38px] rounded-full border border-dashed border-white/55" />
                </>
              ) : (
                <span className="flex w-[180px] flex-col items-center">
                  <img src="/assets/menu/upload_photo_Icon.svg" alt="" className="mb-[18px] h-[42px] w-[42px] opacity-90" />
                  <span className="text-[18px] font-semibold">
                    {t("registration_flow.photo.upload", { defaultValue: "Upload a photo" })}
                  </span>
                  <span className="mt-[8px] text-[13px] leading-[18px] text-white/62">
                    {t("registration_flow.photo.formats", { defaultValue: "JPG, PNG or HEIC. Max 10 MB." })}
                  </span>
                </span>
              )}
            </motion.button>
            {registrationPhotoPreview ? (
              <div className="mt-[12px] flex flex-col items-center gap-[8px] text-[14px] text-white/72">
                <span>{t("registration_flow.photo.adjust", { defaultValue: "Drag to adjust" })}</span>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => registrationPhotoInputRef.current?.click()}
                  className="h-[30px] rounded-full border border-white/20 bg-white/10 px-[18px] text-[12px] font-semibold"
                >
                  {t("registration_flow.photo.change", { defaultValue: "Change photo" })}
                </button>
              </div>
            ) : (
              <p className="mt-[16px] max-w-[322px] text-center text-[13px] leading-[19px] text-white/68">
                {t("registration_flow.photo.hint", { defaultValue: "Use a recent photo of only you with your whole face visible in good lighting." })}
              </p>
            )}
          </div>
        )}

        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-40 h-[calc(var(--vg-safe-area-bottom)+150px)] bg-[linear-gradient(0deg,#050812_28%,rgba(5,8,18,0)_100%)]" />
        {registrationStepError ? (
          <div className="absolute inset-x-[30px] bottom-[calc(var(--vg-safe-area-bottom)+78px)] z-50 text-center text-[12px] font-semibold text-red-300">
            {registrationStepError}
          </div>
        ) : null}
        {errorMsg ? (
          <div className="absolute inset-x-[30px] bottom-[calc(var(--vg-safe-area-bottom)+78px)] z-50 text-center text-[12px] font-semibold text-red-300">
            {errorMsg}
          </div>
        ) : null}
        <motion.button
          type="submit"
          disabled={!canContinue}
          whileTap={canContinue ? PRESS_TAP : undefined}
          className={clsx(
            "absolute inset-x-[30px] bottom-[calc(var(--vg-safe-area-bottom)+16px)] z-50 flex h-[55px] items-center justify-center rounded-full border border-white/20 px-5 text-[15px] font-semibold text-white transition-all",
            canContinue ? "bg-[#3e6ffb]" : "bg-[#2e58d0]/70 text-white/50"
          )}
        >
          {isSubmitting ? progressLabel : actionLabel}
        </motion.button>
        {renderMobileSignupFinalIntro()}
      </div>
    );
  };

  const renderRegistrationAgeView = () => {
    const validAge = isValidRegistrationAge(registrationAge);
    const goBack = () => {
      const index = registrationStepOrder.indexOf("age");
      setRegistrationStep(registrationStepOrder[Math.max(0, index - 1)]);
      setRegistrationStepError("");
    };
    const goForward = () => {
      if (!validAge && !localRegistrationWalkthroughPreview) {
        setRegistrationStepError(t("registration_flow.age.error", { defaultValue: "Choose an age from 18 to 60." }));
        return;
      }
      setRegistrationStepError("");
      setRegistrationStep("photo");
    };
    return (
      <div className="relative h-full min-h-0 w-full overflow-hidden text-white" data-testid="mobile-registration-age">
        <button
          type="button"
          onClick={goBack}
          className="absolute left-[24px] top-[calc(var(--vg-safe-area-top)+12px)] z-30 flex h-[40px] w-[40px] items-center justify-center"
          aria-label={t("buttons.go_back_plain", { defaultValue: "Go back" })}
        >
          <img src="/assets/landing/back_arrow.svg" alt="" className="h-[24px] w-[24px] opacity-90" />
        </button>
        <h1 className="absolute inset-x-[44px] top-[calc(var(--vg-safe-area-top)+12px)] flex h-[40px] items-center justify-center whitespace-nowrap text-center text-[32px] font-semibold leading-[35px]">
          {t("registration_flow.age.title", { defaultValue: "How old are you?" })}
        </h1>
        <div className="absolute inset-x-[30px] bottom-[calc(var(--vg-safe-area-bottom)+86px)] top-[calc(var(--vg-safe-area-top)+104px)] flex items-center justify-center">
          {isDesktop ? (
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={registrationAge}
              onChange={(event) => {
                setRegistrationAge(event.target.value.replace(/\D/g, "").slice(0, 2));
                setRegistrationStepError("");
              }}
              placeholder="—"
              aria-label={t("registration_flow.age.input", { defaultValue: "Age in years" })}
              maxLength={2}
              className="h-[90px] w-[180px] rounded-[28px] border border-white/30 bg-white/10 text-center text-[52px] font-semibold text-white outline-none placeholder:text-white/45 focus:border-white/75"
            />
          ) : (
            <RegistrationAgeWheel
              value={registrationAge}
              onChange={(age) => { setRegistrationAge(age); setRegistrationStepError(""); }}
              label={t("registration_flow.age.input", { defaultValue: "Age in years" })}
            />
          )}
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[calc(var(--vg-safe-area-bottom)+150px)] bg-[linear-gradient(0deg,#050812_28%,rgba(5,8,18,0)_100%)]" />
        {registrationStepError ? (
          <p className="absolute inset-x-[30px] bottom-[calc(var(--vg-safe-area-bottom)+78px)] z-40 text-center text-[12px] font-semibold text-red-300">{registrationStepError}</p>
        ) : null}
        <motion.button
          type="button"
          onClick={goForward}
          disabled={!validAge && !localRegistrationWalkthroughPreview}
          whileTap={validAge || localRegistrationWalkthroughPreview ? PRESS_TAP : undefined}
          className={clsx(
            "absolute bottom-[calc(var(--vg-safe-area-bottom)+16px)] z-50 flex h-[55px] items-center justify-center rounded-full border border-white/20 px-5 text-[15px] font-semibold text-white transition-all",
            isDesktop ? "left-1/2 w-[342px] max-w-[calc(100%-60px)] -translate-x-1/2" : "inset-x-[30px]",
            validAge || localRegistrationWalkthroughPreview ? "bg-[#3e6ffb]" : "bg-[#2e58d0]/70 text-white/50"
          )}
        >
          {t("buttons.continue")}
        </motion.button>
      </div>
    );
  };

  const renderMobileRegisterView = () => {
    if (registrationStep === "age") return renderRegistrationAgeView();
    // The identity and email steps share one responsive canvas so their text,
    // fields and action button keep the same physical size on wider phones.
    // City and photo keep their dedicated full-screen onboarding layouts.
    if (useStableMobileAuth && (registrationStep === "form" || registrationStep === "email")) {
      return renderStableMobileWebRegisterView();
    }
    if (useStableMobileAuth && (registrationStep === "city" || registrationStep === "photo")) {
      return renderStableMobileOnboardingView();
    }
    const { width: availableW, height: availableH } = getAuthStageAvailableSize();
    const registerScale = getMobileAuthScale(availableW, availableH);
	    const codeLetters = (registrationCode || "---").padEnd(3, "-").slice(0, 3).split("");
	    const canBrowsePreviousRegistrationCode =
	      registrationCodeHistory.length > 1 && registrationCodeHistoryIndex > 0;
	    const canBrowseNextRegistrationCode =
	      registrationCodeHistory.length > 1 &&
	      registrationCodeHistoryIndex >= 0 &&
	      registrationCodeHistoryIndex < registrationCodeHistory.length - 1;
	    const { createAccountTop } = getMobileActionLayout(availableH, registerScale);
	    const registerFieldNames = isSocialRegistration
	      ? ["username"]
	      : ["username", "email", "password1", "password2"];
	    const hasRegisterFieldErrors = registerFieldNames.some((name) => Boolean(errors[name]));
	    const registerErrorSlot = 22;
	    const registerFieldHeight = 55;
	    const registerFieldGap = hasRegisterFieldErrors ? 14 : 10;
	    const russianConsentTopOffset = 64;
	    const russianLegalConsentReserve = 78;
	    const termsTop = createAccountTop - (
	      (isDesktop
	        ? (errors.terms ? 91 : 89)
	        : (errors.terms ? 57 : 55)) +
	      (isRussianLegalBundle ? russianLegalConsentReserve : 0)
	    );
	    const registerFieldTops = {};
	    let nextRegisterFieldBottom = termsTop - registerFieldGap;
	    [...registerFieldNames].reverse().forEach((fieldName) => {
	      const fieldTop =
	        nextRegisterFieldBottom -
	        registerFieldHeight -
	        (errors[fieldName] ? registerErrorSlot : 0);
	      registerFieldTops[fieldName] = fieldTop;
	      nextRegisterFieldBottom = fieldTop - registerFieldGap;
	    });
	    const getRegisterFieldTop = (fieldName) => {
	      return registerFieldTops[fieldName] ?? 0;
	    };
	    const registerUsernameTop = getRegisterFieldTop("username");
	    const codeButtonTop = Math.min(326, registerUsernameTop - 52);
	    const codeCardsTop = codeButtonTop - 135;
	    const codeTitleTop = codeCardsTop - 59;
	    const codeNavTop = codeCardsTop + 40;
	    const codeErrorTop = codeButtonTop + 44;
	    const emailCodeInputTop = Math.min(619, createAccountTop - (registrationStepError ? 156 : 126));
	    const emailResendTop = Math.min(
	      emailCodeInputTop + (registrationStepError ? 94 : 78),
	      createAccountTop - 54
	    );
	    const emailCopyTop = Math.min(564, emailCodeInputTop - 75);
	    const emailTitleTop = Math.max(116, Math.min(246, emailCopyTop - 276));
	    const photoFrameTop = Math.min(238, createAccountTop - (registrationPhotoPreview ? 398 : 384));
	    const photoTitleTop = Math.max(92, Math.min(126, photoFrameTop - 112));
	    const photoHintTop = registrationPhotoPreview
	      ? Math.min(526, photoFrameTop + 288)
	      : Math.min(548, createAccountTop - 142);
	    const photoErrorTop = Math.min(616, createAccountTop - 30);
	    const canSubmitRegisterForm = canSubmitRegistrationIdentityStep({
	      isSubmitting,
	      registrationCodeLoading,
	      registrationCode,
	      acceptedTerms,
	      isRussianLegalBundle,
	      acceptedPersonalDataConsent,
	    });
		    const canSubmitRegister = localRegistrationWalkthroughPreview || (
		      registrationStep === "form"
		        ? canSubmitRegisterForm
		        : registrationStep === "email"
		          ? !isSubmitting && !registrationEmailSending && /^\d{6}$/.test(registrationEmailCodeInput.replace(/\D/g, ""))
		        : registrationStep === "city"
		          ? !isSubmitting && Boolean(registrationCity.trim())
		          : !isSubmitting && Boolean(registrationPhotoFile)
		    );
	    const codeLimitReached = registrationCodeRemaining === 0 && Boolean(registrationCode);
	    const codeGenerateDisabled =
	      registrationCodeLoading ||
	      isSubmitting ||
	      codeLimitReached;
	    const codeButtonLabel = registrationCodeLoading
	      ? t("buttons.generating")
	      : registrationCodeFlashLabel
	        ? registrationCodeFlashLabel
	        : codeLimitReached
	          ? t("buttons.limit_reached")
	          : t("buttons.generate_new");
	    const codeButtonTitle = registrationCodeResetAt
	      ? `Limit ${registrationCodeLimit} per 24h. Remaining: ${registrationCodeRemaining ?? "unknown"}. Resets at ${registrationCodeResetAt}.`
	      : `Limit ${registrationCodeLimit} per 24h. Remaining: ${registrationCodeRemaining ?? "unknown"}.`;
	    const primaryButtonLabel = localRegistrationWalkthroughPreview
	      ? t("buttons.continue")
	      :
	      registrationStep === "form" && isSocialRegistration
	          ? t("buttons.continue")
	          : registrationStep === "email"
	          ? t("registration_flow.email.confirm", { defaultValue: "Confirm email" })
	          : registrationStep === "city"
		          ? t("buttons.continue")
		          : localRegistrationOnboardingFlowPreview
		            ? t("buttons.continue")
		            : t("buttons.create_account");
	    const primaryButtonProgressLabel = registrationStep === "email"
	      ? t("registration_flow.email.confirming", { defaultValue: "Confirming..." })
	      : registrationPhotoChecking
	      ? t("registration_flow.photo.checking", { defaultValue: "Checking photo..." })
	      : t("buttons.creating_account", "Creating account...");
	    const resendEmailDisabled = registrationEmailSending || registrationEmailResendCooldown > 0;
	    const resendEmailLabel = registrationEmailSending
	      ? t("registration_flow.email.sending", { defaultValue: "Sending..." })
	      : registrationEmailResendStatus
	        ? registrationEmailResendStatus
	        : registrationEmailResendCooldown > 0
	        ? t("registration_flow.email.resend_in", {
              seconds: registrationEmailResendCooldown,
              defaultValue: `Resend in ${registrationEmailResendCooldown}s`,
            })
	        : t("registration_flow.email.resend_code", { defaultValue: "Resend code" });

	    const fieldClassName =
	      "h-[55px] w-full rounded-full border border-white/25 bg-white/10 px-[20px] pr-12 text-[15px] font-medium leading-[16px] text-white placeholder:text-white/50 outline-none backdrop-blur-[18px]";
    const citySearchShellClassName =
      "absolute left-[30px] top-[140px] z-30 flex h-[50px] w-[342px] items-center justify-center overflow-hidden rounded-full border border-white/80 px-[16px] py-[6px] text-left backdrop-blur-[20px]";
    const citySearchShellStyle = {
      backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.10) 100%)",
      boxShadow:
        "inset 0 0 0 1px rgba(255,255,255,0.07), 0 1px 0 rgba(0,0,0,0.05), 0 4px 4px rgba(0,0,0,0.05), 0 10px 10px rgba(0,0,0,0.10)",
    };
    const cityOptionClassName =
      "relative h-[70px] w-[343px] shrink-0 rounded-[24px] border border-white/25 bg-[linear-gradient(90deg,rgba(153,153,153,0.15),rgba(255,255,255,0.15))] text-left text-white";

    if (isDesktop && registrationStep === "city") {
      const stepOrder = registrationStepOrder;
      const currentIndex = stepOrder.indexOf(registrationStep);

      return (
        <div
          className="relative flex h-full min-h-0 w-full flex-col overflow-hidden text-white"
          data-testid="desktop-register-city"
        >
          <header className="relative w-full shrink-0 px-[clamp(32px,5vw,80px)] pb-[28px] pt-[38px]">
            <motion.button
              type="button"
              onClick={() => {
                if (currentIndex === 0) setTab("login");
                else setRegistrationStep(stepOrder[currentIndex - 1]);
                setRegistrationStepError("");
              }}
              whileHover={PRESS_HOVER}
              whileTap={SUBTLE_PRESS_TAP}
              transition={PRESS_TRANSITION}
              className="absolute left-[clamp(32px,5vw,80px)] top-[36px] flex h-[44px] w-[44px] items-center justify-center rounded-full border border-white/15 bg-white/[0.06] transition-colors hover:bg-white/[0.12]"
              aria-label={t("buttons.go_back")}
            >
              <img src="/assets/landing/back_arrow.svg" alt="" className="h-[22px] w-[22px] opacity-90" />
            </motion.button>

            <div className="mx-auto max-w-[760px] text-center">
              <h1 className="text-[40px] font-semibold leading-[44px]">
                {t("registration_flow.city.title", { defaultValue: "Where are you?" })}
              </h1>
              <p className="mt-[6px] text-[16px] leading-[22px] text-white/75">
                {t("registration_flow.city.subtitle", { defaultValue: "Select your city" })}
              </p>
            </div>
          </header>

          <div className="min-h-0 flex-1 px-[clamp(32px,6vw,96px)] pb-[30px]">
            <div className="mx-auto flex h-full min-h-0 w-full max-w-[860px] flex-col">
              <div className="relative mx-auto w-full max-w-[680px]">
                <input
                  ref={registrationCitySearchInputRef}
                  value={registrationCityQuery}
                  onChange={(event) => {
                    setRegistrationCityQuery(event.target.value);
                    setRegistrationCity("");
                    setRegistrationCitySearchActive(true);
                    setRegistrationStepError("");
                  }}
                  onFocus={(event) => {
                    setRegistrationCitySearchActive(true);
                    ensureFieldVisible(event.currentTarget);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      event.currentTarget.blur();
                    }
                  }}
                  enterKeyHint="done"
                  className="h-[56px] w-full rounded-full border border-white/35 bg-white/[0.09] px-[22px] pr-[54px] text-[15px] font-medium text-white outline-none transition-colors placeholder:text-white/55 hover:bg-white/[0.12] focus:border-white/70 focus:bg-white/[0.13]"
                  placeholder={t("registration_flow.city.search", { defaultValue: "Search city" })}
                  autoComplete="address-level2"
                />
                <img
                  src="/assets/menu/magnifying_glass_white.svg"
                  alt=""
                  className="pointer-events-none absolute right-[20px] top-1/2 h-[21px] w-[21px] -translate-y-1/2 opacity-85"
                />
              </div>

              <div className="mt-[24px] grid min-h-0 flex-1 grid-cols-2 content-start gap-[10px] overflow-y-auto pr-[4px] [scrollbar-color:rgba(255,255,255,0.22)_transparent] [scrollbar-width:thin]">
                <CitySearchFeedback options={registrationCityOptions} loading={registrationCityLoading} error={registrationCitySearchError} query={registrationCitySearchActive ? registrationCityQuery : ""} t={t} />
                {registrationCityLoading ? (
                  <div className="col-span-2 flex h-[64px] items-center justify-center rounded-[22px] border border-white/20 bg-white/[0.08] text-white/65">
                    {t("registration_flow.city.loading", { defaultValue: "Loading..." })}
                  </div>
                ) : (
                  registrationCityOptions.map((option) => {
                    const name = String(option?.name || option || "").trim();
                    if (!name) return null;
                    const optionCountry = String(option?.country || selectedCountry || "").trim();
                    const active = registrationCity === name && selectedCountry === optionCountry
                      && (option.municipality_id
                        ? selectedMunicipalityId === option.municipality_id
                        : !selectedMunicipalityId);
                    return (
                      <motion.button
                        key={getRegistrationCityOptionKey(option, optionCountry, name)}
                        type="button"
                        onClick={() => selectRegistrationCityOption(option)}
                        whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.16)" }}
                        whileTap={SUBTLE_PRESS_TAP}
                        transition={PRESS_TRANSITION}
                        className={clsx(
                          "flex h-[64px] min-w-0 items-center justify-between rounded-[22px] border px-[22px] text-left text-[19px] font-semibold transition-colors",
                          active
                            ? "border-white/75 bg-white/[0.20]"
                            : "border-white/22 bg-white/[0.08] hover:border-white/38 hover:bg-white/[0.12]"
                        )}
                      >
                        <span className="truncate"><span className="block truncate">{getRegistrationCityDisplayName(option.display_name || name)}</span>
</span>
                        <span className="ml-[16px] flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full border border-white/45">
                          {active ? <span className="h-[12px] w-[12px] rounded-full bg-white" /> : null}
                        </span>
                      </motion.button>
                    );
                  })
                )}
              </div>

              {registrationStepError ? (
                <div className="mt-[10px] text-center text-[13px] font-semibold leading-[18px] text-red-300">
                  {registrationStepError}
                </div>
              ) : null}
              {errorMsg ? (
                <div className="mt-[10px] text-center text-[13px] font-semibold leading-[18px] text-red-300">
                  {errorMsg}
                </div>
              ) : null}

              <motion.button
                type="submit"
                disabled={!canSubmitRegister}
                whileHover={canSubmitRegister ? PRESS_HOVER : undefined}
                whileTap={canSubmitRegister ? PRESS_TAP : undefined}
                transition={PRESS_TRANSITION}
                className={clsx(
                  "mx-auto mt-[18px] flex h-[56px] w-full max-w-[680px] shrink-0 items-center justify-center rounded-full border border-white/20 px-5 text-[15px] font-semibold text-white shadow-[0_14px_34px_rgba(0,0,0,0.18)] transition-all",
                  canSubmitRegister ? "bg-[#3e6ffb]" : "bg-[#2e58d0]/70 text-white/50"
                )}
              >
                {isSubmitting ? primaryButtonProgressLabel : primaryButtonLabel}
              </motion.button>
            </div>
          </div>
          {renderMobileSignupFinalIntro()}
        </div>
      );
    }

    return (
      <div
        className="relative h-full w-full overflow-hidden bg-[#050812]"
        data-figma-node="305:1949"
        style={{
          width: "100%",
          maxWidth: "none",
          ...mobileAuthStageSurfaceStyle,
          // Preserve the email screen's absolute design coordinates while the
          // outer scroller moves the code above the keyboard/autofill edge.
          height: useStableNativeAuth && registrationStep === "email" ? availableH : undefined,
          minHeight: useStableNativeAuth && registrationStep === "email" ? availableH : undefined,
        }}
      >
        <div
          className="absolute left-1/2 top-0 h-[874px] w-[402px] origin-top"
          style={{
            transform: `translateX(-50%) translateY(${useStableNativeAuth && registrationStep === "email" ? 0 : mobileKeyboardShiftY}px) scale(${registerScale})`,
            height: `${AUTH_DESIGN_HEIGHT}px`,
            width: `${AUTH_DESIGN_WIDTH}px`,
          }}
        >
		          <motion.button
		            type="button"
		            onClick={() => {
		      if (registrationStepOrder.indexOf(registrationStep) > 0) {
	                const stepOrder = registrationStepOrder;
			        const currentIndex = stepOrder.indexOf(registrationStep);
			        setRegistrationStep(stepOrder[Math.max(0, currentIndex - 1)] || "form");
		                setRegistrationStepError("");
		                return;
		              }
	              setTab("login");
	              resetMobileRegistrationFlow();
	              reset();
	              setErrorMsg("");
	              setRegisterMode("classic");
	              setGoogleToken(null);
	              setAppleToken(null);
	            }}
	            whileTap={SUBTLE_PRESS_TAP}
	            transition={PRESS_TRANSITION}
	            className="absolute left-[24px] top-[24px] z-20 flex h-[24px] w-[24px] items-center justify-center"
	            aria-label={t("buttons.go_back")}
	          >
	            <img src="/assets/landing/back_arrow.svg" alt="" className="h-[24px] w-[24px] opacity-90" />
	          </motion.button>

            {registrationStep === "form" ? <AuthLanguageToggle /> : null}

	          <AnimatePresence mode="wait">
	            {registrationStep === "form" && (
	              <motion.div
	                key="register-step-form"
	                className="absolute inset-0"
	                initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
	                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
	                exit={{ opacity: 0, y: -12, filter: "blur(6px)" }}
	                transition={mobileAuthTransition}
	              >
		                <div
		                  className="absolute left-1/2 flex h-[32px] w-[342px] -translate-x-1/2 items-center justify-center whitespace-nowrap text-center text-[27px] font-semibold leading-[32px] text-white"
		                  style={{ top: `${codeTitleTop}px` }}
		                >
		                  {t("headings.vaultgrid_code", { defaultValue: "Your VaultGrid code" })}
		                </div>

		                <div
		                  className="absolute left-1/2 flex h-[120px] w-[290px] -translate-x-1/2 items-center justify-between"
		                  style={{ top: `${codeCardsTop}px` }}
		                >
	                  {codeLetters.map((letter, idx) => (
	                    <div
	                      key={`${letter}-${idx}`}
	                      className="flex h-[120px] w-[90px] items-center justify-center rounded-[16px] border border-white/25 bg-black/10 text-[64px] font-black leading-none text-white"
	                    >
	                      {letter}
	                    </div>
	                  ))}
	                </div>

	                {canBrowsePreviousRegistrationCode && (
	                  <motion.button
	                    type="button"
	                    onClick={() => selectRegistrationCodeFromHistory(-1)}
	                    initial={{ opacity: 0, x: 8, scale: 0.95 }}
	                    animate={{ opacity: 1, x: 0, scale: 1 }}
	                    whileTap={{ x: -2, scale: 0.9 }}
	                    transition={PRESS_TRANSITION}
		                    className="absolute left-[30px] flex h-[40px] w-[24px] items-center justify-center"
		                    style={{ top: `${codeNavTop}px` }}
		                    aria-label="Previous generated code"
	                  >
	                    <img src="/assets/landing/back_arrow.svg" alt="" className="h-[22px] w-[22px] opacity-90" />
	                  </motion.button>
	                )}

	                <motion.button
	                  type="button"
                  onClick={() => localRegistrationWalkthroughPreview
                    ? setRegistrationCode((current) => current === "RAT" ? "VGT" : "RAT")
                    : fetchRegistrationCode()}
	                  disabled={codeGenerateDisabled}
	                  title={codeButtonTitle}
	                  whileHover={codeGenerateDisabled ? undefined : PRESS_HOVER}
	                  whileTap={codeGenerateDisabled ? undefined : PRESS_TAP}
	                  transition={PRESS_TRANSITION}
		                  className="absolute left-[106px] flex h-[35px] w-[190px] items-center justify-center rounded-full border border-white/20 bg-[#3e6ffb] px-4 text-[12px] font-semibold leading-[16px] text-white transition-opacity disabled:opacity-45"
		                  style={{ top: `${codeButtonTop}px` }}
		                >
	                  <span className="flex w-[150px] items-center justify-start gap-[9px]">
		                    <span className="flex h-[16px] w-[16px] shrink-0 items-center justify-center text-[15px] leading-none">
		                      ↻
		                    </span>
	                    <span className="relative block h-[16px] w-[125px] overflow-hidden whitespace-nowrap text-left">
	                      <AnimatePresence mode="wait" initial={false}>
	                        <motion.span
	                          key={codeButtonLabel}
	                          className="absolute left-0 top-0"
	                          initial={{ opacity: 0, y: 7, filter: "blur(4px)" }}
	                          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
	                          exit={{ opacity: 0, y: -7, filter: "blur(4px)" }}
	                          transition={LABEL_CROSSFADE_TRANSITION}
	                        >
	                          {codeButtonLabel}
	                        </motion.span>
	                      </AnimatePresence>
	                    </span>
	                  </span>
	                </motion.button>

	                {canBrowseNextRegistrationCode && (
	                  <motion.button
	                    type="button"
	                    onClick={() => selectRegistrationCodeFromHistory(1)}
	                    initial={{ opacity: 0, x: -8, scale: 0.95 }}
	                    animate={{ opacity: 1, x: 0, scale: 1 }}
	                    whileTap={{ x: 2, scale: 0.9 }}
	                    transition={PRESS_TRANSITION}
		                    className="absolute left-[348px] flex h-[40px] w-[24px] items-center justify-center"
		                    style={{ top: `${codeNavTop}px` }}
		                    aria-label="Next generated code"
	                  >
	                    <img src="/assets/landing/back_arrow.svg" alt="" className="h-[22px] w-[22px] rotate-180 opacity-90" />
	                  </motion.button>
	                )}
	                {registrationCodeError && (
		                  <div
		                    className="absolute left-[30px] w-[343px] text-center text-[12px] font-medium leading-[15px] text-red-300"
		                    style={{ top: `${codeErrorTop}px` }}
		                  >
		                    {registrationCodeError}
		                  </div>
	                )}

	                <div className="absolute left-[30px] top-0 w-[343px]">
	                  {renderField("username", "text", "", 16, false, {
	                    wrapperClassName: "absolute left-0 w-full",
	                    wrapperStyle: { top: `${getRegisterFieldTop("username")}px` },
	                    placeholder: isSocialRegistration
	                      ? t("placeholders.vaultgrid_username")
	                      : t("placeholders.username"),
	                    inputClassName: fieldClassName,
	                    showError: false,
	                  })}
	                  {isSocialRegistration ? (
	                    <>
	                      <input type="hidden" {...register("email")} />
	                      <input type="hidden" {...register("email_repeat")} />
	                    </>
	                  ) : (
	                    <>
	                      {renderField("email", "text", "", undefined, false, {
	                        wrapperClassName: "absolute left-0 w-full",
	                        wrapperStyle: { top: `${getRegisterFieldTop("email")}px` },
	                        placeholder: t("placeholders.email"),
	                        inputClassName: fieldClassName,
	                        inputMode: "email",
	                        showError: false,
	                      })}
	                      <input type="hidden" {...register("email_repeat")} />
	                      {renderField("password1", "password", "", undefined, false, {
	                        wrapperClassName: "absolute left-0 w-full",
	                        wrapperStyle: { top: `${getRegisterFieldTop("password1")}px` },
	                        placeholder: t("placeholders.password"),
	                        inputClassName: fieldClassName,
	                        showError: false,
	                      })}
	                      {renderField("password2", "password", "", undefined, false, {
	                        wrapperClassName: "absolute left-0 w-full",
	                        wrapperStyle: { top: `${getRegisterFieldTop("password2")}px` },
	                        placeholder: t("placeholders.password_repeat"),
	                        inputClassName: fieldClassName,
	                        showError: false,
	                      })}
	                    </>
	                  )}
	                  {registerFieldNames.map((fieldName) => (
	                    errors[fieldName] ? (
	                      <div
	                        key={fieldName}
	                        className="absolute left-0 w-[343px] truncate text-left text-[12px] font-medium leading-[15px] text-red-400"
	                        style={{ top: `${getRegisterFieldTop(fieldName) + 58}px` }}
	                      >
	                        {errors[fieldName]?.message}
	                      </div>
	                    ) : null
	                  ))}
	                  <input type="hidden" {...register("country")} />
	                </div>

	                <label
	                  className="absolute left-[40px] flex w-[330px] items-start gap-[8px] text-[12px] font-normal leading-normal text-white"
	                  style={{ top: `${termsTop}px` }}
	                >
	                  <input
	                    {...register("terms")}
	                    type="checkbox"
	                    checked={Boolean(acceptedTerms)}
	                    readOnly
	                    className="sr-only"
	                    tabIndex={-1}
	                  />
	                  <motion.button
	                    type="button"
	                    onClick={() => {
	                      setValue("terms", !acceptedTerms, {
	                        shouldDirty: true,
	                        shouldTouch: true,
	                        shouldValidate: false,
	                      });
	                      if (errors.terms) clearErrors("terms");
	                    }}
	                    className={clsx(
	                      "mt-[1px] flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[4px] border text-[11px] leading-none text-white transition-all",
	                      acceptedTerms
	                        ? "border-[#3e6ffb] bg-[#3e6ffb]"
	                        : "border-white/60 bg-black/10"
	                    )}
	                    whileTap={SUBTLE_PRESS_TAP}
	                    transition={PRESS_TRANSITION}
	                    aria-pressed={Boolean(acceptedTerms)}
	                    aria-label={t("validation.terms_required")}
	                  >
	                    {acceptedTerms ? <span className="h-[8px] w-[8px] rounded-[2px] bg-white" /> : null}
	                  </motion.button>
	                  <span>
	                    {isRussianLegalBundle ? (
	                      <Trans
	                        i18nKey="ru_public_offer_text"
	                        ns="auth"
	                        components={[
	                          <a href="/legal/privacy?from=signup" className="underline" aria-label="Политика обработки персональных данных" />,
	                          <a href="/legal/user-agreement?from=signup" className="underline" aria-label="Пользовательское соглашение" />,
	                          <a href="/legal/terms?from=signup" className="underline" aria-label="Публичная оферта" />,
	                        ]}
	                      />
	                    ) : (
	                      <Trans
	                        i18nKey="terms_text"
	                        ns="auth"
	                        components={[
	                          <a href="/legal/terms?from=signup" className="underline" aria-label="User Agreement" />,
	                          <a href="/legal/community-rules?from=signup" className="underline" aria-label="Community Rules" />,
	                          <a href="/legal/moderation?from=signup" className="underline" aria-label="Moderation Policy" />,
	                          <a href="/legal/privacy?from=signup" className="underline" aria-label="Privacy Policy" />,
	                          <a href="/legal/cookies?from=signup" className="underline" aria-label="Cookies Policy" />,
	                        ]}
	                      />
	                    )}
	                  </span>
	                </label>
	                {isRussianLegalBundle ? (
	                  <label
	                    className="absolute left-[40px] flex w-[330px] items-start gap-[8px] text-[12px] font-normal leading-normal text-white"
	                    style={{ top: `${termsTop + russianConsentTopOffset}px` }}
	                  >
	                    <input
	                      {...register("personal_data_consent")}
	                      type="checkbox"
	                      checked={Boolean(acceptedPersonalDataConsent)}
	                      readOnly
	                      className="sr-only"
	                      tabIndex={-1}
	                    />
	                    <motion.button
	                      type="button"
	                      onClick={() => {
	                        setValue("personal_data_consent", !acceptedPersonalDataConsent, {
	                          shouldDirty: true,
	                          shouldTouch: true,
	                          shouldValidate: false,
	                        });
	                        if (errors.personal_data_consent) clearErrors("personal_data_consent");
	                      }}
	                      className={clsx(
	                        "mt-[1px] flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[4px] border text-[11px] leading-none text-white transition-all",
	                        acceptedPersonalDataConsent
	                          ? "border-[#3e6ffb] bg-[#3e6ffb]"
	                          : "border-white/60 bg-black/10"
	                      )}
	                      whileTap={SUBTLE_PRESS_TAP}
	                      transition={PRESS_TRANSITION}
	                      aria-pressed={Boolean(acceptedPersonalDataConsent)}
	                      aria-label={t("validation.personal_data_consent_required")}
	                    >
	                      {acceptedPersonalDataConsent ? <span className="h-[8px] w-[8px] rounded-[2px] bg-white" /> : null}
	                    </motion.button>
	                    <span>
	                      <Trans
	                        i18nKey="ru_personal_data_consent_text"
	                        ns="auth"
	                        components={[
	                          <a href="/legal/personal-data-consent?from=signup" className="underline" aria-label="Согласие на обработку персональных данных" />,
	                        ]}
	                      />
	                    </span>
	                  </label>
	                ) : null}
	                {errors.terms && (
	                  <div
	                    className="absolute left-[40px] text-[12px] font-medium leading-[15px] text-red-400"
	                    style={{ top: `${termsTop + 32}px` }}
	                  >
	                    {errors.terms.message}
	                  </div>
	                )}
	                {errors.personal_data_consent && (
	                  <div
	                    className="absolute left-[40px] text-[12px] font-medium leading-[15px] text-red-400"
	                    style={{ top: `${termsTop + russianConsentTopOffset + 34}px` }}
	                  >
	                    {errors.personal_data_consent.message}
	                  </div>
	                )}
		              </motion.div>
		            )}

		            {registrationStep === "email" && (
		              <motion.div
		                key="register-step-email"
		                className="absolute inset-0"
		                initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
		                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
		                exit={{ opacity: 0, y: -12, filter: "blur(6px)" }}
		                transition={mobileAuthTransition}
		              >
			                <motion.h1
			                  className="absolute left-[51px] w-[300px] text-center text-[40px] font-semibold leading-[44px] text-white"
			                  style={{ top: `${emailTitleTop}px` }}
			                  initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
		                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
		                  transition={{ duration: 0.32, ease: "easeOut" }}
		                >
		                  <span className="mb-[8px] block text-[36px] leading-[36px]" aria-hidden="true">✉️</span>
		                  <span className="block">
                        {t("registration_flow.email.title_line_1", { defaultValue: "Check your" })}
                      </span>
		                  <span className="block">
                        {t("registration_flow.email.title_line_2", { defaultValue: "email" })}
                      </span>
		                </motion.h1>

			                <motion.p
			                  className="absolute left-[52px] w-[298px] text-center text-[15px] font-semibold leading-[20px] text-white"
			                  style={{ top: `${emailCopyTop}px` }}
		                  initial={{ opacity: 0, y: 8, filter: "blur(4px)" }}
		                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
		                  transition={{ duration: 0.28, ease: "easeOut", delay: 0.05 }}
		                >
		                  <span className="block">
                        {t("registration_flow.email.prompt_line_1", { defaultValue: "Enter the confirmation code" })}
                      </span>
		                  <span className="block">
                        {t("registration_flow.email.prompt_line_2", { defaultValue: "that we sent on:" })}
                      </span>
		                  <span className="mx-auto mt-[1px] block max-w-[298px] truncate">
		                    {registrationEmailSentTo || registerEmailValue || t("registration_flow.email.fallback_email", { defaultValue: "your email" })}
		                  </span>
		                </motion.p>

			                <div className="absolute left-[30px] w-[343px]" style={{ top: `${emailCodeInputTop}px` }}>
		                  <input
		                    type="text"
		                    inputMode="numeric"
		                    autoComplete="one-time-code"
		                    data-auth-field="registration-email-code"
		                    value={registrationEmailCodeInput}
		                    onFocus={(e) => ensureFieldVisible(e.currentTarget)}
		                    onChange={(event) => {
		                      const nextCode = event.target.value.replace(/\D/g, "").slice(0, 6);
		                      setRegistrationEmailCodeInput(nextCode);
		                      setRegistrationStepError("");
		                    }}
		                    className={fieldClassName}
		                    placeholder={t("registration_flow.email.code_placeholder", { defaultValue: "Confirmation code" })}
		                    aria-label={t("registration_flow.email.code_aria", { defaultValue: "Email confirmation code" })}
		                  />
		                </div>
		                {registrationStepError && (
			                  <div
			                    className="absolute left-[50px] z-20 w-[303px] truncate text-left text-[12px] font-medium leading-[15px] text-[#ff4a4a]"
			                    style={{ top: `${emailCodeInputTop + 62}px` }}
			                  >
		                    {registrationStepError}
		                  </div>
		                )}
		                <button
		                  type="button"
		                  disabled={resendEmailDisabled || localRegistrationWalkthroughPreview}
		                  onClick={() => requestRegistrationEmailCode(getValues(), { resend: true })}
		                  className={clsx(
		                    "absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-center text-[15px] font-normal leading-[22px] underline underline-offset-2 transition-opacity",
		                    resendEmailDisabled ? "text-white/34" : "text-white/50 active:text-white/70"
		                  )}
			                  style={{ top: `${emailResendTop}px` }}
			                >
		                  {resendEmailLabel}
		                </button>
		              </motion.div>
		            )}

		            {registrationStep === "city" && (
		              <motion.div
		                key="register-step-city"
	                className="absolute inset-0"
	                initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
	                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
	                exit={{ opacity: 0, y: -12, filter: "blur(6px)" }}
	                transition={mobileAuthTransition}
	              >
				                <div className="absolute left-0 top-0 z-20 h-[220px] w-[402px] rounded-b-[45px] bg-[linear-gradient(180deg,rgba(255,255,255,0.30),rgba(77,117,230,0.28)_52%,rgba(0,0,0,0.34))] backdrop-blur-[64px]" />
		                <div className="absolute left-1/2 top-[-5px] z-30 h-[13px] w-[252px] -translate-x-1/2 rounded-full bg-white/45 blur-[38px]" />
		                <div className="absolute left-1/2 top-[63px] z-30 flex h-[35px] w-[265px] -translate-x-1/2 items-center justify-center text-center text-[32px] font-semibold leading-[35px] text-white">
		                  {t("registration_flow.city.title", { defaultValue: "Where are you?" })}
		                </div>
		                <div className="absolute left-1/2 top-[102px] z-30 w-[302px] -translate-x-1/2 text-center text-[15px] font-normal leading-[18px] text-white">
		                  {t("registration_flow.city.subtitle", { defaultValue: "Select your city" })}
		                </div>

			                {registrationCitySearchActive ? (
			                  <div className={citySearchShellClassName} style={citySearchShellStyle}>
		                    <input
		                      ref={registrationCitySearchInputRef}
	                      value={registrationCityQuery}
	                      onChange={(event) => {
	                        const value = event.target.value;
	                        setRegistrationCityQuery(value);
	                        setRegistrationCity("");
	                        setRegistrationStepError("");
	                      }}
	                      onFocus={(e) => ensureFieldVisible(e.currentTarget)}
		                      className="h-full min-w-0 flex-1 bg-transparent text-[13px] font-medium leading-[20px] text-white outline-none placeholder:text-white/80"
		                      placeholder={t("registration_flow.city.search", { defaultValue: "Search city" })}
		                      autoComplete="address-level2"
		                    />
		                    <img src="/assets/menu/magnifying_glass_white.svg" alt="" className="h-[20px] w-[20px] shrink-0" />
		                  </div>
		                ) : (
			                  <motion.button
	                    type="button"
	                    onClick={() => {
	                      setRegistrationCitySearchActive(true);
	                      setRegistrationCityQuery("");
	                      setRegistrationStepError("");
	                    }}
	                    whileHover={PRESS_HOVER}
	                    whileTap={PRESS_TAP}
	                    transition={PRESS_TRANSITION}
			                    className={citySearchShellClassName}
			                    style={citySearchShellStyle}
			                  >
		                    <span className="min-w-0 flex-1 text-[13px] font-medium leading-[20px] text-white/80">
		                      {t("registration_flow.city.search", { defaultValue: "Search city" })}
		                    </span>
		                    <img src="/assets/menu/magnifying_glass_white.svg" alt="" className="h-[20px] w-[20px] shrink-0" />
		                  </motion.button>
		                )}

		                <div
		                  className="absolute left-0 top-[180px] z-10 h-[694px] w-[402px] overflow-y-auto px-[29px] pb-[188px] pt-[75px] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
		                  style={{
		                    WebkitMaskImage:
		                      "linear-gradient(180deg, rgba(0,0,0,0) 0px, rgba(0,0,0,0.12) 28px, rgba(0,0,0,0.72) 58px, #000 76px)",
		                    maskImage:
		                      "linear-gradient(180deg, rgba(0,0,0,0) 0px, rgba(0,0,0,0.12) 28px, rgba(0,0,0,0.72) 58px, #000 76px)",
		                  }}
		                >
		                  <div className="flex w-[343px] flex-col gap-[5px]">
	                    <CitySearchFeedback options={registrationCityOptions} loading={registrationCityLoading} error={registrationCitySearchError} query={registrationCitySearchActive ? registrationCityQuery : ""} t={t} />
{(registrationCityLoading ? [] : registrationCityOptions).map((option) => {
	                      const name = String(option?.name || option || "").trim();
	                      if (!name) return null;
	                      const optionCountry = String(option?.country || selectedCountry || "").trim();
		              const active = registrationCity === name && selectedCountry === optionCountry
		                && (option.municipality_id
		                  ? selectedMunicipalityId === option.municipality_id
		                  : !selectedMunicipalityId);
	                      return (
	                        <motion.button
		                  key={getRegistrationCityOptionKey(option, optionCountry, name)}
	                          type="button"
		                          onClick={() => selectRegistrationCityOption(option)}
	                          whileHover={PRESS_HOVER}
	                          whileTap={PRESS_TAP}
	                          transition={PRESS_TRANSITION}
		                          className={cityOptionClassName}
		                        >
		                          <span className="absolute left-[20px] top-1/2 max-w-[260px] -translate-y-1/2 truncate text-[24px] font-semibold leading-[30px] text-white">
		                            <span className="block truncate">{getRegistrationCityDisplayName(option.display_name || name)}</span>

		                          </span>
		                          <span className="absolute left-[303px] top-[25px] h-[20px] w-[20px] rounded-full border border-white/45">
			                            {active && (
			                              <motion.span
			                                layoutId="registration-city-dot"
		                                className="absolute left-[3px] top-[3px] h-[12px] w-[12px] rounded-full bg-white"
		                              />
	                            )}
	                          </span>
	                        </motion.button>
	                      );
	                    })}
	                    {registrationCityLoading && (
	                      <div className="flex h-[70px] w-[343px] items-center rounded-[24px] border border-white/20 bg-white/10 px-[20px] text-[20px] font-semibold text-white/70">
	                        {t("registration_flow.city.loading", { defaultValue: "Loading..." })}
	                      </div>
	                    )}
	                  </div>
	                </div>

	                <div className="pointer-events-none absolute bottom-[-1px] left-1/2 z-10 h-[170px] w-[403px] -translate-x-1/2 bg-[linear-gradient(0deg,#000_35%,rgba(0,0,0,0)_100%)] shadow-[0_4px_4px_rgba(0,0,0,0.25)]" />
	                {registrationStepError && (
	                  <div className="absolute left-[30px] top-[742px] z-20 w-[343px] text-center text-[12px] font-medium leading-[15px] text-red-300">
	                    {registrationStepError}
	                  </div>
	                )}
	              </motion.div>
	            )}

	            {registrationStep === "photo" && (
	              <motion.div
	                key="register-step-photo"
	                className="absolute inset-0"
	                initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
	                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
	                exit={{ opacity: 0, y: -12, filter: "blur(6px)" }}
	                transition={mobileAuthTransition}
	              >
		                <div
		                  className="absolute left-1/2 w-[343px] -translate-x-1/2 text-center text-[32px] font-semibold leading-[36px] text-white"
		                  style={{ top: `${photoTitleTop}px` }}
		                >
		                  {t("registration_flow.photo.title", { defaultValue: "Add a profile photo" })}
		                </div>
	                <input
	                  ref={registrationPhotoInputRef}
	                  type="file"
	                  accept="image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif"
	                  className="hidden"
		                  onChange={(event) => {
		                    const file = event.target.files?.[0] || null;
		                    event.target.value = "";
		                    if (file && file.size > 10 * 1024 * 1024) {
		                      setRegistrationStepError(t("registration_flow.photo.errors.too_large", { defaultValue: "Please choose a photo under 10 MB." }));
		                      return;
		                    }
		                    setRegistrationPhotoChecking(false);
		                    setRegistrationPhotoFile(file);
		                    setRegistrationStepError("");
		                  }}
		                />
			                <motion.button
		                  type="button"
		                  onClick={() => {
		                    if (!registrationPhotoPreview) {
		                      registrationPhotoInputRef.current?.click();
		                    }
		                  }}
		                  onPointerDown={handleRegistrationPhotoPointerDown}
		                  onPointerMove={handleRegistrationPhotoPointerMove}
			                  onPointerUp={handleRegistrationPhotoPointerEnd}
			                  onPointerCancel={handleRegistrationPhotoPointerEnd}
			                  onWheel={handleRegistrationPhotoWheel}
			                  whileHover={registrationPhotoPreview ? undefined : PRESS_HOVER}
			                  whileTap={registrationPhotoPreview ? undefined : SUBTLE_PRESS_TAP}
				                  transition={PRESS_TRANSITION}
			                  className={clsx(
				                    "absolute left-[66px] flex h-[270px] w-[270px] items-center justify-center overflow-hidden rounded-full text-center text-white",
			                    registrationPhotoPreview
			                      ? clsx(
			                          "touch-none border-[4px] border-[#4b7cff] bg-black/10 shadow-[0_0_28px_rgba(62,111,251,0.35)]",
			                          registrationPhotoDragging ? "cursor-grabbing" : "cursor-grab"
			                        )
			                      : "cursor-pointer border border-dashed border-white/45 bg-white/[0.03] backdrop-blur-[12px]"
				                  )}
				                  style={{ top: `${photoFrameTop}px` }}
			                >
			                  {registrationPhotoPreview ? (
			                    <>
			                      <div
			                        className="pointer-events-none absolute left-0 top-0"
			                        style={{
			                          width: `${registrationPhotoImageSize.w * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
			                          height: `${registrationPhotoImageSize.h * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
			                          transform: `translate3d(${registrationPhotoPos.x}px, ${registrationPhotoPos.y}px, 0)`,
			                          transformOrigin: "top left",
			                          willChange: "transform",
			                        }}
			                      >
			                        <div
			                          style={{
			                            width: `${registrationPhotoImageSize.w * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
			                            height: `${registrationPhotoImageSize.h * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
			                            transform: `scale(${registrationPhotoZoom})`,
			                            transformOrigin: "top left",
			                            willChange: "transform",
			                            pointerEvents: "none",
			                          }}
			                        >
			                          <img
			                            src={registrationPhotoPreview}
			                            alt=""
			                            className="block max-w-none select-none"
			                            draggable="false"
			                            style={{
			                              width: `${registrationPhotoImageSize.w * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
			                              height: `${registrationPhotoImageSize.h * registrationPhotoBaseScale || REGISTRATION_PHOTO_FRAME_SIZE}px`,
			                              userSelect: "none",
			                              pointerEvents: "none",
			                            }}
			                          />
			                        </div>
			                      </div>
			                      <span className="pointer-events-none absolute inset-[38px] rounded-full border border-dashed border-white/55" />
			                    </>
		                  ) : (
	                    <span className="flex w-[170px] flex-col items-center justify-center">
	                      <img src="/assets/menu/upload_photo_Icon.svg" alt="" className="mb-[18px] h-[42px] w-[42px] opacity-90" />
	                      <span className="text-[18px] font-semibold leading-[22px]">
	                        {t("registration_flow.photo.upload", { defaultValue: "Upload a photo" })}
	                      </span>
	                      <span className="mt-[8px] text-[13px] font-medium leading-[18px] text-white/62">
	                        {t("registration_flow.photo.formats", { defaultValue: "JPG, PNG or HEIC. Max 10 MB." })}
	                      </span>
	                    </span>
	                  )}
	                </motion.button>
		                {registrationPhotoPreview ? (
			                  <div
			                    className="absolute left-1/2 flex w-[343px] -translate-x-1/2 flex-col items-center justify-center gap-[9px] text-white/72"
			                    style={{ top: `${photoHintTop}px` }}
			                  >
		                    <div className="flex items-center justify-center gap-[8px] text-[15px] font-medium leading-[20px]">
		                      <img src="/assets/menu/photo_adjustment.svg" alt="" className="h-[18px] w-[18px]" />
		                      <span>{t("registration_flow.photo.adjust", { defaultValue: "Drag to adjust" })}</span>
		                    </div>
		                    <motion.button
		                      type="button"
		                      disabled={isSubmitting}
		                      onClick={() => {
		                        setRegistrationStepError("");
		                        registrationPhotoInputRef.current?.click();
		                      }}
		                      whileHover={isSubmitting ? undefined : PRESS_HOVER}
		                      whileTap={isSubmitting ? undefined : SUBTLE_PRESS_TAP}
		                      transition={PRESS_TRANSITION}
		                      className="h-[28px] rounded-full border border-white/20 bg-white/[0.08] px-[18px] text-[12px] font-semibold leading-[14px] text-white/80 backdrop-blur-[12px] disabled:opacity-45"
		                    >
		                      {t("registration_flow.photo.change", { defaultValue: "Change photo" })}
		                    </motion.button>
		                  </div>
	                ) : (
		                  <div
		                    className="absolute left-1/2 w-[322px] -translate-x-1/2 text-center text-[13px] font-medium leading-[19px] text-white/68"
		                    style={{ top: `${photoHintTop}px` }}
		                  >
		                    {t("registration_flow.photo.hint", { defaultValue: "Use a recent photo of only you with your whole face visible in good lighting." })}
		                  </div>
		                )}
		                {registrationStepError && (
		                  <div
		                    className="absolute left-[30px] z-30 w-[343px] text-center text-[13px] font-semibold leading-[17px] text-red-300"
		                    style={{ top: `${photoErrorTop}px` }}
		                  >
	                    {registrationStepError}
	                  </div>
	                )}
	              </motion.div>
	            )}

	          </AnimatePresence>

	          {errorMsg && (
	            <div
	              className="absolute left-[30px] w-[343px] text-center text-[12px] font-medium leading-[15px] text-red-300"
	              style={{ top: `${createAccountTop - 24}px` }}
	            >
	              {errorMsg}
	            </div>
	          )}

	          <motion.button
	            type="submit"
	            disabled={!canSubmitRegister}
	            whileHover={canSubmitRegister ? PRESS_HOVER : undefined}
	            whileTap={canSubmitRegister ? PRESS_TAP : undefined}
	            transition={PRESS_TRANSITION}
	            className={clsx(
              "absolute left-[30px] z-20 flex h-[55px] w-[343px] items-center justify-center rounded-full border border-white/20 px-5 text-[15px] font-semibold leading-[16px] text-white transition-all",
              canSubmitRegister ? "bg-[#3e6ffb]" : "bg-[#2e58d0]/70 text-white/50"
            )}
            style={{ top: `${createAccountTop}px` }}
          >
	            {isSubmitting ? primaryButtonProgressLabel : primaryButtonLabel}
		          </motion.button>
	        </div>
	        {renderMobileSignupFinalIntro()}
	      </div>
	    );
	  };

  const renderCountrySelect = () => {
    const registerProps = register("country");
    return (
      <div className="mb-4">
        <div className="relative">
          <select
            onFocus={(e) => ensureFieldVisible(e.currentTarget)}
            {...registerProps}
            className="w-full h-12 bg-zinc-300 bg-opacity-25 rounded-2xl px-4 text-white pr-10 appearance-none"
            style={{ color: !selectedCountry ? "rgba(255,255,255,0.5)" : "#ffffff" }}
            defaultValue=""
          >
            <option value="" disabled hidden>{t("placeholders.country")}</option>
            {COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.value === "SK" ? t("countries.slovakia", { defaultValue: c.label }) : c.label}
              </option>
            ))}
          </select>
          <img
            src="/assets/search/arrow_down.svg"
            alt=""
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-70"
          />
        </div>
        {errors.country && (
          <div style={{ color: "red", fontSize: "0.8rem" }}>{errors.country.message}</div>
        )}
      </div>
    );
  };

  /** Стили/поведение корневого контейнера
   *  - Desktop: redesigned auth живет на всю рабочую область AppShell
   *  - Mobile / Telegram iOS: фуллскрин-оверлей с управлением высотой
   */
  const mobileViewportPx = viewportH ? `${viewportH}px` : "100vh";
  const lockMobileLoginCanvas = !isDesktop && showAuthRedesign;
  const nativeIosCanvasHeight =
    useNativeIosSafeStage && authStageSize.viewportHeight > 0
      ? `${authStageSize.viewportHeight}px`
      : "100vh";
  const lockedMobileCanvasHeight = useNativeIosSafeStage
    ? nativeIosCanvasHeight
    : "100vh";
  const useFullBleedNativeAuthStage =
    showAuthRedesign &&
    (tab === "forgot" ||
      (tab === "register" &&
        (registrationStep === "city" || registrationStep === "age" || registrationStep === "photo")));
  const useFullBleedNativeRegistrationScroll =
    showAuthRedesign && useNativeIosSafeStage && tab === "register" &&
    (registrationStep === "form" || registrationStep === "email");
  const rootStyle = isDesktop
    ? {
        backgroundImage: showAuthRedesign ? AUTH_GRADIENT_BACKGROUND : undefined,
        backgroundSize: showAuthRedesign ? "100% 100%" : undefined,
        backgroundColor: showAuthRedesign ? "#050812" : "transparent",
        height: "100%",
        minHeight: showAuthRedesign ? 0 : "100%",
        overflow: showAuthRedesign ? "hidden" : undefined,
        width: "100%",
      }
    : useStableMobileWebAuth
    ? {
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        overflow: "visible",
        backgroundColor: "#111827",
        backgroundImage: AUTH_GRADIENT_BACKGROUND,
        backgroundSize: "100% 100%",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
      }
    : useStableNativeAuth
    ? {
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: "#050812",
        backgroundImage: AUTH_GRADIENT_BACKGROUND,
        backgroundSize: "100% 100%",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
      }
    : {
        position: (isTelegramIOS || isAndroid) ? "absolute" : "fixed",
        top: 0,
        right: 0,
        bottom: lockMobileLoginCanvas && useNativeIosSafeStage ? "auto" : 0,
        left: 0,
        paddingBottom: lockMobileLoginCanvas ? 0 : "env(safe-area-inset-bottom, 0)",
        // On native iOS, 100vh follows the resized WKWebView and re-stretches
        // the SVG gradient every time the keyboard moves. Freeze the auth
        // canvas to its pre-keyboard pixel height; the WebView may clip its
        // bottom, but the visible gradient and all design coordinates stay put.
        height: lockMobileLoginCanvas ? lockedMobileCanvasHeight : (isTelegramIOS ? "100vh" : mobileViewportPx),
        maxHeight: lockMobileLoginCanvas ? lockedMobileCanvasHeight : (isTelegramIOS ? "100vh" : mobileViewportPx),
        overflowY: lockMobileLoginCanvas
          ? "hidden"
          :
          isTelegramIOS || isKbOpen || isInputFocused || contentNeedsScroll ? "auto" : "hidden",
        overflowX: "hidden",
        scrollPaddingBottom: "140px",
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorY: "contain",
        overscrollBehaviorX: "none",
        scrollbarGutter: "auto",
        backgroundColor: "#050812",
        backgroundImage: AUTH_GRADIENT_BACKGROUND,
        backgroundSize: "100% 100%",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
      };

  return (
    <div
      ref={rootRef}
      className={clsx(
        "flex flex-col items-center",
        showAuthRedesign
          ? "auth-shell auth-vh-stable"
          : isDesktop
          ? "auth-desktop-shell pt-20 pb-16"
          : "auth-shell auth-vh-stable pt-8"
      )}
      style={rootStyle}
    >
      {!showAuthRedesign && (
        <div className="absolute -top-1 left-1/2 h-3 w-64 -translate-x-1/2 transform rounded-full bg-zinc-300 opacity-50 blur-2xl" />
      )}

      <div
        ref={contentRef}
        className={clsx(
          "mx-auto flex w-full max-w-[500px] flex-col px-4 pb-8",
          showAuthRedesign
            ? "h-full max-w-none px-0 pb-0"
            : (!isDesktop && !isKbOpen && !isSmallPhone && "min-h-[620px]")
        )}
        style={{
          // Safe areas are scrolling padding on registration, so content can
          // pass behind the status bar while keeping its resting coordinates.
          paddingTop: useFullBleedNativeRegistrationScroll ? "var(--vg-safe-area-top)" : undefined,
          paddingBottom: useFullBleedNativeRegistrationScroll && !nativeKeyboardState.isOpen
            ? "var(--vg-safe-area-bottom)"
            : showAuthRedesign ? 0 : bottomSpacer ? `${bottomSpacer}px` : undefined,
          width: showAuthRedesign ? "100%" : undefined,
          maxWidth: showAuthRedesign ? "none" : undefined,
          paddingLeft: showAuthRedesign ? 0 : undefined,
          paddingRight: showAuthRedesign ? 0 : undefined,
          // Keep the page background edge-to-edge, but lay out controls only
          // inside the usable iOS area. Measuring this element also makes the
          // bottom action stack fit above the home indicator.
          height:
            showAuthRedesign && useNativeIosSafeStage
              ? useFullBleedNativeRegistrationScroll
                ? nativeKeyboardState.isOpen && nativeAuthVisibleHeight > 0
                  ? `calc(${nativeAuthVisibleHeight}px + var(--vg-safe-area-top))`
                  : "100%"
                : useFullBleedNativeAuthStage
                ? tab === "forgot" && nativeKeyboardState.isOpen && nativeAuthVisibleHeight > 0
                  ? `calc(${nativeAuthVisibleHeight}px + var(--vg-safe-area-top))`
                  : "100%"
                : nativeKeyboardState.isOpen && nativeAuthVisibleHeight > 0
                ? `${nativeAuthVisibleHeight}px`
                : "calc(100% - var(--vg-safe-area-top) - var(--vg-safe-area-bottom))"
              : undefined,
          minHeight: showAuthRedesign && useNativeIosSafeStage ? 0 : undefined,
          marginTop:
            showAuthRedesign && useNativeIosSafeStage
              ? useFullBleedNativeAuthStage || useFullBleedNativeRegistrationScroll
                ? 0
                : "var(--vg-safe-area-top)"
              : undefined,
          overflowY:
            showAuthRedesign && useStableNativeAuth
              ? useFullBleedNativeAuthStage
                ? tab === "forgot"
                  ? "auto"
                  : "hidden"
                : "auto"
              : undefined,
          overflowX:
            showAuthRedesign && useStableNativeAuth
              ? "hidden"
              : undefined,
          scrollPaddingTop:
            showAuthRedesign && useStableNativeAuth
              ? useFullBleedNativeRegistrationScroll
                ? "calc(var(--vg-safe-area-top) + 20px)"
                : "20px"
              : undefined,
          scrollPaddingBottom:
            showAuthRedesign && useStableNativeAuth
              ? "20px"
              : undefined,
          WebkitOverflowScrolling:
            showAuthRedesign && useStableNativeAuth
              ? "touch"
              : undefined,
          overscrollBehaviorY:
            showAuthRedesign && useStableNativeAuth
              ? "contain"
              : undefined,
        }}
      >
        {!showAuthRedesign && renderTabs()}

        {tab === "register" && refInfo && !showAuthRedesign && (
          <div className="mb-4 -mt-4 text-white text-center">
            {t("referral", { code: refInfo.storage_code, level: refInfo.level })}
          </div>
        )}

        <form
          noValidate
          onSubmit={localRegistrationOnboardingFlowPreview
            ? (event) => {
                event.preventDefault();
                void continueLocalRegistrationOnboardingFlow();
              }
            : nativeApp && tab === "register" && registrationStep === "city"
              ? (event) => {
                  event.preventDefault();
                  void onSubmit(getValues());
                }
              : handleSubmit(onSubmit)}
          className={showAuthRedesign ? "h-full" : undefined}
        >
          {showAuthRedesign && (
            <AnimatePresence mode="wait">
              <motion.div
	                key={`mobile-auth-${tab}-${tab === "forgot" ? forgotStep : tab === "register" ? registrationStep : "main"}`}
                className="h-full"
                initial={{ opacity: 0, y: 22, scale: 0.985, filter: "blur(8px)" }}
                animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -14, scale: 0.99, filter: "blur(6px)" }}
                transition={mobileAuthTransition}
              >
                {tab === "login" && renderMobileLoginView()}
                {tab === "forgot" && renderMobileForgotView()}
                {tab === "register" && renderMobileRegisterView()}
              </motion.div>
            </AnimatePresence>
          )}

          {tab === "login" && !showAuthRedesign && (
            <div>
              {renderField("username", "text", "placeholders.username_or_email")}
              {renderField("password", "password", "placeholders.password")}
              <button
                type="submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                className={`w-full h-12 rounded-2xl flex items-center justify-center text-base font-semibold text-white mb-4 transition-all duration-150 ${
                  isSubmitting
                    ? "bg-blue-500/90 cursor-wait shadow-[0_10px_24px_rgba(37,99,235,0.28)]"
                    : "bg-blue-600 active:scale-[0.985] active:bg-blue-500 shadow-[0_12px_28px_rgba(37,99,235,0.26)]"
                }`}
              >
                {isSubmitting ? t("buttons.entering") : t("buttons.enter")}
              </button>

              {!isRuAuthContext && !isTelegram ? (
                <button
                  type="button"
                  onClick={handleGoogleClick}
                  disabled={isSubmitting}
                  className={`w-full h-12 bg-white rounded-2xl flex items-center justify-center text-base font-semibold text-black mb-4 ${
                    isSubmitting ? "opacity-60 cursor-not-allowed" : isInstagramIAB ? "opacity-80" : ""
                  }`}
                >
                  Login with Google
                </button>
              ) : null}
              {!isRuAuthContext && nativeAppleSignInEnabled ? (
                <button
                  type="button"
                  onClick={handleAppleClick}
                  disabled={isSubmitting}
                  className="mb-4 flex h-12 w-full items-center justify-center gap-3 rounded-2xl bg-white text-base font-semibold text-black"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-[24px] w-[24px] items-center justify-center text-[28px] leading-[24px] text-black"
                  >
                    
                  </span>
                  <span>{t("buttons.continue_with_apple", "Continue with Apple")}</span>
                </button>
              ) : null}
              {!isRuAuthContext && !isTelegram && isInstagramIAB && (
                <div className="mb-4 text-xs text-white/70 text-center">
                  {t("errors.google_requires_browser")}
                </div>
              )}

              <div className="mb-4">
                <button type="button"
                  onClick={() => { setTab("forgot"); setForgotStep(1); reset(); setErrorMsg(""); }}
                  className="w-full h-12 border border-white rounded-2xl flex items-center justify-center text-white text-base opacity-50">
                  {t("buttons.forgot_password")}
                </button>
              </div>
            </div>
          )}

          {tab === "register" && !showAuthRedesign && (
            <>
              {/* Аватар-пикер */}
              <div
                className={clsx(
                  "w-full relative select-none",
                  isDesktop ? "mb-6" : (isSmallPhone ? "mb-2" : "mb-4")
                )}
              >
                {loadingAvatars ? (
                  <div className="flex w-full items-center justify-center gap-3 py-2" aria-hidden="true">
                    {[0, 1, 2, 3].map((index) => (
                      <LoadingAvatarSkeleton key={index} className="h-16 w-16" tone="blue" />
                    ))}
                  </div>
                ) : (
                  (() => {
                    const renderBtn = (av, idx) => (
                      <button
                        type="button"
                        key={av.id || idx}
                        onClick={() => setAvatarIdx(idx)}
                        className={clsx(
                          `${isDesktop ? 'w-20 h-20' : 'w-16 h-16'} rounded-full overflow-hidden flex-shrink-0 border-2`,
                          avatarIdx === idx ? 'border-blue-400 ring-1 ring-blue-400/50' : 'border-transparent'
                        )}
                        aria-pressed={avatarIdx === idx}
                        aria-label={av.display || av.name || 'avatar'}
                        title={av.display || av.name || 'avatar'}
                      >
                        <img src={av.src} alt="" className="w-full h-full object-cover" />
                      </button>
                    );

                    const limited = avatars.slice(0, 5);

                    if (isDesktop) {
                      return <div className="flex items-center gap-3 justify-center">{limited.map(renderBtn)}</div>;
                    }

                    const item = 64, gap = 12, FUDGE = 6;
                    const widthPx = Math.max(0, visibleCount * item + Math.max(0, visibleCount - 1) * gap + FUDGE);
                    const showChevron = limited.length > visibleCount;

                    return (
                      <div ref={avatarsWrapRef} className="relative w-full">
                        <div
                          ref={avatarsScrollRef}
                          className={clsx("flex items-center gap-3 px-1 py-1 mx-auto", showChevron ? "overflow-x-auto" : "overflow-visible")}
                          style={{ width: widthPx ? `${widthPx}px` : undefined, scrollbarWidth: 'none' }}
                        >
                          {limited.map(renderBtn)}
                        </div>
                        {showChevron && (
                          <button
                            type="button"
                            onClick={() => avatarsScrollRef.current && avatarsScrollRef.current.scrollBy({ left: item + gap, behavior: 'smooth' })}
                            className="absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 opacity-80 hover:opacity-100"
                            aria-label={t("buttons.see_more_avatars")}
                          >
                            <img src="/assets/search/arrow_down.svg" alt="" className="w-full h-full" style={{ transform: 'rotate(-90deg)' }} />
                          </button>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>

              {renderField("username", "text", "placeholders.username", 16)}
              {registerMode === "classic" && renderField("email", "email", "placeholders.email")}
              {registerMode === "classic" && renderField("email_repeat", "email", "placeholders.email_repeat")}
              {isSocialRegistration && (
                <input type="hidden" {...register("email")} />
              )}
              {registerMode === "classic" && renderField("password1", "password", "placeholders.password")}
              {registerMode === "classic" && renderField("password2", "password", "placeholders.password_repeat")}
              {!hideCountrySelect && renderCountrySelect()}
              {hideCountrySelect && (
                <input
                  type="hidden"
                  {...register("country")}
                />
              )}

              <div className="mb-4 flex items-start">
                <input {...register("terms")} type="checkbox" id="terms" className="mt-1 mr-2" />
                <label htmlFor="terms" className="text-white text-sm">
                  {isRussianLegalBundle ? (
                    <Trans
                      i18nKey="ru_public_offer_text"
                      ns="auth"
                      components={[
                        <a href="/legal/privacy?from=signup" className="underline" aria-label="Политика обработки персональных данных" />,
                        <a href="/legal/user-agreement?from=signup" className="underline" aria-label="Пользовательское соглашение" />,
                        <a href="/legal/terms?from=signup" className="underline" aria-label="Публичная оферта" />,
                      ]}
                    />
                  ) : (
                    <Trans
                      i18nKey="terms_text"
                      ns="auth"
                      components={[
                        <a href="/legal/terms?from=signup" className="underline" aria-label="User Agreement" />,
                        <a href="/legal/community-rules?from=signup" className="underline" aria-label="Community Rules" />,
                        <a href="/legal/moderation?from=signup" className="underline" aria-label="Moderation Policy" />,
                        <a href="/legal/privacy?from=signup" className="underline" aria-label="Privacy Policy" />,
                        <a href="/legal/cookies?from=signup" className="underline" aria-label="Cookies Policy" />,
                      ]}
                    />
                  )}
                </label>
              </div>
              {errors.terms && <div style={{ color: "red", fontSize: "0.8rem" }}>{errors.terms.message}</div>}
              {isRussianLegalBundle ? (
                <>
                  <div className="mb-4 flex items-start">
                    <input
                      {...register("personal_data_consent")}
                      type="checkbox"
                      id="personal-data-consent"
                      className="mt-1 mr-2"
                    />
                    <label htmlFor="personal-data-consent" className="text-white text-sm">
                      <Trans
                        i18nKey="ru_personal_data_consent_text"
                        ns="auth"
                        components={[
                          <a href="/legal/personal-data-consent?from=signup" className="underline" aria-label="Согласие на обработку персональных данных" />,
                        ]}
                      />
                    </label>
                  </div>
                  {errors.personal_data_consent ? (
                    <div style={{ color: "red", fontSize: "0.8rem" }}>
                      {errors.personal_data_consent.message}
                    </div>
                  ) : null}
                </>
              ) : null}

              <button
                type="submit"
                disabled={isSubmitting || !selectedCountry || !isCountrySupported}
                className={`w-full h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-base font-semibold text-white ${
                  isSubmitting || !selectedCountry || !isCountrySupported ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  t("buttons.creating_account", "Creating account...")
                ) : (
                  (!selectedCountry || isCountrySupported)
                    ? t("buttons.create_account")
                    : t("errors.region_not_supported")
                )}
              </button>

              {registerMode === "classic" && !isRuAuthContext && !isTelegram && (
                <>
                  <button
                    type="button"
                    onClick={handleGoogleClick}
                    className={`w-full h-12 bg-white rounded-2xl flex items-center justify-center text-base font-semibold text-black mt-4 ${
                      isInstagramIAB ? "opacity-80" : ""
                    }`}
                  >
                    {t("buttons.continue_with_google")}
                  </button>
                  {isInstagramIAB && (
                    <div className="mt-3 text-xs text-white/70 text-center">
                      {t("errors.google_requires_browser")}
                    </div>
                  )}
                </>
              )}

            </>
          )}

          {tab === "forgot" && !showAuthRedesign && (
            <div>
              {forgotStep === 1 ? (
                <>
                  {renderField("email", "email", "placeholders.email")}
                  <button type="submit" disabled={isSubmitting}
                    className="w-full h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-base font-semibold text-white mb-4">
                    {t("buttons.send_reset_code")}
                  </button>
                  <button type="button" onClick={() => { setTab("login"); reset(); setErrorMsg(""); }}
                    className="w-full h-12 border border-white rounded-2xl flex items-center justify-center text-base text-white opacity-50">
                    {t("buttons.go_back")}
                  </button>
                </>
              ) : forgotStep === 2 ? (
                <>
                  {renderField("email", "email", "placeholders.email", undefined, true)}
                  {renderField("code", "text", "placeholders.reset_code", 6, false, { inputMode: "numeric" })}
                  <button type="submit" disabled={isSubmitting}
                    className="w-full h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-base font-semibold text-white mb-4">
                    {t("buttons.continue")}
                  </button>
                  <button type="button" onClick={handleForgotBack}
                    className="w-full h-12 border border-white rounded-2xl flex items-center justify-center text-base text-white opacity-50">
                    {t("buttons.back")}
                  </button>
                </>
              ) : (
                <>
                  {renderField("email", "email", "placeholders.email", undefined, true)}
                  {renderField("code", "text", "placeholders.reset_code", 6, true)}
                  {renderField("new_password1", "password", "placeholders.new_password")}
                  {renderField("new_password2", "password", "placeholders.new_password_repeat")}
                  <button type="submit" disabled={isSubmitting}
                    className="w-full h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-base font-semibold text-white mb-4">
                    {t("buttons.reset_password")}
                  </button>
                  <button type="button" onClick={() => setForgotStep(1)}
                    className="w-full h-12 border border-white rounded-2xl flex items-center justify-center text-base text-white opacity-50">
                    {t("buttons.back")}
                  </button>
                </>
              )}
            </div>
          )}

          {errorMsg && !showAuthRedesign && (
            <div className="mt-4 flex items-start gap-2 bg-red-500/10 border border-red-400 text-red-300 rounded-md p-3 text-sm">
              <span>{errorMsg}</span>
            </div>
          )}
        </form>
      </div>

      <GlobalErrorModal />
    </div>
  );
}
